import { useState, useEffect, useRef, useCallback } from "react";
import { supabase } from "./supabase";
import Auth from "./Auth"; import AIAnalysis from "./AIAnalysis";
import ImportHistory from "./ImportHistory";

/* ─── FONTS & GLOBAL CSS ─────────────────────────────────────────────────── */
const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&family=JetBrains+Mono:wght@400;500&display=swap');

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --bg: #f8fafc;
  --s1: #ffffff;
  --s2: #f1f5f9;
  --s3: #e9eef6;
  --s4: #dde3ee;
  --b1: #e2e8f0;
  --b2: #cbd5e1;
  --b3: #94a3b8;

  --ac: #059669;
  --ac2: #047857;
  --ac-glow: rgba(5,150,105,0.08);

  --green: #059669;
  --green-bg: #ecfdf5;
  --red: #dc2626;
  --red-bg: #fef2f2;
  --yellow: #d97706;
  --yellow-bg: #fffbeb;
  --orange: #ea580c;
  --purple: #7c3aed;
  --purple-bg: #f5f3ff;

  --t1: #0f172a;
  --t2: #334155;
  --t3: #64748b;
  --t4: #94a3b8;

  --lock-bg: #fef2f2;
  --lock-border: #dc2626;
  --lock-glow: rgba(220,38,38,0.08);

  --mono: 'JetBrains Mono', monospace;
  --sans: 'Inter', sans-serif;
  --r8: 8px; --r12: 12px; --r16: 16px;
}

[data-theme="dark"] {
  --bg: #07090f;
  --s1: #0c0f1a;
  --s2: #111520;
  --s3: #161c2a;
  --s4: #1c2438;
  --b1: #1e2940;
  --b2: #253350;
  --b3: #2e4066;
  --ac: #34d399;
  --ac2: #10b981;
  --ac-glow: rgba(52,211,153,0.12);
  --green: #1dde8a;
  --green-bg: rgba(29,222,138,0.08);
  --red: #ff4560;
  --red-bg: rgba(255,69,96,0.08);
  --yellow: #ffb830;
  --yellow-bg: rgba(255,184,48,0.08);
  --orange: #ff7043;
  --purple: #b57aff;
  --purple-bg: rgba(181,122,255,0.08);
  --t1: #eef2ff;
  --t2: #7a90b8;
  --t3: #3d5278;
  --t4: #1f2f4a;
  --lock-bg: #140a0a;
  --lock-border: #ff4560;
  --lock-glow: rgba(255,69,96,0.12);
}

html, body, #root { height: 100%; }
body {
  background: var(--bg);
  color: var(--t1);
  font-family: var(--sans);
  font-size: 14px;
  line-height: 1.5;
  -webkit-font-smoothing: antialiased;
  overflow: hidden;
}

::-webkit-scrollbar { width: 4px; height: 4px; }
::-webkit-scrollbar-track { background: transparent; }
::-webkit-scrollbar-thumb { background: var(--b2); border-radius: 4px; }

/* ── LAYOUT ── */
.shell { display: flex; height: 100vh; }

/* ── SIDEBAR ── */
.nav {
  width: 56px;
  background: var(--s1);
  border-right: 1px solid var(--b1);
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px 0;
  gap: 2px;
  flex-shrink: 0;
  transition: width .2s ease;
  overflow: hidden;
  position: relative;
  z-index: 50;
}
.nav.wide { width: 220px; align-items: flex-start; }

.nav-logo {
  display: flex; align-items: center; gap: 10px;
  padding: 2px 12px 14px;
  border-bottom: 1px solid var(--b1);
  margin-bottom: 4px;
  width: 100%;
  min-width: 220px;
}
.nav-logo-mark {
  width: 30px; height: 30px; flex-shrink: 0;
  background: var(--ac2);
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  font-size: 14px;
}
.nav-logo-text { opacity: 0; transition: opacity .1s; white-space: nowrap; }
.nav.wide .nav-logo-text { opacity: 1; }
.nav-logo-name { font-weight: 700; font-size: 14px; letter-spacing: -.3px; color: var(--t1); }
.nav-logo-name span { color: var(--ac); }
.nav-logo-tag { font-family: var(--mono); font-size: 8px; color: var(--t4); letter-spacing: 1.5px; margin-top: 1px; }

.nav-group { font-size: 9px; font-weight: 600; color: var(--t4); letter-spacing: 1px; padding: 10px 14px 4px; white-space: nowrap; opacity: 0; min-width: 220px; text-transform: uppercase; }
.nav.wide .nav-group { opacity: 1; }

.nav-links { flex: 1; padding: 0 8px; width: 100%; }

.ni {
  display: flex; align-items: center; gap: 9px;
  padding: 7px 8px; border-radius: 7px;
  cursor: pointer; font-size: 13px; font-weight: 500;
  color: var(--t3); transition: all .12s;
  border: 1px solid transparent;
  white-space: nowrap; width: 100%;
  user-select: none; min-width: 220px; position: relative;
}
.ni-ico { font-size: 15px; width: 20px; text-align: center; flex-shrink: 0; }
.ni-lbl { opacity: 0; transition: opacity .08s; }
.nav.wide .ni-lbl { opacity: 1; }
.ni:hover { background: var(--s2); color: var(--t2); }
.ni.on { background: var(--ac-glow); color: var(--ac); border-color: rgba(5,150,105,0.15); font-weight: 600; }
[data-theme="dark"] .ni.on { background: rgba(52,211,153,.1); border-color: rgba(52,211,153,.2); }
.ni .pip {
  position: absolute; right: 10px;
  background: var(--red); color: #fff;
  font-size: 9px; font-family: var(--mono);
  padding: 1px 5px; border-radius: 8px;
}

.nav-bot { padding: 10px 8px; border-top: 1px solid var(--b1); width: 100%; }
.nav-toggle {
  width: 100%; background: transparent; border: 1px solid var(--b1);
  border-radius: 7px; color: var(--t3); cursor: pointer; padding: 6px;
  font-size: 12px; transition: all .12s;
}
.nav-toggle:hover { background: var(--s2); color: var(--t2); }

/* ── MAIN ── */
.main { flex: 1; display: flex; flex-direction: column; overflow: hidden; min-width: 0; }

.topbar {
  display: flex; align-items: center; justify-content: space-between;
  padding: 0 20px; height: 50px;
  border-bottom: 1px solid var(--b1);
  background: var(--s1);
  flex-shrink: 0;
}
.topbar-title { font-weight: 600; font-size: 14px; color: var(--t1); }
.topbar-right { display: flex; align-items: center; gap: 8px; }

.content { flex: 1; overflow-y: auto; padding: 20px; }

/* ── CARDS ── */
.card {
  background: var(--s1);
  border: 1px solid var(--b1);
  border-radius: var(--r12);
  padding: 16px;
}
.card-lbl { font-size: 11px; font-weight: 500; color: var(--t3); margin-bottom: 6px; }
.card-val { font-family: var(--mono); font-size: 22px; font-weight: 500; letter-spacing: -.5px; }
.card-sub { font-size: 11px; color: var(--t4); margin-top: 3px; }

/* ── GRIDS ── */
.g4 { display: grid; grid-template-columns: repeat(4,1fr); gap: 12px; }
.g3 { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; }
.g2 { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; }
.g21 { display: grid; grid-template-columns: 2fr 1fr; gap: 12px; }

/* ── BUTTONS ── */
.btn {
  display: inline-flex; align-items: center; gap: 6px;
  padding: 7px 14px; border-radius: 7px;
  font-size: 12px; font-weight: 600; cursor: pointer;
  border: 1px solid transparent; transition: all .12s;
  font-family: var(--sans); user-select: none; white-space: nowrap;
}
.btn-sm { padding: 5px 10px; font-size: 11px; }
.btn-xs { padding: 3px 7px; font-size: 10px; }
.bp { background: var(--ac2); color: #fff; border-color: var(--ac2); }
.bp:hover { background: var(--ac); border-color: var(--ac); }
.bg { background: var(--s1); color: var(--t2); border-color: var(--b1); }
.bg:hover { background: var(--s2); color: var(--t1); border-color: var(--b2); }
.br { background: var(--red-bg); color: var(--red); border-color: rgba(220,38,38,0.2); }
.br:hover { background: rgba(220,38,38,0.12); }
.bs { background: var(--green-bg); color: var(--green); border-color: rgba(5,150,105,0.2); }
.bs:hover { background: rgba(5,150,105,0.12); }
.by { background: var(--yellow-bg); color: var(--yellow); border-color: rgba(217,119,6,0.2); }
.by:hover { background: rgba(217,119,6,0.12); }
.w100 { width: 100%; }
.btn:disabled { opacity: .4; cursor: not-allowed; }

/* ── FORM ── */
.fg { margin-bottom: 12px; }
label, .lbl { display: block; font-size: 11px; font-weight: 500; color: var(--t3); margin-bottom: 4px; }
input, select, textarea {
  width: 100%; background: var(--s1); border: 1px solid var(--b1);
  border-radius: 7px; padding: 8px 11px;
  color: var(--t1); font-family: var(--sans); font-size: 13px;
  outline: none; transition: border-color .12s;
}
input:focus, select:focus, textarea:focus { border-color: var(--ac); box-shadow: 0 0 0 3px var(--ac-glow); }
select option { background: var(--s1); }
textarea { resize: vertical; min-height: 80px; line-height: 1.6; }
.fr { display: grid; grid-template-columns: 1fr 1fr; gap: 10px; }
.fr3 { display: grid; grid-template-columns: 1fr 1fr 1fr; gap: 10px; }

/* ── MODAL ── */
.ov {
  position: fixed; inset: 0;
  background: rgba(15,23,42,0.4); backdrop-filter: blur(4px);
  z-index: 200; display: flex; align-items: center; justify-content: center;
  animation: fadeIn .15s;
}
@keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
.modal {
  background: var(--s1); border: 1px solid var(--b1);
  border-radius: 14px; padding: 24px;
  width: 560px; max-width: 96vw; max-height: 90vh; overflow-y: auto;
  animation: slideUp .15s; position: relative;
  box-shadow: 0 8px 32px rgba(15,23,42,0.12);
}
.modal-lg { width: 720px; }
@keyframes slideUp { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
.mc { position: absolute; top: 14px; right: 14px; background: var(--s2); border: 1px solid var(--b1); color: var(--t3); cursor: pointer; width: 26px; height: 26px; border-radius: 6px; font-size: 12px; display: flex; align-items: center; justify-content: center; transition: all .12s; }
.mc:hover { background: var(--s3); color: var(--t1); }
.mt { font-size: 16px; font-weight: 700; letter-spacing: -.3px; margin-bottom: 3px; }
.ms { font-size: 12px; color: var(--t3); margin-bottom: 18px; }

/* ── TAGS ── */
.tag { display: inline-flex; align-items: center; padding: 2px 7px; border-radius: 5px; font-size: 10px; font-weight: 600; letter-spacing: .3px; font-family: var(--mono); }
.tg { background: var(--green-bg); color: var(--green); }
.tr { background: var(--red-bg); color: var(--red); }
.ty { background: var(--yellow-bg); color: var(--yellow); }
.tb { background: var(--ac-glow); color: var(--ac); }
.tp { background: var(--purple-bg); color: var(--purple); }

/* ── TABLE ── */
.tbl-wrap { overflow-x: auto; }
table { width: 100%; border-collapse: collapse; font-size: 12px; }
thead th { text-align: left; padding: 8px 12px; font-size: 10px; font-weight: 600; letter-spacing: .5px; color: var(--t3); border-bottom: 1px solid var(--b1); text-transform: uppercase; white-space: nowrap; }
tbody tr { border-bottom: 1px solid var(--b1); transition: background .1s; }
tbody tr:last-child { border-bottom: none; }
tbody tr:hover { background: var(--s2); }
td { padding: 10px 12px; color: var(--t2); white-space: nowrap; }
td.hl { color: var(--t1); font-weight: 600; }

/* ── TOGGLE ── */
.tog { width: 34px; height: 18px; background: var(--s3); border-radius: 9px; position: relative; cursor: pointer; border: 1px solid var(--b2); flex-shrink: 0; transition: background .2s; }
.tog.on { background: var(--ac); border-color: var(--ac); }
.tog.on-g { background: var(--green); border-color: var(--green); }
.tog.on-r { background: var(--red); border-color: var(--red); }
.tog::after { content: ''; position: absolute; top: 2px; left: 2px; width: 12px; height: 12px; border-radius: 50%; background: var(--t4); transition: all .2s; }
.tog.on::after, .tog.on-g::after, .tog.on-r::after { left: 18px; background: #fff; }

/* ── ALERT ── */
.alert { border-radius: 8px; padding: 10px 13px; display: flex; align-items: flex-start; gap: 8px; margin-bottom: 12px; font-size: 12px; line-height: 1.5; }
.al-r { background: var(--red-bg); border: 1px solid rgba(220,38,38,0.2); color: var(--red); }
.al-y { background: var(--yellow-bg); border: 1px solid rgba(217,119,6,0.2); color: var(--yellow); }
.al-b { background: var(--ac-glow); border: 1px solid rgba(37,99,235,0.2); color: var(--ac); }
.al-g { background: var(--green-bg); border: 1px solid rgba(5,150,105,0.2); color: var(--green); }

/* ── PROGRESS ── */
.prog { height: 4px; background: var(--s3); border-radius: 2px; overflow: hidden; }
.pfill { height: 100%; border-radius: 2px; transition: width .5s; }

/* ── EMOTION BUTTONS ── */
.emo-row { display: flex; gap: 6px; }
.emo-btn { flex: 1; display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 8px 4px; border-radius: 7px; font-size: 9px; font-weight: 500; background: var(--s2); border: 1px solid var(--b1); color: var(--t3); cursor: pointer; transition: all .12s; }
.emo-btn .ei { font-size: 16px; }
.emo-btn:hover { background: var(--s3); color: var(--t2); }
.emo-btn.sel { background: var(--ac-glow); border-color: rgba(5,150,105,0.3); color: var(--ac); }

/* ── IMAGE UPLOAD ── */
.img-grid { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; margin-top: 8px; }
.img-thumb {
  aspect-ratio: 16/9; border-radius: 7px; overflow: hidden;
  border: 1px solid var(--b1); background: var(--s2);
  position: relative; cursor: pointer;
  display: flex; align-items: center; justify-content: center;
}
.img-thumb img { width: 100%; height: 100%; object-fit: cover; }
.img-thumb .img-del {
  position: absolute; top: 4px; right: 4px;
  background: rgba(15,23,42,0.6); border: none; color: #fff;
  width: 18px; height: 18px; border-radius: 4px; font-size: 9px;
  cursor: pointer; display: none; align-items: center; justify-content: center;
}
.img-thumb:hover .img-del { display: flex; }
.img-add {
  aspect-ratio: 16/9; border-radius: 7px;
  border: 1px dashed var(--b2); background: var(--s2);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 5px; cursor: pointer; transition: all .12s; color: var(--t3);
  font-size: 11px;
}
.img-add:hover { border-color: var(--ac); color: var(--ac); background: var(--ac-glow); }
.img-add-ico { font-size: 20px; }

/* ── LOCK SYSTEM ── */
.lock-wall {
  position: fixed; inset: 0;
  background: var(--lock-bg);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  z-index: 500;
  animation: fadeIn .3s;
}
.lock-card {
  background: var(--s1);
  border: 1px solid var(--lock-border);
  border-radius: 16px; padding: 36px;
  width: 440px; max-width: 96vw;
  text-align: center;
  box-shadow: 0 4px 24px var(--lock-glow);
}
.lock-icon { font-size: 44px; margin-bottom: 14px; display: block; }
.lock-title { font-size: 22px; font-weight: 700; letter-spacing: -.4px; color: var(--red); margin-bottom: 8px; }
.lock-reason { font-family: var(--mono); font-size: 11px; color: var(--t3); margin-bottom: 22px; line-height: 1.6; }
.lock-countdown { background: var(--red-bg); border: 1px solid rgba(220,38,38,0.15); border-radius: 10px; padding: 18px; margin-bottom: 20px; }
.lock-time { font-family: var(--mono); font-size: 38px; font-weight: 600; color: var(--red); letter-spacing: 3px; line-height: 1; }
.lock-time-lbl { font-size: 9px; font-weight: 600; color: var(--t4); letter-spacing: 1.5px; margin-top: 6px; text-transform: uppercase; }
.lock-rules-list { text-align: left; margin-bottom: 20px; }
.lock-rule-item { display: flex; align-items: center; gap: 8px; padding: 7px 11px; background: var(--s2); border-radius: 7px; margin-bottom: 5px; font-size: 11px; color: var(--t2); }
.lock-rule-item .dot { width: 6px; height: 6px; border-radius: 50%; flex-shrink: 0; }

/* ── ACCOUNT CARD ── */
.ac-card { background: var(--s1); border: 1px solid var(--b1); border-radius: 10px; padding: 16px; transition: all .15s; position: relative; }
.ac-card:hover { border-color: var(--b2); }
.ac-locked { border-color: rgba(220,38,38,0.3) !important; background: var(--red-bg) !important; }
.ac-locked-bar { background: var(--red-bg); color: var(--red); font-size: 9px; font-weight: 600; letter-spacing: 1.5px; text-align: center; padding: 4px; margin: -16px -16px 14px; border-radius: 9px 9px 0 0; border-bottom: 1px solid rgba(220,38,38,0.15); text-transform: uppercase; }
.ac-name { font-weight: 700; font-size: 14px; letter-spacing: -.2px; margin-bottom: 3px; }
.ac-meta { font-family: var(--mono); font-size: 10px; color: var(--t3); display: flex; align-items: center; gap: 6px; margin-bottom: 12px; }
.ac-stats { display: grid; grid-template-columns: repeat(3,1fr); gap: 8px; margin-bottom: 12px; }
.ac-stat-l { font-size: 9px; font-weight: 500; color: var(--t4); letter-spacing: .5px; text-transform: uppercase; }
.ac-stat-v { font-family: var(--mono); font-size: 13px; font-weight: 600; margin-top: 2px; }

/* ── JOURNAL ENTRY ── */
.je {
  background: var(--s1); border: 1px solid var(--b1);
  border-radius: 10px; padding: 14px; margin-bottom: 8px;
  border-left: 3px solid transparent; cursor: pointer; transition: all .12s;
}
.je:hover { border-color: var(--b2); }
.je.win { border-left-color: var(--green); }
.je.loss { border-left-color: var(--red); }
.je.be { border-left-color: var(--yellow); }
.je-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 8px; }
.je-pair { font-weight: 700; font-size: 15px; letter-spacing: -.2px; }
.je-pnl { font-family: var(--mono); font-size: 14px; font-weight: 600; }
.je-meta { display: flex; align-items: center; gap: 6px; flex-wrap: wrap; margin-bottom: 8px; }
.je-note { font-size: 12px; color: var(--t2); line-height: 1.55; margin-bottom: 10px; padding: 10px 12px; background: var(--s2); border-radius: 6px; border-left: 2px solid var(--b2); }
.je-imgs { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 10px; }
.je-img { width: 110px; height: 62px; border-radius: 6px; overflow: hidden; border: 1px solid var(--b1); cursor: zoom-in; }
.je-img img { width: 100%; height: 100%; object-fit: cover; transition: transform .2s; }
.je-img:hover img { transform: scale(1.04); }

/* ── RULE CARD ── */
.rule-card { background: var(--s1); border: 1px solid var(--b1); border-radius: 8px; padding: 12px 14px; display: flex; align-items: center; gap: 12px; margin-bottom: 8px; }
.rule-ico { font-size: 16px; width: 32px; height: 32px; display: flex; align-items: center; justify-content: center; background: var(--s2); border-radius: 8px; flex-shrink: 0; }
.rule-inf { flex: 1; }
.rule-name { font-weight: 600; font-size: 12px; }
.rule-desc { font-size: 10px; color: var(--t3); margin-top: 2px; }

/* ── EA CODE ── */
.code-hd { display: flex; align-items: center; justify-content: space-between; background: var(--s2); border: 1px solid var(--b1); border-bottom: none; border-radius: 8px 8px 0 0; padding: 8px 14px; }
.code-body { background: #1e293b; border: 1px solid var(--b1); border-radius: 0 0 8px 8px; padding: 14px; font-family: var(--mono); font-size: 11px; color: #93c5fd; overflow-x: auto; max-height: 360px; overflow-y: auto; line-height: 1.7; white-space: pre; }

/* ── LIGHTBOX ── */
.lightbox-ov { position:fixed; inset:0; background:rgba(15,23,42,.88); z-index:9999; display:flex; align-items:center; justify-content:center; animation:fadeIn .15s; cursor:zoom-out; }
.lightbox-img { max-width:90vw; max-height:88vh; border-radius:10px; }
.lightbox-close { position:absolute; top:18px; right:18px; background:rgba(255,255,255,.1); border:none; color:#fff; width:36px; height:36px; border-radius:50%; font-size:16px; cursor:pointer; display:flex; align-items:center; justify-content:center; }
.lightbox-close:hover { background:rgba(255,255,255,.2); }

/* ── UTILS ── */
.fl { display: flex; }
.aic { align-items: center; }
.jb { justify-content: space-between; }
.g1 { gap: 4px; } .g2 { gap: 8px; } .g3 { gap: 12px; } .g4 { gap: 16px; }
.mb1 { margin-bottom: 4px; } .mb2 { margin-bottom: 8px; } .mb3 { margin-bottom: 12px; } .mb4 { margin-bottom: 16px; } .mb5 { margin-bottom: 20px; }
.mt2 { margin-top: 8px; } .mt3 { margin-top: 12px; } .mt4 { margin-top: 16px; }
.fm { font-family: var(--mono); }
.gr { color: var(--green); } .rr { color: var(--red); } .yr { color: var(--yellow); } .br2 { color: var(--ac); } .pr { color: var(--purple); }
.sh { display: flex; align-items: center; justify-content: space-between; margin-bottom: 14px; }
.st { font-size: 14px; font-weight: 700; letter-spacing: -.2px; }
.divider { border: none; border-top: 1px solid var(--b1); margin: 14px 0; }
.chip { display: inline-flex; align-items: center; gap: 4px; padding: 3px 9px; border-radius: 20px; font-size: 11px; font-weight: 500; background: var(--s2); border: 1px solid var(--b1); color: var(--t3); cursor: pointer; transition: all .12s; user-select: none; }
.chip.on { background: var(--ac-glow); border-color: rgba(5,150,105,0.25); color: var(--ac); }
.platform-badge { display: inline-flex; align-items: center; gap: 4px; padding: 2px 7px; border-radius: 4px; font-size: 9px; font-weight: 600; }
.pb-mt5 { background: var(--ac-glow); color: var(--ac); }
.pb-mt { background: var(--green-bg); color: var(--green); }
.pb-ct { background: var(--yellow-bg); color: var(--yellow); }
.pb-dx { background: var(--purple-bg); color: var(--purple); }

/* ── ONBOARDING ── */
.onb-panel {
  width: 260px; flex-shrink: 0;
  background: var(--s1);
  border-left: 1px solid var(--b1);
  display: flex; flex-direction: column;
  padding: 20px 16px;
  overflow-y: auto;
}
.onb-title { font-size: 13px; font-weight: 700; color: var(--t1); margin-bottom: 3px; }
.onb-sub { font-size: 11px; color: var(--t3); margin-bottom: 18px; line-height: 1.5; }
.onb-item { display: flex; align-items: flex-start; gap: 10px; padding: 9px 10px; border-radius: 8px; margin-bottom: 4px; cursor: pointer; transition: background .12s; border: 1px solid transparent; }
.onb-item:hover { background: var(--s2); }
.onb-item.active { background: var(--ac-glow); border-color: rgba(37,99,235,0.15); }
.onb-item.done { opacity: 0.55; }
.onb-ico { width: 22px; height: 22px; border-radius: 50%; border: 2px solid var(--b2); display: flex; align-items: center; justify-content: center; flex-shrink: 0; font-size: 10px; margin-top: 1px; }
.onb-ico.done { background: var(--green); border-color: var(--green); color: #fff; }
.onb-ico.active { border-color: var(--ac); }
.onb-lbl { font-size: 12px; font-weight: 600; color: var(--t2); }
.onb-lbl.done { text-decoration: line-through; color: var(--t4); }
.onb-desc { font-size: 10px; color: var(--t3); margin-top: 2px; line-height: 1.4; }
.onb-prog { margin-top: 20px; }
.onb-prog-lbl { font-size: 10px; color: var(--t4); margin-bottom: 5px; font-family: var(--mono); }
.onb-prog-bar { height: 4px; background: var(--s3); border-radius: 2px; overflow: hidden; }
.onb-prog-fill { height: 100%; background: var(--ac); border-radius: 2px; transition: width .4s; }
.onb-tip { background: var(--green-bg); border: 1px solid rgba(5,150,105,0.15); border-radius: 8px; padding: 10px 12px; font-size: 11px; color: var(--green); margin-top: 16px; line-height: 1.5; }
.onb-dismiss { margin-top: 16px; width: 100%; padding: 7px; background: transparent; border: 1px solid var(--b1); border-radius: 7px; color: var(--t3); font-size: 11px; cursor: pointer; font-family: var(--sans); transition: all .12s; }
.onb-dismiss:hover { background: var(--s2); color: var(--t2); }

/* ── LAUNCH PAGE ── */
.launch-page { min-height: 100%; }
.hero { text-align: center; padding: 52px 30px 36px; }
.hero-badge { display: inline-flex; align-items: center; gap: 6px; background: var(--ac-glow); border: 1px solid rgba(37,99,235,0.2); border-radius: 20px; padding: 4px 12px; font-size: 10px; font-weight: 600; color: var(--ac); letter-spacing: .5px; margin-bottom: 18px; }
.hero-title { font-size: clamp(26px,4vw,42px); font-weight: 700; letter-spacing: -1px; line-height: 1.1; margin-bottom: 14px; }
.hero-title span { color: var(--ac); }
.hero-sub { font-size: 14px; color: var(--t2); max-width: 480px; margin: 0 auto 28px; line-height: 1.6; }
.price-cards { display: grid; grid-template-columns: repeat(3,1fr); gap: 12px; max-width: 820px; margin: 0 auto 36px; }
.price-card { background: var(--s1); border: 1px solid var(--b1); border-radius: 12px; padding: 22px; text-align: left; position: relative; }
.price-card.featured { border-color: var(--ac); }
.price-card.featured::before { content: 'MÁS POPULAR'; position: absolute; top: -10px; left: 50%; transform: translateX(-50%); background: var(--ac2); color: #fff; font-size: 9px; font-weight: 600; letter-spacing: 1px; padding: 2px 10px; border-radius: 8px; }
.price-name { font-weight: 700; font-size: 15px; margin-bottom: 4px; }
.price-amount { font-family: var(--mono); font-size: 28px; font-weight: 600; letter-spacing: -.5px; margin-bottom: 4px; }
.price-amount span { font-size: 13px; color: var(--t3); font-weight: 400; }
.price-feat { font-size: 11px; color: var(--t2); display: flex; align-items: flex-start; gap: 7px; margin-bottom: 6px; line-height: 1.4; }
.price-feat::before { content: '✓'; color: var(--green); font-weight: 700; flex-shrink: 0; margin-top: 1px; }
.launch-section { max-width: 820px; margin: 0 auto; padding: 0 20px 40px; }
.launch-title { font-size: 20px; font-weight: 700; letter-spacing: -.3px; margin-bottom: 5px; }
.launch-sub { font-size: 12px; color: var(--t3); margin-bottom: 18px; }
.timeline { position: relative; }
.timeline::before { content: ''; position: absolute; left: 18px; top: 0; bottom: 0; width: 1px; background: var(--b1); }
.tl-item { display: flex; gap: 14px; margin-bottom: 18px; position: relative; }
.tl-dot { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 13px; flex-shrink: 0; border: 1px solid var(--b1); background: var(--s1); position: relative; z-index: 1; }
.tl-dot.done { border-color: var(--green); background: var(--green-bg); }
.tl-dot.now { border-color: var(--ac); background: var(--ac-glow); }
.tl-content { flex: 1; padding-top: 6px; }
.tl-title { font-weight: 600; font-size: 13px; margin-bottom: 3px; }
.tl-desc { font-size: 11px; color: var(--t3); line-height: 1.5; }
.tl-date { font-size: 10px; font-weight: 500; color: var(--t4); letter-spacing: .5px; margin-top: 3px; }
`

/* ─── DATA ────────────────────────────────────────────────────────────────── */
const EMOTIONS = [
  { k:'calm', e:'😌', l:'Calmado' }, { k:'confident', e:'💪', l:'Seguro' },
  { k:'anxious', e:'😰', l:'Ansioso' }, { k:'frustrated', e:'😤', l:'Frustrado' },
  { k:'fomo', e:'🤑', l:'FOMO' }, { k:'revenge', e:'😡', l:'Revenge' },
];
const SETUPS = ['LQ Sweep','BOS+FVG','OB Weekly','Supply & Demand','Break & Retest','ICT Kill Zone','News Play','Scalping','BE Mgmt','None'];
const SESSIONS = ['Asian','London','New York','London/NY Overlap'];

const ACCOUNTS0 = [
  { id:1, name:'FTMO Challenge', broker:'FTMO', platform:'MT5', login:'882341', server:'FTMO-Server2',
    balance:98450, equity:97820, dd:1.58, dailyDD:0.82, maxDD:10, maxDailyDD:5,
    locked:false, lockReason:'', lockUntil:null,
    rules:{ maxTrades:5, maxLoss:2, maxConsec:3, cooldown:15 },
    todayTrades:3, todayLoss:0.82, phase:'Challenge', connected:true },
  { id:2, name:'The5ers Pro', broker:'The5ers', platform:'MT5', login:'44219', server:'The5ers-Live',
    balance:24880, equity:24120, dd:3.12, dailyDD:1.9, maxDD:6, maxDailyDD:4,
    locked:true, lockReason:'Pérdida diaria alcanzada: 1.9% de $24,880',
    lockUntil: (() => { const d = new Date(); d.setHours(24,0,0,0); return d.getTime(); })(),
    rules:{ maxTrades:8, maxLoss:2, maxConsec:2, cooldown:15 },
    todayTrades:6, todayLoss:1.9, phase:'Funded', connected:true },
];

const TRADES0 = [
  { id:1, accId:1, pair:'XAUUSD', type:'BUY', lots:0.5, open:'2328.12', close:'2341.55', pnl:671.5, rr:'1:2.4', date:'2024-05-14 09:32', emotion:'calm', note:'Setup perfecto. LQ sweep en zona de demanda diaria. Esperé confirmación en M15 antes de entrar.', status:'WIN', setup:'LQ Sweep', session:'London', images:[] },
  { id:2, accId:1, pair:'EURUSD', type:'SELL', lots:1.0, open:'1.08612', close:'1.08201', pnl:411, rr:'1:1.8', date:'2024-05-14 07:15', emotion:'confident', note:'BOS en H1 con FVG como zona de entrada. Estructura clara bajista.', status:'WIN', setup:'BOS+FVG', session:'London', images:[] },
  { id:3, accId:2, pair:'NAS100', type:'BUY', lots:0.3, open:'18520', close:'18480', pnl:-120, rr:'1:0.4', date:'2024-05-14 10:01', emotion:'anxious', note:'Revenge trade. Entré sin setup claro después de la pérdida anterior. Error grave.', status:'LOSS', setup:'None', session:'NY', images:[] },
];

/* ─── HELPERS ─────────────────────────────────────────────────────────────── */
const fmtN = n => n >= 0 ? `+$${n.toFixed(2)}` : `-$${Math.abs(n).toFixed(2)}`;
const fmtP = n => `${n.toFixed(2)}%`;
const col = n => n > 0 ? 'var(--green)' : n < 0 ? 'var(--red)' : 'var(--yellow)';

function msToHMS(ms) {
  if (ms <= 0) return '00:00:00';
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return [h, m, sec].map(v => String(v).padStart(2, '0')).join(':');
}

/* ─── LOCK COUNTDOWN ─────────────────────────────────────────────────────── */
function LockCountdown({ until }) {
  const [remaining, setRemaining] = useState(until - Date.now());
  useEffect(() => {
    const t = setInterval(() => setRemaining(until - Date.now()), 1000);
    return () => clearInterval(t);
  }, [until]);
  return <div className="lock-time">{msToHMS(remaining)}</div>;
}

/* ─── LOCK WALL ─────────────────────────────────────────────────────────────
   Una vez activado, NO hay botón de desbloqueo. Solo cuenta atrás hasta medianoche.
──────────────────────────────────────────────────────────────────────────── */
function LockWall({ account, onDismiss }) {
  const midnight = account.lockUntil;
  const triggered = account.rules;

  return (
    <div className="lock-wall">
      <div className="lock-card">
        <span className="lock-icon">🔒</span>
        <div className="lock-title">CUENTA BLOQUEADA</div>
        <div className="lock-reason">
          {account.lockReason}<br/>
          <span style={{ color: 'var(--t4)', fontSize: 10 }}>El bloqueo se libera automáticamente a medianoche. No puede revertirse.</span>
        </div>

        <div className="lock-countdown">
          <LockCountdown until={midnight} />
          <div className="lock-time-lbl">TIEMPO HASTA MEDIANOCHE</div>
        </div>

        <div className="lock-rules-list">
          <div style={{ fontSize: 10, fontFamily: 'var(--mono)', color: 'var(--t4)', letterSpacing: '1.5px', marginBottom: 8, textTransform: 'uppercase' }}>Reglas que activaron el bloqueo</div>
          {[
            { label: `Max pérdida diaria`, val: `${triggered.maxLoss}%`, color: 'var(--red)' },
            { label: `Max trades/día`, val: triggered.maxTrades, color: 'var(--yellow)' },
            { label: `Max pérdidas consecutivas`, val: triggered.maxConsec, color: 'var(--orange)' },
          ].map(r => (
            <div className="lock-rule-item" key={r.label}>
              <div className="dot" style={{ background: r.color }} />
              <span style={{ flex: 1 }}>{r.label}</span>
              <span style={{ color: r.color, fontWeight: 700 }}>{r.val}</span>
            </div>
          ))}
        </div>

        <div className="alert al-y" style={{ fontSize: 11, textAlign: 'left' }}>
          <span>💡</span>
          <span>Usa este tiempo para revisar tus trades del día, identificar los errores y preparar el plan de mañana.</span>
        </div>

        <button className="btn bg w100" style={{ marginTop: 8, fontSize: 11 }} onClick={onDismiss}>
          Ver Journal del Día →
        </button>
      </div>
    </div>
  );
}

/* ─── IMAGE UPLOADER ──────────────────────────────────────────────────────── */
function ImageUploader({ images, onChange }) {
  const inputRef = useRef();
  const [pasted, setPasted] = useState(false);

  const handleFiles = (files) => {
    Array.from(files).forEach(file => {
      if (!file.type.startsWith('image/')) return;
      const reader = new FileReader();
      reader.onload = e => onChange([...images, { id: Date.now() + Math.random(), src: e.target.result, name: file.name || 'captura.png' }]);
      reader.readAsDataURL(file);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    handleFiles(e.dataTransfer.files);
  };

  const handlePaste = useCallback((e) => {
    const items = e.clipboardData?.items;
    if (!items) return;
    const imageItems = Array.from(items).filter(item => item.type.startsWith('image/'));
    if (!imageItems.length) return;
    e.preventDefault();
    imageItems.forEach(item => {
      const file = item.getAsFile();
      if (!file) return;
      const reader = new FileReader();
      reader.onload = ev => {
        onChange(prev => [...prev, { id: Date.now() + Math.random(), src: ev.target.result, name: 'captura_' + Date.now() + '.png' }]);
        setPasted(true);
        setTimeout(() => setPasted(false), 2000);
      };
      reader.readAsDataURL(file);
    });
  }, [images, onChange]);

  useEffect(() => {
    document.addEventListener('paste', handlePaste);
    return () => document.removeEventListener('paste', handlePaste);
  }, [handlePaste]);

  const remove = (id) => onChange(images.filter(i => i.id !== id));

  return (
    <div>
      <div className="fl aic jb mb2">
        <label style={{ marginBottom: 0 }}>Capturas de Pantalla</label>
        {pasted && <span style={{ fontSize: 10, color: 'var(--green)', fontFamily: 'var(--mono)', fontWeight: 600 }}>✓ Imagen pegada</span>}
      </div>
      <div
        className="img-add mb2"
        style={{ padding: 16 }}
        onClick={() => inputRef.current.click()}
        onDrop={handleDrop}
        onDragOver={e => e.preventDefault()}
      >
        <span className="img-add-ico">📷</span>
        <span>Arrastra, click para subir</span>
        <span style={{ fontSize: 10, color: 'var(--ac)', fontWeight: 600 }}>o Ctrl+V para pegar desde TradingView</span>
        <span style={{ fontSize: 9, color: 'var(--t4)' }}>PNG, JPG, WEBP</span>
      </div>
      <input ref={inputRef} type="file" accept="image/*" multiple style={{ display: 'none' }} onChange={e => handleFiles(e.target.files)} />
      {images.length > 0 && (
        <div className="img-grid">
          {images.map(img => (
            <div className="img-thumb" key={img.id}>
              <img src={img.src} alt={img.name} />
              <button className="img-del" onClick={e => { e.stopPropagation(); remove(img.id); }}>✕</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── EQUITY MINI CURVE ───────────────────────────────────────────────────── */
function MiniCurve({ trades, h = 48 }) {
  const ref = useRef();
  const [tooltip, setTooltip] = useState(null);

  const sorted = [...trades].sort((a, b) => new Date(a.created_at || a.date) - new Date(b.created_at || b.date));

  useEffect(() => {
    const c = ref.current; if (!c) return;
    const ctx = c.getContext('2d');
    const PAD_L = 52, PAD_R = 12, PAD_T = 10, PAD_B = 28;
    const w = c.offsetWidth || 400; c.width = w; c.height = h;
    const cW = w - PAD_L - PAD_R, cH = h - PAD_T - PAD_B;

    // Build cumulative PnL points
    let eq = 0;
    const pts = [0, ...sorted.map(t => { eq += (t.pnl || 0); return eq; })];
    if (pts.length < 2) {
      ctx.clearRect(0, 0, w, h);
      ctx.fillStyle = 'rgba(100,116,139,0.4)';
      ctx.font = '11px Inter, sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Sin datos suficientes', w / 2, h / 2);
      return;
    }

    const mn = Math.min(...pts), mx = Math.max(...pts);
    const range = mx - mn || 1;
    const xS = cW / (pts.length - 1);
    const yAt = v => PAD_T + cH - ((v - mn) / range) * cH;
    const xAt = i => PAD_L + i * xS;
    const isPos = pts[pts.length - 1] >= 0;
    const lineColor = isPos ? '#059669' : '#dc2626';
    const fillColor = isPos ? 'rgba(5,150,105,0.08)' : 'rgba(220,38,38,0.08)';

    ctx.clearRect(0, 0, w, h);

    // Grid lines
    ctx.strokeStyle = 'rgba(148,163,184,0.15)';
    ctx.lineWidth = 1;
    for (let i = 0; i <= 4; i++) {
      const y = PAD_T + (cH / 4) * i;
      ctx.beginPath(); ctx.moveTo(PAD_L, y); ctx.lineTo(PAD_L + cW, y); ctx.stroke();
    }

    // Zero line
    const zeroY = yAt(0);
    if (zeroY >= PAD_T && zeroY <= PAD_T + cH) {
      ctx.strokeStyle = 'rgba(148,163,184,0.4)';
      ctx.setLineDash([4, 4]);
      ctx.beginPath(); ctx.moveTo(PAD_L, zeroY); ctx.lineTo(PAD_L + cW, zeroY); ctx.stroke();
      ctx.setLineDash([]);
    }

    // Fill area
    ctx.beginPath();
    ctx.moveTo(xAt(0), yAt(pts[0]));
    pts.forEach((p, i) => { if (i > 0) ctx.lineTo(xAt(i), yAt(p)); });
    ctx.lineTo(xAt(pts.length - 1), yAt(0));
    ctx.lineTo(xAt(0), yAt(0));
    ctx.closePath();
    ctx.fillStyle = fillColor;
    ctx.fill();

    // Line
    ctx.beginPath();
    ctx.moveTo(xAt(0), yAt(pts[0]));
    pts.forEach((p, i) => { if (i > 0) ctx.lineTo(xAt(i), yAt(p)); });
    ctx.strokeStyle = lineColor;
    ctx.lineWidth = 2;
    ctx.lineJoin = 'round';
    ctx.stroke();

    // Dots on each trade
    pts.forEach((p, i) => {
      if (i === 0) return;
      ctx.beginPath();
      ctx.arc(xAt(i), yAt(p), 3, 0, Math.PI * 2);
      ctx.fillStyle = p >= 0 ? '#059669' : '#dc2626';
      ctx.fill();
    });

    // Y axis labels
    ctx.fillStyle = 'rgba(100,116,139,0.8)';
    ctx.font = '9px JetBrains Mono, monospace';
    ctx.textAlign = 'right';
    [mn, (mn + mx) / 2, mx].forEach(v => {
      const y = yAt(v);
      if (y >= PAD_T && y <= PAD_T + cH) {
        ctx.fillText((v >= 0 ? '+' : '') + v.toFixed(0), PAD_L - 4, y + 3);
      }
    });

    // X axis labels (first, middle, last)
    ctx.textAlign = 'center';
    const labelIdxs = [1, Math.floor(sorted.length / 2), sorted.length - 1].filter((v, i, a) => a.indexOf(v) === i && v > 0 && v <= sorted.length);
    labelIdxs.forEach(i => {
      const t = sorted[i - 1];
      if (!t) return;
      const d = (t.created_at || t.date || '').slice(5, 10);
      ctx.fillStyle = 'rgba(100,116,139,0.7)';
      ctx.font = '9px JetBrains Mono, monospace';
      ctx.fillText(d, xAt(i), h - 6);
    });

  }, [sorted]);

  const handleMouseMove = (e) => {
    const c = ref.current; if (!c) return;
    const PAD_L = 52, PAD_R = 12;
    const rect = c.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const cW = rect.width - PAD_L - PAD_R;
    const xS = cW / sorted.length;
    const idx = Math.round((x - PAD_L) / xS) - 1;
    if (idx >= 0 && idx < sorted.length) {
      const t = sorted[idx];
      let eq = 0;
      for (let i = 0; i <= idx; i++) eq += (sorted[i].pnl || 0);
      setTooltip({ x: e.clientX - rect.left, trade: t, eq });
    } else {
      setTooltip(null);
    }
  };

  return (
    <div style={{ position: 'relative' }} onMouseMove={handleMouseMove} onMouseLeave={() => setTooltip(null)}>
      <canvas ref={ref} style={{ width: '100%', height: h, display: 'block' }} />
      {tooltip && (
        <div style={{
          position: 'absolute', top: 8, left: Math.min(tooltip.x + 10, 220),
          background: 'var(--s1)', border: '1px solid var(--b1)',
          borderRadius: 7, padding: '6px 10px', fontSize: 11,
          fontFamily: 'var(--mono)', pointerEvents: 'none', zIndex: 10,
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)', minWidth: 130,
        }}>
          <div style={{ fontWeight: 700, color: 'var(--t1)', marginBottom: 2 }}>{tooltip.trade.pair} · {tooltip.trade.type}</div>
          <div style={{ color: (tooltip.trade.pnl || 0) >= 0 ? 'var(--green)' : 'var(--red)' }}>Trade: {fmtN(tooltip.trade.pnl || 0)}</div>
          <div style={{ color: tooltip.eq >= 0 ? 'var(--green)' : 'var(--red)' }}>Acum: {fmtN(tooltip.eq)}</div>
          <div style={{ color: 'var(--t3)', fontSize: 10, marginTop: 2 }}>{(tooltip.trade.created_at || tooltip.trade.date || '').slice(0,10)}</div>
        </div>
      )}
    </div>
  );
}

/* ─── EA CODE ─────────────────────────────────────────────────────────────── */
const EA_MQ5 = `//+---------------------------------------------------------------+
//|  TradeLeash EA v3.8                                           |
//|  Bloqueo automatico MT5 - sync con journal via webhook        |
//|  Compatible: MetaTrader 5                                     |
//+---------------------------------------------------------------+
#property copyright "TradeLeash"
#property version   "3.8"

input double MaxDailyLoss    = 2.0;   // % perdida diaria maxima
input double MaxDailyProfit  = 5.0;   // % ganancia diaria maxima
input double MaxDailyDD      = 3.0;   // % drawdown diario
input int    MaxTrades        = 5;    // trades max/dia
input int    MaxConsecLoss    = 3;    // perdidas consecutivas
input int    CooldownMin      = 0;    // pausa entre trades (min)
input bool   BlockWeekends    = true; // bloquear viernes 20h+
input bool   AutoBreakEven    = true; // mover SL a BE
input double BEpips           = 20;   // pips para activar BE
input string AccountToken     = "";   // token de tu cuenta

string WebhookURL = "https://www.tradeleash.com/api/webhook";

double   DayBal;
int      TradesToday = 0, ConsecLoss = 0;
string   LastDirection = "";
datetime LastTrade, DayStart;
bool     IsLocked = false;
string   LockReason = "";
int      TimerCount = 0;

int OnInit() {
  DayBal   = AccountInfoDouble(ACCOUNT_BALANCE);
  DayStart = iTime(_Symbol, PERIOD_D1, 0);
  EventSetTimer(5);
  Print("TradeLeash EA v3.8 iniciado. Token: ", AccountToken);
  return INIT_SUCCEEDED;
}

void OnTimer() {
  TimerCount++;
  CheckRemoteLock();
  if(TimerCount % 6 == 0) {
    CheckNewDay();
    CheckLock();
    if(AutoBreakEven) ManageBE();
  }
}

void CheckRemoteLock() {
  if(AccountToken == "") return;
  string body = StringFormat(
    "{\"token\":\"%s\",\"event\":\"status\",\"symbol\":\"%s\"," +
    "\"volume\":0,\"price\":0,\"pnl\":0,\"direction\":\"\"," +
    "\"balance\":%.2f,\"trades_today\":%d,\"consec_loss\":%d}",
    AccountToken, _Symbol,
    AccountInfoDouble(ACCOUNT_BALANCE), TradesToday, ConsecLoss);
  char data[]; char result[]; string resHeaders;
  StringToCharArray(body, data, 0, StringLen(body));
  int res = WebRequest("POST", WebhookURL,
    "Content-Type: application/json\r\n", 3000, data, result, resHeaders);
  if(res == -1) return;
  string resp = CharArrayToString(result);
  if(StringFind(resp, "\"is_locked\":true") >= 0) {
    if(!IsLocked) Lock("Bloqueado remotamente por journal");
    else CloseAllPositions();
  }
}

void OnTradeTransaction(const MqlTradeTransaction &tr,
  const MqlTradeRequest &rq, const MqlTradeResult &rs) {
  if(tr.type != TRADE_TRANSACTION_DEAL_ADD) return;
  if(!HistoryDealSelect(tr.deal)) return;
  ENUM_DEAL_ENTRY entry = (ENUM_DEAL_ENTRY)HistoryDealGetInteger(tr.deal, DEAL_ENTRY);
  ENUM_DEAL_TYPE  dtype = (ENUM_DEAL_TYPE)HistoryDealGetInteger(tr.deal, DEAL_TYPE);
  string sym   = HistoryDealGetString(tr.deal, DEAL_SYMBOL);
  double vol   = HistoryDealGetDouble(tr.deal, DEAL_VOLUME);
  double price = HistoryDealGetDouble(tr.deal, DEAL_PRICE);
  double pnl   = HistoryDealGetDouble(tr.deal, DEAL_PROFIT);
  if(entry == DEAL_ENTRY_IN) {
    if(IsLocked) { ClosePositionByTicket(tr.position, sym); return; }
    TradesToday++;
    LastTrade = TimeCurrent();
    string direction = (dtype == DEAL_TYPE_BUY) ? "BUY" : "SELL";
    LastDirection = direction;
    bool remLocked = Webhook("open", sym, vol, price, 0, direction);
    if(remLocked) { Lock("Bloqueado remotamente"); ClosePositionByTicket(tr.position, sym); }
  }
  else if(entry == DEAL_ENTRY_OUT || entry == DEAL_ENTRY_OUT_BY) {
    if(pnl < 0) ConsecLoss++;
    else ConsecLoss = 0;
    if(ConsecLoss >= MaxConsecLoss)
      Lock("Perdidas consecutivas: " + (string)ConsecLoss);
    bool remLocked = Webhook("close", sym, vol, price, pnl, LastDirection);
    if(remLocked && !IsLocked) Lock("Bloqueado remotamente");
  }
}

void CheckLock() {
  if(IsLocked) { CloseAllPositions(); return; }
  double bal  = AccountInfoDouble(ACCOUNT_BALANCE);
  double eq   = AccountInfoDouble(ACCOUNT_EQUITY);
  double dL   = (DayBal > 0) ? (DayBal - bal) / DayBal * 100 : 0;
  double dP   = (DayBal > 0) ? (bal - DayBal) / DayBal * 100 : 0;
  double dDD  = (DayBal > 0) ? (DayBal - eq)  / DayBal * 100 : 0;
  if(dL  >= MaxDailyLoss)   { Lock("Perdida diaria: "  + DoubleToString(dL,2)  + "%"); return; }
  if(dP  >= MaxDailyProfit) { Lock("Ganancia diaria: " + DoubleToString(dP,2)  + "%"); return; }
  if(dDD >= MaxDailyDD)     { Lock("DD diario: "       + DoubleToString(dDD,2) + "%"); return; }
  if(TradesToday > MaxTrades) { Lock("Max trades/dia: " + (string)TradesToday); return; }
  if(CooldownMin > 0 && ConsecLoss > 0) {
    int elapsed = (int)(TimeCurrent() - LastTrade) / 60;
    if(elapsed < CooldownMin) { IsLocked = true; return; }
  }
  if(BlockWeekends) {
    MqlDateTime t; TimeToStruct(TimeCurrent(), t);
    if(t.day_of_week == 5 && t.hour >= 20) { Lock("Fin de semana"); return; }
  }
}

void Lock(string reason) {
  if(IsLocked) return;
  IsLocked = true; LockReason = reason;
  CloseAllPositions();
  Print("BLOQUEADO: ", reason);
  Webhook("lock", _Symbol, 0, 0, 0, "");
}

void CloseAllPositions() {
  for(int i = PositionsTotal() - 1; i >= 0; i--) {
    ulong ticket = PositionGetTicket(i);
    if(ticket == 0) continue;
    if(!PositionSelectByTicket(ticket)) continue;
    string sym  = PositionGetString(POSITION_SYMBOL);
    double vol  = PositionGetDouble(POSITION_VOLUME);
    long   ptype = PositionGetInteger(POSITION_TYPE);
    MqlTradeRequest r = {}; MqlTradeResult rs = {};
    r.action   = TRADE_ACTION_DEAL;
    r.position = ticket; r.symbol = sym; r.volume = vol; r.deviation = 20;
    if(ptype == POSITION_TYPE_BUY) { r.type = ORDER_TYPE_SELL; r.price = SymbolInfoDouble(sym, SYMBOL_BID); }
    else                           { r.type = ORDER_TYPE_BUY;  r.price = SymbolInfoDouble(sym, SYMBOL_ASK); }
    OrderSend(r, rs);
  }
}

void ClosePositionByTicket(ulong ticket, string sym) {
  if(!PositionSelectByTicket(ticket)) return;
  double vol  = PositionGetDouble(POSITION_VOLUME);
  long   ptype = PositionGetInteger(POSITION_TYPE);
  MqlTradeRequest r = {}; MqlTradeResult rs = {};
  r.action   = TRADE_ACTION_DEAL;
  r.position = ticket; r.symbol = sym; r.volume = vol; r.deviation = 20;
  if(ptype == POSITION_TYPE_BUY) { r.type = ORDER_TYPE_SELL; r.price = SymbolInfoDouble(sym, SYMBOL_BID); }
  else                           { r.type = ORDER_TYPE_BUY;  r.price = SymbolInfoDouble(sym, SYMBOL_ASK); }
  OrderSend(r, rs);
}

void ManageBE() {
  for(int i = 0; i < PositionsTotal(); i++) {
    ulong t = PositionGetTicket(i);
    if(!PositionSelectByTicket(t)) continue;
    double op = PositionGetDouble(POSITION_PRICE_OPEN),
           sl = PositionGetDouble(POSITION_SL),
           cp = PositionGetDouble(POSITION_PRICE_CURRENT),
           pt = SymbolInfoDouble(_Symbol, SYMBOL_POINT),
           pp = BEpips * pt * 10;
    long ty = PositionGetInteger(POSITION_TYPE);
    if(ty == POSITION_TYPE_BUY  && cp >= op+pp && sl < op) ModSL(t, op+pt);
    if(ty == POSITION_TYPE_SELL && cp <= op-pp && sl > op) ModSL(t, op-pt);
  }
}

void ModSL(ulong t, double sl) {
  MqlTradeRequest r = {}; MqlTradeResult rs = {};
  r.action = TRADE_ACTION_SLTP; r.position = t;
  r.sl = sl; r.tp = PositionGetDouble(POSITION_TP);
  OrderSend(r, rs);
}

void CheckNewDay() {
  datetime d = iTime(_Symbol, PERIOD_D1, 0);
  if(d == DayStart) return;
  DayStart = d;
  DayBal   = AccountInfoDouble(ACCOUNT_BALANCE);
  TradesToday = 0; ConsecLoss = 0; IsLocked = false; LockReason = "";
  Print("Nuevo dia - contadores reseteados");
}

bool Webhook(string ev, string sym, double vol, double price, double pnl, string direction) {
  if(AccountToken == "") return false;
  string body = StringFormat(
    "{\"token\":\"%s\",\"event\":\"%s\",\"symbol\":\"%s\"," +
    "\"volume\":%.2f,\"price\":%.5f,\"pnl\":%.2f,\"direction\":\"%s\"," +
    "\"balance\":%.2f,\"trades_today\":%d,\"consec_loss\":%d}",
    AccountToken, ev, sym, vol, price, pnl, direction,
    AccountInfoDouble(ACCOUNT_BALANCE), TradesToday, ConsecLoss);
  char data[]; char result[]; string resHeaders;
  StringToCharArray(body, data, 0, StringLen(body));
  int res = WebRequest("POST", WebhookURL,
    "Content-Type: application/json\r\n", 5000, data, result, resHeaders);
  if(res == -1) { Print("Webhook error: ", GetLastError()); return false; }
  string resp = CharArrayToString(result);
  Print("Webhook OK [", ev, "] HTTP ", res, " | ", resp);
  if(StringFind(resp, "\"is_locked\":true") >= 0) return true;
  return false;
}`


/* ─── EMOTION STATS ──────────────────────────────────────────────────────── */
function EmotionStats({ trades }) {
  if (!trades.length) return (
    <div style={{ textAlign: 'center', padding: '32px', color: 'var(--t4)', fontSize: 12 }}>
      Sin trades registrados aún
    </div>
  );

  const stats = EMOTIONS.map(em => {
    const emoTrades = trades.filter(t => t.emotion === em.k);
    const wins = emoTrades.filter(t => t.status === 'WIN').length;
    const pnl = emoTrades.reduce((s, t) => s + (t.pnl || 0), 0);
    const wr = emoTrades.length ? (wins / emoTrades.length * 100) : 0;
    const avgPnl = emoTrades.length ? pnl / emoTrades.length : 0;
    return { ...em, count: emoTrades.length, wins, wr, pnl, avgPnl };
  }).filter(e => e.count > 0).sort((a, b) => b.wr - a.wr);

  if (!stats.length) return (
    <div style={{ textAlign: 'center', padding: '32px', color: 'var(--t4)', fontSize: 12 }}>
      Sin datos emocionales aún
    </div>
  );

  const best = stats[0];
  const worst = [...stats].sort((a, b) => a.avgPnl - b.avgPnl)[0];
  const maxCount = Math.max(...stats.map(s => s.count));

  return (
    <div>
      {/* Resumen top */}
      <div className="g2 mb4">
        <div className="card" style={{ borderLeft: '3px solid var(--green)' }}>
          <div className="card-lbl">😊 Mejor emoción</div>
          <div style={{ fontSize: 22 }}>{best.e}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--t1)', marginTop: 4 }}>{best.l}</div>
          <div style={{ fontSize: 11, color: 'var(--green)', fontFamily: 'var(--mono)', marginTop: 3 }}>
            {best.wr.toFixed(0)}% win rate · {fmtN(best.avgPnl)} avg
          </div>
        </div>
        <div className="card" style={{ borderLeft: '3px solid var(--red)' }}>
          <div className="card-lbl">⚠️ Peor emoción</div>
          <div style={{ fontSize: 22 }}>{worst.e}</div>
          <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--t1)', marginTop: 4 }}>{worst.l}</div>
          <div style={{ fontSize: 11, color: 'var(--red)', fontFamily: 'var(--mono)', marginTop: 3 }}>
            {worst.wr.toFixed(0)}% win rate · {fmtN(worst.avgPnl)} avg
          </div>
        </div>
      </div>

      {/* Tabla por emoción */}
      <div className="card mb4">
        <div className="sh mb3"><span className="st">Win Rate por Estado Emocional</span></div>
        {stats.map(em => (
          <div key={em.k} style={{ marginBottom: 14 }}>
            <div className="fl aic jb mb1">
              <div className="fl aic g2">
                <span style={{ fontSize: 18 }}>{em.e}</span>
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>{em.l}</span>
                <span style={{ fontSize: 11, color: 'var(--t3)', fontFamily: 'var(--mono)' }}>{em.count} trade{em.count !== 1 ? 's' : ''}</span>
              </div>
              <div className="fl aic g2">
                <span style={{ fontSize: 12, fontWeight: 700, color: em.avgPnl >= 0 ? 'var(--green)' : 'var(--red)', fontFamily: 'var(--mono)' }}>{fmtN(em.avgPnl)}</span>
                <span style={{ fontSize: 12, fontWeight: 700, color: em.wr >= 50 ? 'var(--green)' : 'var(--red)', fontFamily: 'var(--mono)', minWidth: 40, textAlign: 'right' }}>{em.wr.toFixed(0)}%</span>
              </div>
            </div>
            {/* Barra win rate */}
            <div style={{ height: 6, background: 'var(--s3)', borderRadius: 3, overflow: 'hidden', marginBottom: 4 }}>
              <div style={{ height: '100%', width: `${em.wr}%`, background: em.wr >= 60 ? 'var(--green)' : em.wr >= 40 ? 'var(--yellow)' : 'var(--red)', borderRadius: 3, transition: 'width .5s' }} />
            </div>
            {/* Barra frecuencia */}
            <div style={{ height: 3, background: 'var(--s3)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(em.count / maxCount) * 100}%`, background: 'var(--ac)', borderRadius: 2, opacity: 0.4, transition: 'width .5s' }} />
            </div>
          </div>
        ))}
      </div>

      {/* PnL por emoción */}
      <div className="card">
        <div className="sh mb3"><span className="st">PnL Total por Emoción</span></div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {[...stats].sort((a, b) => b.pnl - a.pnl).map(em => {
            const maxAbs = Math.max(...stats.map(s => Math.abs(s.pnl)), 1);
            const pct = Math.abs(em.pnl) / maxAbs * 100;
            return (
              <div key={em.k} className="fl aic g2">
                <span style={{ fontSize: 16, width: 24, flexShrink: 0 }}>{em.e}</span>
                <span style={{ fontSize: 12, color: 'var(--t2)', width: 80, flexShrink: 0 }}>{em.l}</span>
                <div style={{ flex: 1, height: 8, background: 'var(--s3)', borderRadius: 4, overflow: 'hidden' }}>
                  <div style={{ height: '100%', width: `${pct}%`, background: em.pnl >= 0 ? 'var(--green)' : 'var(--red)', borderRadius: 4, transition: 'width .5s' }} />
                </div>
                <span style={{ fontSize: 12, fontWeight: 700, fontFamily: 'var(--mono)', color: em.pnl >= 0 ? 'var(--green)' : 'var(--red)', minWidth: 72, textAlign: 'right' }}>{fmtN(em.pnl)}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}


/* ─── SETUP STATS ────────────────────────────────────────────────────────── */
function SetupStats({ trades }) {
  if (!trades.length) return null;

  const stats = {};
  trades.forEach(t => {
    const s = t.setup || 'Sin setup';
    if (!stats[s]) stats[s] = { count: 0, wins: 0, losses: 0, be: 0, pnl: 0 };
    stats[s].count++;
    stats[s].pnl += t.pnl || 0;
    if (t.status === 'WIN') stats[s].wins++;
    else if (t.status === 'LOSS') stats[s].losses++;
    else stats[s].be++;
  });

  const rows = Object.entries(stats)
    .map(([name, s]) => ({ name, ...s, wr: s.count ? (s.wins / s.count * 100) : 0, avgPnl: s.count ? s.pnl / s.count : 0 }))
    .sort((a, b) => b.wr - a.wr);

  const best = rows[0];
  const worst = [...rows].sort((a, b) => a.avgPnl - b.avgPnl)[0];
  const maxCount = Math.max(...rows.map(r => r.count));

  return (
    <div>
      <div className="g2 mb4">
        <div className="card" style={{ borderLeft: '3px solid var(--green)' }}>
          <div className="card-lbl">🏆 Mejor setup</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--t1)', marginBottom: 3 }}>{best.name}</div>
          <div style={{ fontSize: 11, color: 'var(--green)', fontFamily: 'var(--mono)' }}>
            {best.wr.toFixed(0)}% WR · {fmtN(best.avgPnl)} avg · {best.count} trades
          </div>
        </div>
        <div className="card" style={{ borderLeft: '3px solid var(--red)' }}>
          <div className="card-lbl">⚠️ Peor setup</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--t1)', marginBottom: 3 }}>{worst.name}</div>
          <div style={{ fontSize: 11, color: 'var(--red)', fontFamily: 'var(--mono)' }}>
            {worst.wr.toFixed(0)}% WR · {fmtN(worst.avgPnl)} avg · {worst.count} trades
          </div>
        </div>
      </div>

      <div className="card">
        <div className="sh mb3"><span className="st">Win Rate por Setup</span></div>
        {rows.map(r => (
          <div key={r.name} style={{ marginBottom: 16 }}>
            <div className="fl aic jb mb1">
              <div className="fl aic g2">
                <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)' }}>{r.name}</span>
                <span style={{ fontSize: 10, color: 'var(--t4)', fontFamily: 'var(--mono)' }}>{r.count} trade{r.count !== 1 ? 's' : ''}</span>
              </div>
              <div className="fl aic g3">
                <span style={{ fontSize: 10, color: 'var(--green)', fontFamily: 'var(--mono)' }}>{r.wins}W</span>
                <span style={{ fontSize: 10, color: 'var(--red)', fontFamily: 'var(--mono)' }}>{r.losses}L</span>
                {r.be > 0 && <span style={{ fontSize: 10, color: 'var(--yellow)', fontFamily: 'var(--mono)' }}>{r.be}BE</span>}
                <span style={{ fontSize: 12, fontWeight: 700, color: r.avgPnl >= 0 ? 'var(--green)' : 'var(--red)', fontFamily: 'var(--mono)', minWidth: 60, textAlign: 'right' }}>{fmtN(r.avgPnl)}</span>
                <span style={{ fontSize: 13, fontWeight: 700, color: r.wr >= 60 ? 'var(--green)' : r.wr >= 40 ? 'var(--yellow)' : 'var(--red)', fontFamily: 'var(--mono)', minWidth: 38, textAlign: 'right' }}>{r.wr.toFixed(0)}%</span>
              </div>
            </div>
            {/* WR bar */}
            <div style={{ height: 6, background: 'var(--s3)', borderRadius: 3, overflow: 'hidden', marginBottom: 3 }}>
              <div style={{ height: '100%', width: `${r.wr}%`, background: r.wr >= 60 ? 'var(--green)' : r.wr >= 40 ? 'var(--yellow)' : 'var(--red)', borderRadius: 3, transition: 'width .5s' }} />
            </div>
            {/* Frecuencia bar */}
            <div style={{ height: 3, background: 'var(--s3)', borderRadius: 2, overflow: 'hidden' }}>
              <div style={{ height: '100%', width: `${(r.count / maxCount) * 100}%`, background: 'var(--ac)', borderRadius: 2, opacity: 0.35, transition: 'width .5s' }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ─── VIEWS ──────────────────────────────────────────────────────────────── */


function StatsView({ trades }) {
  const [tab, setTab] = useState('emotions');
  return (
    <div>
      <div className="fl g2 mb4">
        <button className={`chip ${tab === 'emotions' ? 'on' : ''}`} onClick={() => setTab('emotions')}>🧠 Emociones</button>
        <button className={`chip ${tab === 'setups' ? 'on' : ''}`} onClick={() => setTab('setups')}>📐 Setups</button>
      </div>
      {tab === 'emotions' && <EmotionStats trades={trades} />}
      {tab === 'setups' && <SetupStats trades={trades} />}
    </div>
  );
}

function Dashboard({ accounts, trades }) {
  const totalBal = accounts.reduce((s, a) => s + a.balance, 0);
  const pnl = trades.reduce((s, t) => s + t.pnl, 0);
  const wins = trades.filter(t => t.status === 'WIN').length;
  const wr = trades.length ? (wins / trades.length * 100).toFixed(1) : 0;
  const gW = trades.filter(t => t.pnl > 0).reduce((s, t) => s + t.pnl, 0);
  const gL = Math.abs(trades.filter(t => t.pnl < 0).reduce((s, t) => s + t.pnl, 0));
  const pf = (gW / (gL || 1)).toFixed(2);
  const locked = accounts.filter(a => a.locked);

  return (
    <div>
      {locked.length > 0 && (
        <div className="alert al-r mb4">
          <span>🔒</span>
          <div><b>{locked.length} cuenta{locked.length > 1 ? 's bloqueadas' : ' bloqueada'}</b> hasta medianoche — {locked.map(a => a.name).join(', ')}</div>
        </div>
      )}
      <div className="g4 mb4">
        {[
          { l: 'Balance Total', v: `$${totalBal.toLocaleString()}`, sub: `${accounts.length} cuentas`, ico: '💰', c: 'var(--t1)' },
          { l: 'PnL Acumulado', v: fmtN(pnl), sub: `${trades.length} operaciones`, ico: '📈', c: col(pnl) },
          { l: 'Win Rate', v: `${wr}%`, sub: `${wins} wins`, ico: '🎯', c: 'var(--ac)' },
          { l: 'Profit Factor', v: pf, sub: 'Gross W / Gross L', ico: '⚖️', c: +pf >= 1.5 ? 'var(--green)' : +pf >= 1 ? 'var(--yellow)' : 'var(--red)' },
        ].map(s => (
          <div className="card" key={s.l}>
            <div className="fl aic jb mb2"><span className="card-lbl">{s.l}</span><span style={{ fontSize: 18 }}>{s.ico}</span></div>
            <div className="card-val" style={{ color: s.c }}>{s.v}</div>
            <div className="card-sub">{s.sub}</div>
          </div>
        ))}
      </div>
      <div className="g21 mb4">
        <div className="card">
          <div className="sh mb2"><span className="st">📊 Curva de Equidad</span></div>
          <MiniCurve trades={trades} h={160} />
          <div className="fl g2 mt3" style={{ flexWrap: 'wrap' }}>
            {['WIN', 'LOSS', 'BE'].map(s => (
              <div key={s} className="chip">
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: s === 'WIN' ? 'var(--green)' : s === 'LOSS' ? 'var(--red)' : 'var(--yellow)', display: 'inline-block' }} />
                {s}: {trades.filter(t => t.status === s).length}
              </div>
            ))}
          </div>
        </div>
        <div className="card">
          <div className="sh mb2"><span className="st">📋 Resumen</span></div>
          {[
            { l: 'Mejor trade', v: fmtN(Math.max(...trades.map(t => t.pnl), 0)), c: 'var(--green)' },
            { l: 'Peor trade', v: fmtN(Math.min(...trades.map(t => t.pnl), 0)), c: 'var(--red)' },
            { l: 'Avg ganancia', v: fmtN(gW / (wins || 1)), c: 'var(--green)' },
            { l: 'Avg pérdida', v: fmtN(-gL / (trades.filter(t => t.pnl < 0).length || 1)), c: 'var(--red)' },
            { l: 'R:R promedio', v: (() => { const rrTrades = trades.filter(t => t.rr && t.rr.includes(':')); if (!rrTrades.length) return 'N/A'; const avg = rrTrades.reduce((s, t) => { const parts = t.rr.split(':'); return s + (parseFloat(parts[1]) || 0); }, 0) / rrTrades.length; return '1:' + avg.toFixed(2); })(), c: 'var(--ac)' },
          ].map(s => (
            <div key={s.l} className="fl aic jb" style={{ padding: '6px 0', borderBottom: '1px solid var(--b1)' }}>
              <span className="fm" style={{ fontSize: 10, color: 'var(--t3)' }}>{s.l}</span>
              <span className="fm" style={{ fontSize: 12, fontWeight: 700, color: s.c }}>{s.v}</span>
            </div>
          ))}
        </div>
      </div>
      <div className="card">
        <div className="sh mb3"><span className="st">📋 Trades Recientes</span></div>
        <div className="tbl-wrap">
          <table>
            <thead><tr>{['PAR','TIPO','PnL','SETUP','EMOCIÓN','SESIÓN','ESTADO'].map(h => <th key={h}>{h}</th>)}</tr></thead>
            <tbody>
              {trades.filter(t => t.status !== 'open').slice(0, 8).map(t => (
                <tr key={t.id}>
                  <td className="hl">{t.pair}</td>
                  <td><span className={`tag ${t.type === 'BUY' ? 'tg' : 'tr'}`}>{t.type}</span></td>
                  <td style={{ color: col(t.pnl), fontWeight: 700 }}>{fmtN(t.pnl)}</td>

                  <td><span className="tag tp">{t.setup}</span></td>
                  <td style={{ fontSize: 12 }}>{EMOTIONS.find(e => e.k === t.emotion)?.e} {t.emotion}</td>
                  <td><span className="tag tb">{t.session}</span></td>
                  <td><span className={`tag ${t.status === 'WIN' ? 'tg' : t.status === 'LOSS' ? 'tr' : 'ty'}`}>{t.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/* ─── VOICE NOTE ─────────────────────────────────────────────────────────── */
function VoiceNote({ tradeId, existingAudio, existingTranscript, onSaved }) {
  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(existingAudio || null);
  const [transcript, setTranscript] = useState(existingTranscript || '');
  const [editingTranscript, setEditingTranscript] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [secondsLeft, setSecondsLeft] = useState(60);
  const mediaRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);

  const startRecording = async () => {
    setError('');
    setSecondsLeft(60);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mimeType = MediaRecorder.isTypeSupported('audio/mp4') ? 'audio/mp4' : 'audio/webm';
      const mr = new MediaRecorder(stream, { mimeType });
      chunksRef.current = [];
      mr.ondataavailable = e => chunksRef.current.push(e.data);
      mr.onstop = async () => {
        clearInterval(timerRef.current);
        const blob = new Blob(chunksRef.current, { type: mimeType });
        const url = URL.createObjectURL(blob);
        setAudioURL(url);
        stream.getTracks().forEach(t => t.stop());
        await transcribeAndSave(blob);
      };
      mr.start();
      mediaRef.current = mr;
      setRecording(true);

      // Cuenta atrás 60s
      let secs = 60;
      timerRef.current = setInterval(() => {
        secs -= 1;
        setSecondsLeft(secs);
        if (secs <= 0) {
          clearInterval(timerRef.current);
          mr.stop();
          setRecording(false);
        }
      }, 1000);

    } catch (e) {
      setError('No se pudo acceder al micrófono');
    }
  };

  const stopRecording = () => {
    clearInterval(timerRef.current);
    if (mediaRef.current) {
      mediaRef.current.stop();
      setRecording(false);
    }
  };

  const deleteAudio = async () => {
    await supabase.from('trades').update({ audio_url: null, transcript: null }).eq('id', tradeId);
    setAudioURL(null);
    setTranscript('');
    if (onSaved) onSaved(null, '');
  };

  const saveTranscript = async (text) => {
    setTranscript(text);
    setEditingTranscript(false);
    await supabase.from('trades').update({ transcript: text }).eq('id', tradeId);
    if (onSaved) onSaved(audioURL, text);
  };

  const transcribeAndSave = async (blob) => {
    setLoading(true);
    try {
      const ext = blob.type.includes('mp4') ? 'mp4' : 'webm';
      const fileName = `voice_${tradeId}_${Date.now()}.${ext}`;
      const { error: storageError } = await supabase.storage
        .from('voice-notes')
        .upload(fileName, blob, { contentType: 'audio/webm', upsert: true });
      if (storageError) throw new Error('Error al subir audio');
      const { data: { publicUrl } } = supabase.storage.from('voice-notes').getPublicUrl(fileName);

      // Transcripción con Deepgram (Nova-3, español)
      const res = await fetch('https://api.deepgram.com/v1/listen?model=nova-3&language=es&smart_format=true&punctuate=true', {
        method: 'POST',
        headers: {
          Authorization: `Token ${process.env.REACT_APP_DEEPGRAM_API_KEY}`,
          'Content-Type': blob.type || 'audio/webm',
        },
        body: blob,
      });
      if (!res.ok) {
        const errTxt = await res.text();
        throw new Error('Deepgram: ' + errTxt);
      }
      const result = await res.json();
      const text = result?.results?.channels?.[0]?.alternatives?.[0]?.transcript || '';
      setTranscript(text);

      await supabase.from('trades').update({ audio_url: publicUrl, transcript: text }).eq('id', tradeId);
      if (onSaved) onSaved(publicUrl, text);
    } catch (e) {
      setError('Error al procesar el audio: ' + e.message);
    }
    setLoading(false);
  };

  return (
    <div style={{ marginBottom: 12 }}>
      <label style={{ display: 'block', fontSize: 11, fontWeight: 500, color: 'var(--t3)', marginBottom: 6 }}>
        🎙️ Nota de voz
      </label>

      {/* Botón grabar */}
      {!recording && !loading && (
        <button
          onClick={startRecording}
          className="btn"
          style={{ background: 'var(--red-bg)', border: '1px solid rgba(220,38,38,0.3)', color: 'var(--red)', fontSize: 12, marginBottom: 8 }}
        >
          ⏺ {audioURL ? 'Regrabar' : 'Grabar nota'}
        </button>
      )}

      {/* Grabando con cuenta atrás */}
      {recording && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <button
            onClick={stopRecording}
            className="btn"
            style={{ background: 'var(--red)', color: '#fff', fontSize: 12 }}
          >
            ⏹ Parar
          </button>
          <span style={{ fontFamily: 'var(--mono)', fontSize: 13, color: secondsLeft <= 10 ? 'var(--red)' : 'var(--t3)' }}>
            {secondsLeft}s
          </span>
        </div>
      )}

      {/* Procesando */}
      {loading && (
        <div style={{ fontSize: 12, color: 'var(--t3)', marginBottom: 8 }}>⏳ Transcribiendo...</div>
      )}

      {/* Player + botón borrar */}
      {audioURL && !loading && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
          <audio controls src={audioURL} style={{ flex: 1, borderRadius: 8 }} />
          <button
            onClick={deleteAudio}
            className="btn btn-sm br"
            title="Borrar audio"
          >
            🗑️
          </button>
        </div>
      )}

      {/* Transcripción + editar */}
      {transcript && !editingTranscript && (
        <div style={{ background: 'var(--s2)', border: '1px solid var(--b1)', borderRadius: 8, padding: '8px 12px', fontSize: 12, color: 'var(--t2)', lineHeight: 1.6, position: 'relative' }}>
          <span style={{ fontSize: 10, color: 'var(--t4)', fontWeight: 600, letterSpacing: 1 }}>TRANSCRIPCIÓN · </span>
          {transcript}
          <button
            onClick={() => setEditingTranscript(true)}
            style={{ position: 'absolute', top: 6, right: 8, background: 'none', border: 'none', cursor: 'pointer', fontSize: 12, color: 'var(--t3)' }}
            title="Editar transcripción"
          >
            ✏️
          </button>
        </div>
      )}

      {/* Editor de transcripción */}
      {editingTranscript && (
        <div>
          <textarea
            defaultValue={transcript}
            id="transcript-edit"
            style={{ width: '100%', borderRadius: 8, padding: '8px 12px', fontSize: 12, minHeight: 80, marginBottom: 6 }}
          />
          <div style={{ display: 'flex', gap: 6 }}>
            <button className="btn btn-sm bp" onClick={() => saveTranscript(document.getElementById('transcript-edit').value)}>
              Guardar
            </button>
            <button className="btn btn-sm bg" onClick={() => setEditingTranscript(false)}>
              Cancelar
            </button>
          </div>
        </div>
      )}

      {error && <div style={{ fontSize: 11, color: 'var(--red)', marginTop: 4 }}>⚠️ {error}</div>}
    </div>
  );
}
/* ─── EDIT MODAL ─────────────────────────────────────────────────────────── */
function EditModal({ trade, accounts, customSetups, onClose, onSave }) {
  const [et, setEt] = useState({
    pair: trade.pair || '',
    type: trade.type || 'BUY',
    lots: trade.lots || 0.1,
    open_price: trade.open_price || '',
    close_price: trade.close_price || '',
    pnl: trade.pnl || '',
    rr: trade.rr || '',
    status: trade.status || 'WIN',
    setup: trade.setup || 'LQ Sweep',
    session: trade.session || 'London',
    emotion: trade.emotion || 'calm',
    note: trade.note || '',
  });
  const [editImages, setEditImages] = useState(trade.images || []);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const save = async () => {
    setSaving(true);
    setError('');
    const updates = {
      pair: et.pair,
      type: et.type,
      lots: parseFloat(et.lots) || 0.01,
      open_price: parseFloat(et.open_price) || 0,
      close_price: parseFloat(et.close_price) || 0,
      pnl: parseFloat(et.pnl) || 0,
      rr: et.rr || '',
      status: et.status,
      setup: et.setup,
      session: et.session,
      emotion: et.emotion,
      note: et.note,
      images: editImages,
    };
    const { data, error } = await supabase.from('trades').update(updates).eq('id', trade.id).select().single();
    setSaving(false);
    if (!error && data) {
      onSave(data);
    } else {
      setError(error?.message || 'Error al guardar');
    }
  };

  return (
    <div className="ov" onClick={onClose}>
      <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
        <button className="mc" onClick={onClose}>✕</button>
        <div className="mt">✏️ Editar Trade</div>
        <div className="ms">{trade.pair} · {(trade.created_at || '').slice(0,10)}</div>

        <div className="fr mb3">
          <div className="fg" style={{marginBottom:0}}><label>Par</label>
            <input value={et.pair} onChange={e => setEt(p => ({...p, pair: e.target.value.toUpperCase()}))} />
          </div>
          <div className="fg" style={{marginBottom:0}}><label>Dirección</label>
            <select value={et.type} onChange={e => setEt(p => ({...p, type: e.target.value}))}>
              <option>BUY</option><option>SELL</option>
            </select>
          </div>
        </div>

        <div className="fr3 mb3">
          <div className="fg" style={{marginBottom:0}}><label>Precio entrada</label>
            <input value={et.open_price} onChange={e => setEt(p => ({...p, open_price: e.target.value}))} />
          </div>
          <div className="fg" style={{marginBottom:0}}><label>Precio cierre</label>
            <input value={et.close_price} onChange={e => setEt(p => ({...p, close_price: e.target.value}))} />
          </div>
          <div className="fg" style={{marginBottom:0}}><label>Lotes</label>
            <input type="number" step="0.01" value={et.lots} onChange={e => setEt(p => ({...p, lots: e.target.value}))} />
          </div>
        </div>

        <div className="fr3 mb3">
          <div className="fg" style={{marginBottom:0}}><label>PnL ($)</label>
            <input type="number" step="0.01" value={et.pnl} onChange={e => setEt(p => ({...p, pnl: e.target.value}))} />
          </div>
          <div className="fg" style={{marginBottom:0}}><label>R:R</label>
            <input value={et.rr || ''} onChange={e => setEt(p => ({...p, rr: e.target.value}))} placeholder="1:2" />
          </div>
          <div className="fg" style={{marginBottom:0}}><label>Setup</label>
            <select value={et.setup} onChange={e => setEt(p => ({...p, setup: e.target.value}))}>
              {customSetups.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
        </div>

        <div className="fr mb3">
          <div className="fg" style={{marginBottom:0}}><label>Sesión</label>
            <select value={et.session} onChange={e => setEt(p => ({...p, session: e.target.value}))}>
              {SESSIONS.map(s => <option key={s}>{s}</option>)}
            </select>
          </div>
          <div className="fg" style={{marginBottom:0}}><label>Resultado</label>
            <select value={et.status} onChange={e => setEt(p => ({...p, status: e.target.value}))}>
              <option>WIN</option><option>LOSS</option><option>BE</option>
            </select>
          </div>
        </div>

        <div className="fg mb3">
          <label>Estado emocional</label>
          <div className="emo-row">
            {EMOTIONS.map(em => (
              <button key={em.k} className={`emo-btn ${et.emotion === em.k ? 'sel' : ''}`} onClick={() => setEt(p => ({...p, emotion: em.k}))}>
                <span className="ei">{em.e}</span>{em.l}
              </button>
            ))}
          </div>
        </div>

        <div className="fg mb3">
          <label>Notas</label>
          <textarea value={et.note} onChange={e => setEt(p => ({...p, note: e.target.value}))} placeholder="Razonamiento, errores, aprendizajes..." />
        </div><div className="fg mb3">
          <VoiceNote
            tradeId={trade.id}
            existingAudio={trade.audio_url}
            existingTranscript={trade.transcript}
            onSaved={(url, text) => {}}
          />
        </div>

        <div className="fg mb4">
          <ImageUploader images={editImages} onChange={setEditImages} />
        </div>

        {error && <div className="alert al-r mb3" style={{fontSize:11}}><span>❌</span><span>{error}</span></div>}

        <div className="fl g2">
          <button className="btn bp w100" onClick={save} disabled={saving}>{saving ? '⏳ Guardando...' : 'Guardar cambios'}</button>
          <button className="btn bg" onClick={onClose}>Cancelar</button>
        </div>
      </div>
    </div>
  );
}

function JournalView({ trades, setTrades, accounts, setAccounts, session }) {
  const [showAdd, setShowAdd] = useState(false);
  const [filter, setFilter] = useState('ALL');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [periodFilter, setPeriodFilter] = useState('ALL');
  const [expandId, setExpandId] = useState(null);
  const [lightbox, setLightbox] = useState(null);
  const [images, setImages] = useState([]);
  const [showSetupEditor, setShowSetupEditor] = useState(false);
  const [editTrade, setEditTrade] = useState(null);
  const [selectedAccount, setSelectedAccount] = useState('ALL');
  const [showCalendar, setShowCalendar] = useState(true);
  const [calMonth, setCalMonth] = useState(() => { const n = new Date(); return { y: n.getFullYear(), m: n.getMonth() }; });
  const [customSetups, setCustomSetups] = useState(SETUPS);
  const [setupsSaving, setSetupsSaving] = useState(false);

  // Cargar setups de la cuenta seleccionada
  useEffect(() => {
    if (selectedAccount === 'ALL') {
      setCustomSetups(SETUPS);
      return;
    }
    const acc = accounts.find(a => String(a.id) === String(selectedAccount));
    if (acc?.custom_setups && acc.custom_setups.length > 0) {
      setCustomSetups(acc.custom_setups);
    } else {
      setCustomSetups(SETUPS);
    }
  }, [selectedAccount, accounts]);

  const saveSetups = async (newSetups) => {
    if (selectedAccount === 'ALL') return;
    setSetupsSaving(true);
    await supabase.from('accounts').update({ custom_setups: newSetups }).eq('id', selectedAccount);
    setAccounts(p => p.map(a => String(a.id) === String(selectedAccount) ? { ...a, custom_setups: newSetups } : a));
    setSetupsSaving(false);
  };

  const [newSetupName, setNewSetupName] = useState('');
  const [nt, setNt] = useState({ accId: null, pair: 'XAUUSD', type: 'BUY', lots: 0.1, open: '', close: '', pnl: '', rr: '1:2', status: 'WIN', setup: SETUPS[0], session: 'London', emotion: 'calm', note: '' });

  // Inicializar accId con el primer account real cuando lleguen las cuentas
  useEffect(() => {
    if (accounts.length > 0 && !nt.accId) {
      setNt(p => ({ ...p, accId: accounts[0].id }));
    }
  }, [accounts]);

  // Sync nt.setup solo si el setup actual ya no existe en la nueva lista
  useEffect(() => {
    setNt(p => ({
      ...p,
      setup: customSetups.includes(p.setup) ? p.setup : (customSetups[0] || SETUPS[0])
    }));
  }, [customSetups]);

  const calDays = (() => {
    const { y, m } = calMonth;
    const daysInMonth = new Date(y, m + 1, 0).getDate();
    const days = {};
    trades.forEach(t => {
      if (selectedAccount !== 'ALL' && String(t.account_id) !== String(selectedAccount)) return;
      const d = new Date(t.created_at || t.date);
      if (d.getFullYear() === y && d.getMonth() === m) {
        const day = d.getDate();
        days[day] = (days[day] || 0) + (t.pnl || 0);
      }
    });
    return { daysInMonth, days, firstDow: new Date(y, m, 1).getDay() };
  })();

  const filtered = trades.filter(t => {
    if (t.status === 'open') return false; // nunca mostrar trades abiertos en el journal
    if (selectedAccount !== 'ALL' && String(t.account_id) !== String(selectedAccount)) return false;
    // Status filter
    if (filter !== 'ALL' && t.status !== filter) return false;
    // Period quick filter
    if (periodFilter !== 'ALL') {
      const now = new Date();
      const tDate = new Date(t.created_at || t.date);
      if (periodFilter === 'HOY') {
        if (tDate.toDateString() !== now.toDateString()) return false;
      } else if (periodFilter === 'SEMANA') {
        const weekAgo = new Date(now); weekAgo.setDate(now.getDate() - 7);
        if (tDate < weekAgo) return false;
      } else if (periodFilter === 'MES') {
        if (tDate.getMonth() !== now.getMonth() || tDate.getFullYear() !== now.getFullYear()) return false;
      }
    }
    // Custom date range
    if (dateFrom) {
      const from = new Date(dateFrom); from.setHours(0,0,0,0);
      if (new Date(t.created_at || t.date) < from) return false;
    }
    if (dateTo) {
      const to = new Date(dateTo); to.setHours(23,59,59,999);
      if (new Date(t.created_at || t.date) > to) return false;
    }
    return true;
  });
  const bySetup = {};
  trades.forEach(t => { bySetup[t.setup] = (bySetup[t.setup] || 0) + t.pnl; });
  const topSetup = Object.entries(bySetup).sort((a, b) => b[1] - a[1])[0];
  const filteredPnl = filtered.reduce((s, t) => s + (t.pnl || 0), 0);
  const filteredWins = filtered.filter(t => t.status === 'WIN').length;

  const [saveError, setSaveError] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!session?.user?.id) { setSaveError('No hay sesión activa. Recarga la página.'); return; }
    setSaving(true);
    setSaveError('');
    const trade = {
      user_id: session.user.id,
      account_id: nt.accId || null,
      pair: nt.pair,
      type: nt.type,
      lots: parseFloat(nt.lots) || 0.01,
      open_price: parseFloat(nt.open) || 0,
      close_price: parseFloat(nt.close) || 0,
      pnl: parseFloat(nt.pnl) || 0,
      rr: nt.rr || '',
      status: nt.status,
      setup: nt.setup,
      session: nt.session,
      emotion: nt.emotion,
      note: nt.note,
      images: images.map(img => ({ id: img.id, src: img.src, name: img.name })),
    };
    console.log('Guardando trade en Supabase...', { user_id: trade.user_id, pair: trade.pair });
    const { data, error } = await supabase.from('trades').insert([trade]).select().single();
    setSaving(false);
    if (!error && data) {
      console.log('Trade guardado OK:', data);
      setTrades(p => [data, ...p]);
      setImages([]);
      setShowAdd(false);
    } else {
      console.error('Supabase error completo:', JSON.stringify(error, null, 2));
      const msg = error?.message || error?.details || error?.hint || JSON.stringify(error);
      setSaveError('Error Supabase: ' + msg);
    }
  };

  const exportCSV = () => {
    const NL = String.fromCharCode(10);
    const headers = ['Fecha','Par','Tipo','Lotes','Precio Entrada','Precio Cierre','PnL','Estado','Setup','Sesion','Emocion','Notas'];
    const rows = filtered.map(t => [
      (t.created_at || t.date || '').slice(0,16).replace('T',' '),
      t.pair, t.type, t.lots,
      t.open_price, t.close_price,
      t.pnl, t.status, t.setup,
      t.session, t.emotion,
      (t.note || '').replace(/,/g, ';').replace(/\n/g, ' '),
    ]);
    const csv = [headers, ...rows].map(r => r.join(',')).join(NL);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'tradeleash_' + new Date().toISOString().slice(0,10) + '.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const MESES = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
  const DIAS_SEMANA = ['Dom','Lun','Mar','Mie','Jue','Vie','Sab'];

  // Stats para cuenta seleccionada
  const selAcc = selectedAccount !== 'ALL' ? accounts.find(a => String(a.id) === String(selectedAccount)) : null;
  const selT = selectedAccount === 'ALL' ? trades.filter(t => t.status !== 'open') : trades.filter(t => String(t.account_id) === String(selectedAccount) && t.status !== 'open');
  const selWins = selT.filter(t => t.status === 'WIN');
  const selLosses = selT.filter(t => t.status === 'LOSS');
  const selPnl = selT.reduce((s, t) => s + (t.pnl || 0), 0);
  const selWr = selT.length ? ((selWins.length / selT.length) * 100).toFixed(1) : '0.0';
  const selAvgWin = selWins.length ? selWins.reduce((s, t) => s + t.pnl, 0) / selWins.length : 0;
  const selAvgLoss = selLosses.length ? selLosses.reduce((s, t) => s + t.pnl, 0) / selLosses.length : 0;
  const selGW = selWins.reduce((s, t) => s + t.pnl, 0);
  const selGL = Math.abs(selLosses.reduce((s, t) => s + t.pnl, 0));
  const selPF = selGL > 0 ? (selGW / selGL).toFixed(2) : selGW > 0 ? '∞' : '0.00';
  const selPnlHoy = selT.filter(t => new Date(t.created_at||t.date).toDateString() === new Date().toDateString()).reduce((s,t) => s+(t.pnl||0), 0);

  // Rachas máximas
  const { maxWinStreak, maxLossStreak } = (() => {
    const sorted = [...selT].sort((a,b) => new Date(a.created_at||a.date) - new Date(b.created_at||b.date));
    let mW = 0, mL = 0, cW = 0, cL = 0;
    sorted.forEach(t => {
      if (t.status === 'WIN') { cW++; cL = 0; mW = Math.max(mW, cW); }
      else if (t.status === 'LOSS') { cL++; cW = 0; mL = Math.max(mL, cL); }
      else { cW = 0; cL = 0; }
    });
    return { maxWinStreak: mW, maxLossStreak: mL };
  })();

  // Curva de equidad — datos enriquecidos
  const eqData = (() => {
    const sorted = [...selT].sort((a,b) => new Date(a.created_at||a.date) - new Date(b.created_at||b.date));
    let cum = 0;
    return sorted.map(t => ({
      v: (cum += (t.pnl||0), cum),
      pnl: t.pnl||0,
      date: (t.created_at||t.date||'').slice(0,10),
      pair: t.pair,
      status: t.status,
    }));
  })();
  const eqPts = eqData.map(d => d.v);

  const EquityChart = () => {
    const [hover, setHover] = useState(null);
    if (eqData.length < 2) return <div style={{color:'var(--t4)',fontSize:12,textAlign:'center',padding:'20px 0'}}>Sin suficientes trades</div>;

    const W = 600, H = 160;
    const PAD = { l: 60, r: 20, t: 20, b: 36 };
    const iW = W - PAD.l - PAD.r, iH = H - PAD.t - PAD.b;
    const vals = eqData.map(d => d.v);
    const rawMin = Math.min(...vals), rawMax = Math.max(...vals);
    const rangePad = (rawMax - rawMin) * 0.15 || 20;
    const vMin = rawMin - rangePad, vMax = rawMax + rangePad;
    const vRange = vMax - vMin;
    const toX = i => PAD.l + (i / (eqData.length - 1)) * iW;
    const toY = v => PAD.t + iH - ((v - vMin) / vRange) * iH;
    const isPos = vals[vals.length - 1] >= 0;
    const color = isPos ? '#059669' : '#dc2626';
    const colorLight = isPos ? 'rgba(5,150,105,0.12)' : 'rgba(220,38,38,0.1)';

    // Smooth bezier
    const linePts = eqData.map((d, i) => ({ x: toX(i), y: toY(d.v) }));
    const pathD = linePts.map((p, i) => {
      if (i === 0) return `M ${p.x},${p.y}`;
      const prev = linePts[i-1];
      const cpx = (p.x - prev.x) * 0.4;
      return `C ${prev.x+cpx},${prev.y} ${p.x-cpx},${p.y} ${p.x},${p.y}`;
    }).join(' ');

    const areaD = `${pathD} L ${linePts[linePts.length-1].x},${PAD.t+iH} L ${linePts[0].x},${PAD.t+iH} Z`;

    // 5 Y ticks
    const yTicks = Array.from({length:5}, (_,i) => rawMin + ((rawMax-rawMin)/4)*i);
    // X ticks — max 6
    const n = Math.min(6, eqData.length);
    const xIdxs = Array.from({length:n}, (_,i) => Math.round(i*(eqData.length-1)/(n-1)));

    return (
      <div style={{position:'relative', background:'var(--s2)', borderRadius:10, padding:'12px 8px 4px 0', border:'1px solid var(--b1)'}}>
        <svg viewBox={`0 0 ${W} ${H}`} style={{width:'100%', height:200, display:'block'}} preserveAspectRatio="xMidYMid meet">
          <defs>
            <linearGradient id="ecFill2" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor={color} stopOpacity="0.2"/>
              <stop offset="100%" stopColor={color} stopOpacity="0.01"/>
            </linearGradient>
            <clipPath id="ecClip2">
              <rect x={PAD.l} y={PAD.t} width={iW} height={iH}/>
            </clipPath>
          </defs>

          {/* Y grid + labels */}
          {yTicks.map((v, i) => (
            <g key={i}>
              <line x1={PAD.l} y1={toY(v)} x2={PAD.l+iW} y2={toY(v)}
                stroke="var(--b1)" strokeWidth="1" strokeDasharray="4,4"/>
              <text x={PAD.l-8} y={toY(v)+4} textAnchor="end"
                fontSize="10" fill="var(--t3)" fontFamily="monospace">
                {v >= 0 ? '+' : ''}{v.toFixed(0)}$
              </text>
            </g>
          ))}

          {/* X labels */}
          {xIdxs.map((idx, i) => (
            <text key={i} x={toX(idx)} y={H-8} textAnchor="middle"
              fontSize="10" fill="var(--t3)" fontFamily="monospace">
              {eqData[idx].date.slice(5)}
            </text>
          ))}

          {/* Area fill */}
          <path clipPath="url(#ecClip2)" d={areaD} fill="url(#ecFill2)"/>

          {/* Main line */}
          <path clipPath="url(#ecClip2)" d={pathD} fill="none"
            stroke={color} strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"/>

          {/* Hover */}
          {hover !== null && (
            <>
              <line x1={toX(hover)} y1={PAD.t} x2={toX(hover)} y2={PAD.t+iH}
                stroke="var(--b2)" strokeWidth="1" strokeDasharray="3,3"/>
              <circle cx={toX(hover)} cy={toY(eqData[hover].v)} r="5"
                fill="white" stroke={color} strokeWidth="2.5"/>
            </>
          )}

          {/* Hover zones */}
          {eqData.map((_, i) => (
            <rect key={i} x={toX(i)-8} y={PAD.t} width={16} height={iH}
              fill="transparent" style={{cursor:'crosshair'}}
              onMouseEnter={() => setHover(i)}
              onMouseLeave={() => setHover(null)}
            />
          ))}
        </svg>

        {/* Tooltip */}
        {hover !== null && (() => {
          const d = eqData[hover];
          const goLeft = hover > eqData.length * 0.6;
          return (
            <div style={{
              position:'absolute', top:16,
              ...(goLeft ? {right:'3%'} : {left:'10%'}),
              background:'var(--s1)', border:'1px solid var(--b2)',
              borderRadius:8, padding:'8px 12px', fontSize:11,
              pointerEvents:'none', zIndex:10, minWidth:150,
              boxShadow:'0 4px 16px rgba(0,0,0,0.12)'
            }}>
              <div style={{fontFamily:'monospace', fontWeight:700, fontSize:14,
                color: d.pnl >= 0 ? '#059669' : '#dc2626', marginBottom:4}}>
                {d.pnl >= 0 ? '+' : ''}{d.pnl.toFixed(2)}$
              </div>
              <div style={{fontFamily:'monospace', color:'var(--t2)', fontSize:12, marginBottom:2}}>
                Acum: {d.v >= 0 ? '+' : ''}{d.v.toFixed(2)}$
              </div>
              <div style={{color:'var(--t4)', fontSize:10}}>{d.pair} · {d.date}</div>
            </div>
          );
        })()}
      </div>
    );
  };

  return (
    <div>
      {/* SELECTOR DE CUENTAS — tarjetas clicables */}
      <div className="fl g2 mb3" style={{flexWrap:'wrap'}}>
        <div
          onClick={() => setSelectedAccount('ALL')}
          style={{cursor:'pointer', padding:'10px 14px', borderRadius:10, border: selectedAccount==='ALL' ? '1.5px solid var(--ac)' : '1px solid var(--b1)', background: selectedAccount==='ALL' ? 'var(--ac-glow)' : 'var(--s1)', minWidth:110, transition:'all .15s'}}
        >
          <div style={{fontSize:9,color:'var(--t4)',fontWeight:600,letterSpacing:1,textTransform:'uppercase',marginBottom:4}}>TODAS</div>
          <div style={{fontWeight:700,fontSize:13}}>{accounts.length} cuentas</div>
          <div style={{fontSize:11,color:col(trades.filter(t=>t.status!=='open').reduce((s,t)=>s+(t.pnl||0),0)),fontFamily:'var(--mono)',marginTop:2}}>{fmtN(trades.filter(t=>t.status!=='open').reduce((s,t)=>s+(t.pnl||0),0))}</div>
        </div>
        {accounts.map(a => {
          const aT = trades.filter(t => String(t.account_id)===String(a.id) && t.status!=='open');
          const aPnl = aT.reduce((s,t) => s+(t.pnl||0), 0);
          const isSel = String(selectedAccount)===String(a.id);
          return (
            <div key={a.id} onClick={() => setSelectedAccount(String(a.id))}
              style={{cursor:'pointer', padding:'10px 14px', borderRadius:10, border: isSel ? '1.5px solid var(--ac)' : '1px solid var(--b1)', background: isSel ? 'var(--ac-glow)' : 'var(--s1)', minWidth:130, transition:'all .15s'}}
            >
              <div className="fl aic jb" style={{marginBottom:4}}>
                <span style={{fontSize:9,color:'var(--t4)',fontWeight:600,letterSpacing:1,textTransform:'uppercase'}}>{a.broker||a.platform}</span>
                <span className={`tag ${a.connected?'tg':'tr'}`} style={{fontSize:8,padding:'1px 5px'}}>{a.connected?'● LIVE':'OFF'}</span>
              </div>
              <div style={{fontWeight:700,fontSize:13,marginBottom:2}}>{a.name}</div>
              <div style={{fontSize:11,color:'var(--t3)',fontFamily:'var(--mono)'}}>${(a.balance||0).toLocaleString()}</div>
              <div style={{fontSize:11,color:col(aPnl),fontFamily:'var(--mono)',marginTop:2}}>{fmtN(aPnl)}</div>
            </div>
          );
        })}
      </div>

      {/* PANEL DE STATS — solo cuando hay cuenta seleccionada */}
      {selAcc && (
        <div className="card mb4">
          <div className="fl aic jb mb3">
            <div>
              <div style={{fontWeight:700,fontSize:15}}>{selAcc.name}</div>
              <div style={{fontSize:11,color:'var(--t3)',fontFamily:'var(--mono)',marginTop:2}}>{selAcc.broker} · {selAcc.platform} · {selAcc.login}</div>
            </div>
            <span className={`tag ${selAcc.connected?'tg':'tr'}`}>{selAcc.connected?'● LIVE':'OFFLINE'}</span>
          </div>
          {eqData.length >= 2 && (
            <div style={{marginBottom:14}}>
              <div style={{fontSize:9,color:'var(--t4)',fontWeight:600,letterSpacing:1,textTransform:'uppercase',marginBottom:6}}>Curva de Equidad</div>
              <EquityChart />
            </div>
          )}
          <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:8}}>
            {[
              {l:'Balance', v:`$${(selAcc.balance||0).toLocaleString('es-ES',{minimumFractionDigits:2})}`, c:'var(--t1)'},
              {l:'PnL Total', v:fmtN(selPnl), c:col(selPnl)},
              {l:'PnL Hoy', v:fmtN(selPnlHoy), c:col(selPnlHoy)},
              {l:'Win Rate', v:`${selWr}%`, c:parseFloat(selWr)>=50?'var(--green)':'var(--red)'},
              {l:'Profit Factor', v:selPF, c:parseFloat(selPF)>=1?'var(--green)':'var(--red)'},
              {l:'Ganancia prom.', v:fmtN(selAvgWin), c:'var(--green)'},
              {l:'Pérdida prom.', v:fmtN(selAvgLoss), c:'var(--red)'},
              {l:'Total trades', v:selT.length, c:'var(--t1)'},
              {l:'Gan. consecutivos', v:maxWinStreak, c:'var(--green)'},
              {l:'Perd. consecutivos', v:maxLossStreak, c:'var(--red)'},
            ].map(s => (
              <div key={s.l} style={{background:'var(--s2)',borderRadius:8,padding:'8px 10px',border:'1px solid var(--b1)'}}>
                <div style={{fontSize:9,color:'var(--t4)',fontWeight:600,textTransform:'uppercase',letterSpacing:.5,marginBottom:4}}>{s.l}</div>
                <div style={{fontFamily:'var(--mono)',fontSize:13,fontWeight:700,color:s.c}}>{s.v}</div>
              </div>
            ))}
          </div>
        </div>
      )}
      <div className="card mb4" style={{ padding: '12px 14px' }}>
        <div className="fl aic jb mb3">
          <div className="fl aic g2">
            <button onClick={() => setCalMonth(p => { const d = new Date(p.y, p.m - 1); return { y: d.getFullYear(), m: d.getMonth() }; })} style={{ background: 'var(--s2)', border: '1px solid var(--b1)', borderRadius: 6, cursor: 'pointer', padding: '2px 10px', fontSize: 15, color: 'var(--t2)' }}>&#8249;</button>
            <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)', minWidth: 140, textAlign: 'center' }}>{MESES[calMonth.m]} {calMonth.y}</span>
            <button onClick={() => setCalMonth(p => { const d = new Date(p.y, p.m + 1); return { y: d.getFullYear(), m: d.getMonth() }; })} style={{ background: 'var(--s2)', border: '1px solid var(--b1)', borderRadius: 6, cursor: 'pointer', padding: '2px 10px', fontSize: 15, color: 'var(--t2)' }}>&#8250;</button>
          </div>
          <button className="chip" onClick={() => setShowCalendar(p => !p)} style={{ fontSize: 11 }}>{showCalendar ? 'Ocultar' : 'Mostrar'}</button>
        </div>
        {showCalendar && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr) 60px', gap: 3, marginBottom: 4 }}>
              {DIAS_SEMANA.map(d => <div key={d} style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--t3)', padding: '2px 0' }}>{d}</div>)}
              <div style={{ textAlign: 'center', fontSize: 10, fontWeight: 600, color: 'var(--ac)', padding: '2px 0' }}>Sem</div>
            </div>
            {(() => {
              const totalRows = Math.ceil((calDays.firstDow + calDays.daysInMonth) / 7);
              return Array.from({ length: totalRows }).map((_, row) => {
                const weekDays = Array.from({ length: 7 }).map((_, col) => {
                  const day = row * 7 + col - calDays.firstDow + 1;
                  return day >= 1 && day <= calDays.daysInMonth ? day : null;
                });
                const weekPnl = weekDays.reduce((s, d) => d && calDays.days[d] !== undefined ? s + calDays.days[d] : s, 0);
                const weekHasData = weekDays.some(d => d && calDays.days[d] !== undefined);
                return (
                  <div key={row} style={{ display: 'grid', gridTemplateColumns: 'repeat(7,1fr) 60px', gap: 3, marginBottom: 3 }}>
                    {weekDays.map((day, col) => {
                      if (!day) return <div key={'e'+row+col} />;
                      const pnl = calDays.days[day];
                      const hasData = pnl !== undefined;
                      const isToday = new Date().getDate() === day && new Date().getMonth() === calMonth.m && new Date().getFullYear() === calMonth.y;
                      return (
                        <div key={day} style={{ borderRadius: 6, padding: '5px 3px', textAlign: 'center', background: hasData ? (pnl >= 0 ? 'var(--green-bg)' : 'var(--red-bg)') : 'var(--s2)', border: isToday ? '2px solid var(--ac)' : '1px solid var(--b1)', minHeight: 46, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                          <div style={{ fontSize: 10, color: 'var(--t3)', fontWeight: isToday ? 700 : 400 }}>{day}</div>
                          {hasData && <div style={{ fontSize: 10, fontWeight: 700, fontFamily: 'var(--mono)', color: pnl >= 0 ? 'var(--green)' : 'var(--red)', marginTop: 2 }}>{pnl >= 0 ? '+' : ''}{pnl.toFixed(0)}</div>}
                        </div>
                      );
                    })}
                    <div style={{ borderRadius: 6, padding: '5px 3px', textAlign: 'center', background: weekHasData ? (weekPnl >= 0 ? 'rgba(5,150,105,0.15)' : 'rgba(220,38,38,0.15)') : 'var(--s3)', border: '1px solid var(--b1)', minHeight: 46, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                      {weekHasData && <><div style={{ fontSize: 9, color: 'var(--t3)', marginBottom: 2 }}>sem</div><div style={{ fontSize: 11, fontWeight: 700, fontFamily: 'var(--mono)', color: weekPnl >= 0 ? 'var(--green)' : 'var(--red)' }}>{weekPnl >= 0 ? '+' : ''}{weekPnl.toFixed(0)}</div></>}
                    </div>
                  </div>
                );
              });
            })()}
            {(() => {
              const vals = Object.values(calDays.days);
              if (!vals.length) return <div style={{ fontSize: 11, color: 'var(--t4)', textAlign: 'center', marginTop: 10 }}>Sin trades este mes</div>;
              const total = vals.reduce((a, b) => a + b, 0);
              const dias = vals.length;
              const wins = vals.filter(v => v > 0).length;
              return (
                <div className="fl jb mt3" style={{ flexWrap: 'wrap', gap: 8, paddingTop: 10, borderTop: '1px solid var(--b1)' }}>
                  <div style={{ textAlign: 'center' }}><div style={{ fontSize: 10, color: 'var(--t3)' }}>Total mes</div><div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--mono)', color: total >= 0 ? 'var(--green)' : 'var(--red)' }}>{total >= 0 ? '+' : ''}{total.toFixed(2)}</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ fontSize: 10, color: 'var(--t3)' }}>Dias activos</div><div style={{ fontSize: 13, fontWeight: 700 }}>{dias}</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ fontSize: 10, color: 'var(--t3)' }}>Dias ganadores</div><div style={{ fontSize: 13, fontWeight: 700, color: 'var(--green)' }}>{wins}/{dias}</div></div>
                  <div style={{ textAlign: 'center' }}><div style={{ fontSize: 10, color: 'var(--t3)' }}>Media/dia</div><div style={{ fontSize: 13, fontWeight: 700, fontFamily: 'var(--mono)', color: (total/dias) >= 0 ? 'var(--green)' : 'var(--red)' }}>{(total/dias).toFixed(2)}</div></div>
                </div>
              );
            })()}
          </div>
        )}
      </div>
      <div className="card mb4" style={{ padding: '12px 14px' }}>
        <div className="fl aic jb" style={{ flexWrap: 'wrap', gap: 10 }}>
          {/* Left: status + period filters */}
          <div className="fl g2" style={{ flexWrap: 'wrap' }}>
            {['ALL', 'WIN', 'LOSS', 'BE'].map(f => <button key={f} className={`chip ${filter === f ? 'on' : ''}`} onClick={() => setFilter(f)}>{f}</button>)}
            <div style={{ width: 1, background: 'var(--b1)', margin: '0 4px' }} />
            {['ALL', 'HOY', 'SEMANA', 'MES'].map(p => <button key={p} className={`chip ${periodFilter === p ? 'on' : ''}`} onClick={() => { setPeriodFilter(p); setDateFrom(''); setDateTo(''); }}>{p}</button>)}
          </div>
          {/* Right: action buttons */}
          <div className="fl g2">
            <button className="btn bg btn-sm" onClick={() => setShowSetupEditor(true)}>⚙️ Setups</button>
            <button className="btn bg btn-sm" onClick={exportCSV} disabled={!filtered.length}>⬇️ CSV</button>
            <button className="btn bp btn-sm" onClick={() => setShowAdd(true)}>✏️ Añadir Trade</button>
          </div>
        </div>
        {/* Custom date range */}
        <div className="fl aic g2 mt3" style={{ flexWrap: 'wrap' }}>
          <span style={{ fontSize: 11, color: 'var(--t3)', fontWeight: 500 }}>Rango:</span>
          <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPeriodFilter('ALL'); }} style={{ width: 140, padding: '4px 8px', fontSize: 11 }} />
          <span style={{ fontSize: 11, color: 'var(--t3)' }}>→</span>
          <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPeriodFilter('ALL'); }} style={{ width: 140, padding: '4px 8px', fontSize: 11 }} />
          {(dateFrom || dateTo || periodFilter !== 'ALL' || filter !== 'ALL') && (
            <button className="chip" onClick={() => { setDateFrom(''); setDateTo(''); setPeriodFilter('ALL'); setFilter('ALL'); }} style={{ color: 'var(--red)', borderColor: 'rgba(220,38,38,0.2)' }}>✕ Limpiar</button>
          )}
          {filtered.length < trades.length && (
            <span style={{ fontSize: 11, color: 'var(--t3)', fontFamily: 'var(--mono)', marginLeft: 8 }}>
              {filtered.length} trade{filtered.length !== 1 ? 's' : ''} · {fmtN(filteredPnl)} · {filtered.length ? (filteredWins / filtered.length * 100).toFixed(0) : 0}% WR
            </span>
          )}
        </div>
      </div>

      {topSetup && <div className="alert al-g mb4" style={{ fontSize: 11 }}>🏆 Mejor setup: <b>{topSetup[0]}</b> · {fmtN(topSetup[1])} acumulado</div>}

      {filtered.map(t => (
        <div key={t.id} className={`je ${t.status.toLowerCase()}`} onClick={(e) => { setExpandId(expandId === t.id ? null : t.id); }}>
          <div className="je-head">
            <div>
              <div className="je-pair">{t.pair} <span style={{ fontSize: 13, fontWeight: 400, color: 'var(--t2)' }}>· {t.lots}L · {t.type}</span></div>
              <div style={{ fontSize: 11, color: 'var(--t3)', fontFamily: 'var(--mono)', marginTop: 2 }}>{(t.created_at || t.date || '').slice(0,16).replace('T',' ')} · {t.session}</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className="je-pnl" style={{ color: col(t.pnl) }}>{fmtN(t.pnl)}</div>
              {t.rr && <div style={{ fontSize: 11, color: 'var(--ac)', fontFamily: 'var(--mono)', fontWeight: 600 }}>{t.rr}</div>}
            </div>
          </div>
          <div className="je-meta">
            <span className={`tag ${t.status === 'WIN' ? 'tg' : t.status === 'LOSS' ? 'tr' : 'ty'}`}>{t.status}</span>
            <span className="tag tp">{t.setup}</span>
            <span className="tag tb">{t.session}</span>
            <span style={{ fontSize: 12 }}>{EMOTIONS.find(e => e.k === t.emotion)?.e} <span style={{ fontSize: 10, color: 'var(--t3)', fontFamily: 'var(--mono)' }}>{t.emotion}</span></span>
          </div>
          {expandId === t.id && (
            <div>
              {t.note && <div className="je-note">{t.note}</div>}
              {t.images?.length > 0 && (
                <div className="je-imgs">
                  {t.images.map(img => (
                    <div key={img.id} className="je-img" onMouseDown={e=>{e.stopPropagation();}} onClick={e=>{e.stopPropagation();setLightbox(img.src);}}><img src={img.src} alt="" /></div>
                  ))}
                </div>
              )}
              {!t.note && !t.images?.length && <div style={{ fontSize: 11, color: 'var(--t4)', fontFamily: 'var(--mono)' }}>Sin notas ni capturas.</div>}
              <div className="fl g2" style={{marginTop:10}}>
                <button onMouseDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation(); setEditTrade(t);}} style={{background:'var(--ac-glow)',border:'1px solid rgba(37,99,235,0.2)',color:'var(--ac)',borderRadius:6,cursor:'pointer',padding:'4px 12px',fontSize:10,fontFamily:'var(--mono)'}}>✏️ Editar</button>
                <button onMouseDown={e=>e.stopPropagation()} onClick={e=>{e.stopPropagation();if(window.confirm('¿Borrar este trade?')){supabase.from('trades').delete().eq('id',t.id).then(()=>setTrades(p=>p.filter(x=>x.id!==t.id)));} }} style={{background:'var(--red-bg)',border:'1px solid rgba(220,38,38,0.2)',color:'var(--red)',borderRadius:6,cursor:'pointer',padding:'4px 12px',fontSize:10,fontFamily:'var(--mono)'}}>🗑 Borrar</button>
              </div>
            </div>
          )}
        </div>
      ))}

      {lightbox && (
        <div className="lightbox-ov" onClick={()=>setLightbox(null)}>
          <button className="lightbox-close" onClick={()=>setLightbox(null)}>✕</button>
          <img className="lightbox-img" src={lightbox} alt="Captura" />
        </div>
      )}

      {editTrade && (
        <EditModal
          trade={editTrade}
          accounts={accounts}
          customSetups={customSetups}
          onClose={() => setEditTrade(null)}
          onSave={(updated) => {
            setTrades(p => p.map(x => x.id === updated.id ? updated : x));
            setEditTrade(null);
          }}
        />
      )}
      {showSetupEditor && (
        <div className="ov" onClick={() => setShowSetupEditor(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="mc" onClick={() => setShowSetupEditor(false)}>✕</button>
            <div className="mt">⚙️ Gestionar Setups</div>
            <div className="ms">{selectedAccount === 'ALL' ? 'Selecciona una cuenta para personalizar sus setups' : `Setups de: ${accounts.find(a=>String(a.id)===String(selectedAccount))?.name || ''}`}</div>
            {selectedAccount === 'ALL' ? (
              <div className="alert al-y" style={{fontSize:12,margin:'12px 0'}}><span>⚠️</span><span>Selecciona una cuenta específica en el filtro para poder editar sus setups</span></div>
            ) : (
              <>
                <div className="mb4">
                  {customSetups.map((s, i) => (
                    <div key={i} className="fl aic jb" style={{padding:'8px 10px',background:'var(--s2)',borderRadius:8,marginBottom:6,border:'1px solid var(--b1)'}}>
                      <span className="fm" style={{fontSize:12,color:'var(--t1)'}}>{s}</span>
                      <button onClick={() => setCustomSetups(p => p.filter((_, idx) => idx !== i))} style={{background:'rgba(255,69,96,.1)',border:'1px solid rgba(255,69,96,.2)',color:'var(--red)',borderRadius:6,cursor:'pointer',padding:'2px 8px',fontSize:10,fontFamily:'var(--mono)'}}>✕</button>
                    </div>
                  ))}
                </div>
                <div className="fl g2 mb3">
                  <input placeholder="Nuevo setup..." value={newSetupName} onChange={e => setNewSetupName(e.target.value)} onKeyDown={e => { if(e.key==='Enter' && newSetupName.trim()) { setCustomSetups(p => [...p, newSetupName.trim()]); setNewSetupName(''); }}} style={{flex:1}} />
                  <button className="btn bp btn-sm" onClick={() => { if(newSetupName.trim()) { setCustomSetups(p => [...p, newSetupName.trim()]); setNewSetupName(''); }}}>+ Añadir</button>
                </div>
                <button className="btn bp w100" disabled={setupsSaving} onClick={async () => { await saveSetups(customSetups); setShowSetupEditor(false); }}>{setupsSaving ? '⏳ Guardando...' : 'Guardar'}</button>
              </>
            )}
          </div>
        </div>
      )}
      {showAdd && (
        <div className="ov" onClick={() => setShowAdd(false)}>
          <div className="modal modal-lg" onClick={e => e.stopPropagation()}>
            <button className="mc" onClick={() => setShowAdd(false)}>✕</button>
            <div className="mt">✏️ Registrar Trade</div>
            <div className="ms">Documenta cada operación para mejorar tu consistencia</div>

            <div className="fr mb3">
              <div className="fg" style={{ marginBottom: 0 }}><label>Cuenta</label>
                <select value={nt.accId} onChange={e => setNt(p => ({ ...p, accId: +e.target.value }))}>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                </select>
              </div>
              <div className="fg" style={{ marginBottom: 0 }}><label>Par</label>
                <input value={nt.pair} onChange={e => setNt(p => ({ ...p, pair: e.target.value.toUpperCase() }))} />
              </div>
            </div>
            <div className="fr3 mb3">
              <div className="fg" style={{ marginBottom: 0 }}><label>Dirección</label>
                <select value={nt.type} onChange={e => setNt(p => ({ ...p, type: e.target.value }))}>
                  <option>BUY</option><option>SELL</option>
                </select>
              </div>
              <div className="fg" style={{ marginBottom: 0 }}><label>Precio entrada</label>
                <input placeholder="1.08512" value={nt.open} onChange={e => setNt(p => ({ ...p, open: e.target.value }))} />
              </div>
              <div className="fg" style={{ marginBottom: 0 }}><label>Precio cierre</label>
                <input placeholder="1.08201" value={nt.close} onChange={e => setNt(p => ({ ...p, close: e.target.value }))} />
              </div>
            </div>
            <div className="fr3 mb3">
              <div className="fg" style={{ marginBottom: 0 }}><label>Lotes</label>
                <input type="number" step="0.01" value={nt.lots} onChange={e => setNt(p => ({ ...p, lots: +e.target.value }))} />
              </div>
              <div className="fg" style={{ marginBottom: 0 }}><label>PnL ($)</label>
                <input type="number" step="0.01" value={nt.pnl} onChange={e => setNt(p => ({ ...p, pnl: e.target.value }))} />
              </div>
              <div className="fg" style={{ marginBottom: 0 }}><label>R:R</label>
                <input value={nt.rr} onChange={e => setNt(p => ({ ...p, rr: e.target.value }))} placeholder="1:2" />
              </div>
            </div>
            <div className="fr mb3">
              <div className="fg" style={{ marginBottom: 0 }}><label>Setup</label>
                <select value={nt.setup} onChange={e => setNt(p => ({ ...p, setup: e.target.value }))}>
                  {customSetups.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div className="fg" style={{ marginBottom: 0 }}><label>Sesión</label>
                <select value={nt.session} onChange={e => setNt(p => ({ ...p, session: e.target.value }))}>
                  {SESSIONS.map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="fg mb3">
              <label>Estado emocional al operar</label>
              <div className="emo-row">
                {EMOTIONS.map(em => (
                  <button key={em.k} className={`emo-btn ${nt.emotion === em.k ? 'sel' : ''}`} onClick={() => setNt(p => ({ ...p, emotion: em.k }))}>
                    <span className="ei">{em.e}</span>{em.l}
                  </button>
                ))}
              </div>
            </div>
            <div className="fg mb3">
              <label>Resultado</label>
              <div className="fl g2">
                {['WIN', 'LOSS', 'BE'].map(s => (
                  <button key={s} className={`btn ${s === 'WIN' ? 'bs' : s === 'LOSS' ? 'br' : 'by'} ${nt.status === s ? '' : 'bg'}`} style={{ flex: 1 }} onClick={() => setNt(p => ({ ...p, status: s }))}>{s}</button>
                ))}
              </div>
            </div>
            <div className="fg mb3">
              <label>Notas / Razonamiento</label>
              <textarea placeholder="¿Por qué tomaste este trade? ¿Seguiste el plan? ¿Qué aprendiste?" value={nt.note} onChange={e => setNt(p => ({ ...p, note: e.target.value }))} />
            </div>
            <div className="fg mb4">
              <ImageUploader images={images} onChange={setImages} />
            </div>
            {saveError && (
              <div className="alert al-r mb3" style={{ fontSize: 11, wordBreak: 'break-all' }}>
                <span>❌</span><span>{saveError}</span>
              </div>
            )}
            <div className="fl g2">
              <button className="btn bp w100" onClick={save} disabled={saving}>
                {saving ? '⏳ Guardando...' : 'Guardar Trade'}
              </button>
              <button className="btn bg" onClick={() => setShowAdd(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function AccountsView({ accounts, setAccounts, session }) {
  const [showAdd, setShowAdd] = useState(false);
  const [editRules, setEditRules] = useState(null);
  const [showLockWall, setShowLockWall] = useState(null);
  const [na, setNa] = useState({ name: '', broker: 'FTMO', platform: 'MT5', login: '', server: '', maxDD: 10, maxDailyDD: 5, maxTrades: 5, maxLoss: 2, maxConsec: 3 });

  const BROKERS = ['FTMO', 'The5ers', 'Funded Next', 'MyForexFunds', 'True Forex Funds', 'Apex', 'IC Markets', 'Pepperstone', 'Custom'];
  const PLATFORMS = ['MT5', 'Match Trader', 'cTrader', 'DXtrade'];

  const lockNow = async (id, reason) => {
    const midnight = new Date(); midnight.setHours(24, 0, 0, 0);
    const lockUntil = midnight.getTime();
    await supabase.from('accounts').update({ locked: true, lock_reason: reason, lock_until: lockUntil }).eq('id', id);
    setAccounts(p => p.map(a => a.id === id ? { ...a, locked: true, lockReason: reason, lockUntil: lockUntil } : a));
  };

  // Auto-unlock at midnight
  useEffect(() => {
    const check = () => {
      const now = Date.now();
      setAccounts(p => p.map(a => {
        if (a.locked && a.lockUntil && now >= a.lockUntil) return { ...a, locked: false, lockReason: '', lockUntil: null };
        return a;
      }));
    };
    const t = setInterval(check, 10000);
    return () => clearInterval(t);
  }, []);

  const PLATFORM_DOCS = {
    'MT5': { badge: 'pb-mt5', support: 'Bloqueo nativo via EA MQL5. Cierra posiciones y bloquea nuevas órdenes automáticamente.' },
    'Match Trader': { badge: 'pb-mt', support: '🔜 Próximamente — En desarrollo. La integración requiere partnership con Match-Trade Technologies.' },
    'cTrader': { badge: 'pb-ct', support: '🔜 Próximamente — cBot en C# en desarrollo. Disponible en la próxima versión.' },
    'DXtrade': { badge: 'pb-dx', support: '🔜 Próximamente — Integración via API REST v2 en desarrollo.' },
  };

  return (
    <div>
      {showLockWall && (
        <LockWall account={showLockWall} onDismiss={() => setShowLockWall(null)} />
      )}

      <div className="sh mb4">
        <span className="st">Cuentas Conectadas</span>
        <button className="btn bp btn-sm" onClick={() => setShowAdd(true)}>➕ Añadir Cuenta</button>
      </div>

      <div className="g2 mb4">
        {accounts.map(a => (
          <div key={a.id} className={`ac-card ${a.locked ? 'ac-locked' : ''}`}>
            {a.locked && <div className="ac-locked-bar">🔒 BLOQUEADA HASTA MEDIANOCHE</div>}
            <div className="fl aic jb mb2">
              <div>
                <div className="ac-name">{a.name}</div>
                <div className="ac-meta">
                  <span className={`platform-badge ${PLATFORM_DOCS[a.platform]?.badge}`}>{a.platform}</span>
                  {a.broker} · <span style={{ color: 'var(--t4)' }}>{a.login}</span>
                </div>
              </div>
              <span className={`tag ${a.connected ? 'tg' : 'tr'}`}>{a.connected ? '● LIVE' : 'OFFLINE'}</span>
            </div>
            <div className="ac-stats mb3">
              {[
                { l: 'Balance', v: `$${(a.balance || 0).toLocaleString()}`, c: 'var(--t1)' },
                { l: 'Equidad', v: `$${(a.equity || 0).toLocaleString()}`, c: 'var(--t1)' },
                { l: 'Fase', v: a.phase, c: 'var(--purple)' },
                { l: 'DD Diario', v: fmtP(a.dailyDD), c: a.dailyDD > a.maxDailyDD * .7 ? 'var(--red)' : 'var(--green)' },
                { l: 'DD Total', v: fmtP(a.dd), c: a.dd > a.maxDD * .7 ? 'var(--red)' : 'var(--green)' },
                { l: 'Trades hoy', v: `${a.todayTrades}/${a.rules.maxTrades}`, c: 'var(--ac)' },
              ].map(s => <div key={s.l}><div className="ac-stat-l">{s.l}</div><div className="ac-stat-v" style={{ color: s.c }}>{s.v}</div></div>)}
            </div>

            <div className="mb3">
              <div className="fl aic jb mb1" style={{ fontSize: 10 }}><span className="fm" style={{ color: 'var(--t3)' }}>DD Diario {fmtP(a.dailyDD)} / {fmtP(a.maxDailyDD)}</span></div>
              <div className="prog"><div className="pfill" style={{ width: `${Math.min((a.dailyDD / a.maxDailyDD) * 100, 100)}%`, background: a.dailyDD > a.maxDailyDD * .7 ? 'var(--red)' : 'var(--green)' }} /></div>
            </div>

            {/* EA Token */}
            <div style={{ background: 'var(--s2)', border: '1px solid var(--b1)', borderRadius: 8, padding: '8px 12px', marginBottom: 10 }}>
              <div style={{ fontSize: 10, color: 'var(--t4)', fontWeight: 600, letterSpacing: 1, textTransform: 'uppercase', marginBottom: 4 }}>Token EA</div>
              <div className="fl aic jb">
                <span style={{ fontFamily: 'var(--mono)', fontSize: 11, color: 'var(--t2)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }}>
                  {a.ea_token || 'Sin token — genera uno'}
                </span>
                <button
                  onClick={async () => {
                    if (!a.ea_token) {
                      const token = Math.random().toString(36).substring(2) + Math.random().toString(36).substring(2);
                      await supabase.from('accounts').update({ ea_token: token }).eq('id', a.id);
                      setAccounts(p => p.map(x => x.id === a.id ? { ...x, ea_token: token } : x));
                    } else {
                      navigator.clipboard.writeText(a.ea_token);
                    }
                  }}
                  style={{ background: 'var(--ac-glow)', border: '1px solid rgba(5,150,105,0.2)', color: 'var(--ac)', borderRadius: 6, cursor: 'pointer', padding: '3px 10px', fontSize: 10, fontFamily: 'var(--mono)', flexShrink: 0, marginLeft: 8 }}
                >
                  {a.ea_token ? '📋 Copiar' : '⚡ Generar'}
                </button>
              </div>
            </div>

            {a.locked ? (
              <button className="btn br btn-sm w100" onClick={() => setShowLockWall(a)}>🔒 Ver estado del bloqueo</button>
            ) : (
              <div className="fl g2" style={{ flexWrap: 'wrap' }}>
                <button className="btn br btn-sm" onClick={() => lockNow(a.id, `Bloqueo manual activado por el trader`)}>🔒 Bloquear ahora</button>
                <button className="btn bg btn-sm" onClick={() => setEditRules({ ...a })}>⚙️ Reglas</button>
                <button className="btn br btn-sm" onClick={async () => {
                  if (!window.confirm(`¿Eliminar "${a.name}"? Se borrarán todos sus datos. Esta acción no se puede deshacer.`)) return;
                  await supabase.from('trades').delete().eq('account_id', a.id);
                  await supabase.from('accounts').delete().eq('id', a.id);
                  setAccounts(p => p.filter(x => x.id !== a.id));
                }}>🗑️ Eliminar</button>
              </div>
            )}
          </div>
        ))}
      </div>

      {/* PLATFORM COMPATIBILITY */}
      <div className="card mb4">
        <div className="sh mb3"><span className="st">🔌 Compatibilidad de Plataformas</span></div>
        <div className="g2">
          {Object.entries(PLATFORM_DOCS).map(([name, info]) => (
            <div key={name} style={{ background: 'var(--s2)', border: '1px solid var(--b1)', borderRadius: 10, padding: '12px 14px' }}>
              <div className="fl aic g2 mb2">
                <span className={`platform-badge ${info.badge}`}>{name}</span>
                <span className="tag tg">✓ Soportado</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.5 }}>{info.support}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ADD ACCOUNT MODAL */}
      {showAdd && (
        <div className="ov" onClick={() => setShowAdd(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="mc" onClick={() => setShowAdd(false)}>✕</button>
            <div className="mt">➕ Conectar Cuenta</div>
            <div className="ms">Vincula tu cuenta de fondeo al journal</div>
            <div className="fr mb3">
              <div className="fg" style={{ marginBottom: 0 }}><label>Nombre</label><input placeholder="FTMO Challenge #1" value={na.name} onChange={e => setNa(p => ({ ...p, name: e.target.value }))} /></div>
              <div className="fg" style={{ marginBottom: 0 }}><label>Broker</label><select value={na.broker} onChange={e => setNa(p => ({ ...p, broker: e.target.value }))}>{BROKERS.map(b => <option key={b}>{b}</option>)}</select></div>
            </div>
            <div className="fr mb3">
              <div className="fg" style={{ marginBottom: 0 }}><label>Plataforma</label><select value={na.platform} onChange={e => setNa(p => ({ ...p, platform: e.target.value }))}>{PLATFORMS.map(b => <option key={b}>{b}</option>)}</select></div>
              <div className="fg" style={{ marginBottom: 0 }}><label>Login</label><input placeholder="123456" value={na.login} onChange={e => setNa(p => ({ ...p, login: e.target.value }))} /></div>
            </div>
            <div className="fg mb3"><label>Servidor</label><input placeholder="FTMO-Server2" value={na.server} onChange={e => setNa(p => ({ ...p, server: e.target.value }))} /></div>
            <div className="alert al-b mb3" style={{ fontSize: 11 }}>🔐 Solo necesitas la contraseña de <b>inversor</b> (lectura). Nunca la de trader.</div>
            <div className="fr mb3">
              <div className="fg" style={{ marginBottom: 0 }}><label>Max DD Diario (%)</label><input type="number" value={na.maxDailyDD} onChange={e => setNa(p => ({ ...p, maxDailyDD: +e.target.value }))} /></div>
              <div className="fg" style={{ marginBottom: 0 }}><label>Max DD Total (%)</label><input type="number" value={na.maxDD} onChange={e => setNa(p => ({ ...p, maxDD: +e.target.value }))} /></div>
            </div>
            <div className="fr mb4">
              <div className="fg" style={{ marginBottom: 0 }}><label>Trades máx/día</label><input type="number" value={na.maxTrades} onChange={e => setNa(p => ({ ...p, maxTrades: +e.target.value }))} /></div>
              <div className="fg" style={{ marginBottom: 0 }}><label>Pérdidas consec. máx</label><input type="number" value={na.maxConsec} onChange={e => setNa(p => ({ ...p, maxConsec: +e.target.value }))} /></div>
            </div>
            <div className="fl g2">
              <button className="btn bp w100" onClick={async () => {
                const newAcc = {
                  user_id: session.user.id,
                  name: na.name,
                  broker: na.broker,
                  platform: na.platform,
                  login: na.login,
                  server: na.server,
                  max_dd: na.maxDD,
                  max_daily_dd: na.maxDailyDD,
                  max_trades: na.maxTrades,
                  rules: { maxTrades: na.maxTrades, maxLoss: na.maxLoss || 2, maxConsec: na.maxConsec, cooldown: 15 },
                };
                const { data, error } = await supabase.from('accounts').insert([newAcc]).select().single();
                if (!error && data) {
                  setAccounts(p => [...p, { ...data, todayTrades: 0, todayLoss: 0, dailyDD: 0, maxDD: data.max_dd, maxDailyDD: data.max_daily_dd, lockReason: data.lock_reason || '', lockUntil: data.lock_until, rules: data.rules || { maxTrades: 5, maxLoss: 2, maxConsec: 3, cooldown: 15 }, custom_setups: null }]);
                } else {
                  alert('Error al guardar: ' + (error?.message || 'desconocido'));
                }
                setShowAdd(false);
              }}>Conectar</button>
              <button className="btn bg" onClick={() => setShowAdd(false)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}

      {/* EDIT RULES MODAL */}
      {editRules && (
        <div className="ov" onClick={() => setEditRules(null)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <button className="mc" onClick={() => setEditRules(null)}>✕</button>
            <div className="mt">⚙️ Reglas de Bloqueo</div>
            <div className="ms">{editRules.name} — estas reglas se aplican al EA y al journal</div>
            <div className="alert al-r mb3" style={{ fontSize: 11 }}>
              <span>⚠️</span><span>Una vez la cuenta se bloquea, <b>no se puede desbloquear hasta medianoche</b>. Configura con cuidado.</span>
            </div>
            {[
              { k: 'maxTrades', src: 'rules', type: 'num', l: 'Máx trades por día', d: 'El EA cierra posiciones y bloquea al alcanzar este número', ico: '🔢' },
              { k: 'maxLoss', src: 'rules', type: 'num', l: 'Pérdida diaria máx (%)', d: 'Bloqueo inmediato cuando la pérdida del día supera este %', ico: '📉' },
              { k: 'maxConsec', src: 'rules', type: 'num', l: 'Pérdidas consecutivas máx', d: 'Bloquea tras X pérdidas seguidas — el anti-revenge definitivo', ico: '🔄' },
              { k: 'cooldown', src: 'rules', type: 'num', l: 'Cooldown entre trades (min)', d: 'Pausa obligatoria. El EA no abre nuevas posiciones en este tiempo', ico: '⏱️' },
              { k: 'max_daily_profit', src: 'col', type: 'num', l: 'Profit diario máx (%)', d: 'Cierra el día cuando el beneficio supera este % — evita devolver ganancias', ico: '💰', def: 5.0 },
              { k: 'max_daily_dd_rule', src: 'col', type: 'num', l: 'Drawdown diario máx (%)', d: 'Bloqueo cuando el drawdown del día supera este %', ico: '📊', def: 3.0 },
              { k: 'block_weekends', src: 'col', type: 'bool', l: 'Bloquear fines de semana', d: 'El EA no abre nuevas posiciones viernes tarde, sábado ni domingo', ico: '📅', def: true },
              { k: 'auto_breakeven', src: 'col', type: 'bool', l: 'Auto Break-Even', d: 'Mueve el SL a precio de entrada cuando el trade va en beneficio', ico: '⚖️', def: true },
              { k: 'be_pips', src: 'col', type: 'num', l: 'Pips para Break-Even', d: 'Distancia en pips antes de activar el auto break-even', ico: '📏', def: 20.0 },
            ].map(r => {
              const val = r.src === 'rules' ? (editRules.rules?.[r.k] ?? 0) : (editRules[r.k] ?? r.def);
              const onChange = (newVal) => {
                if (r.src === 'rules') {
                  setEditRules(p => ({ ...p, rules: { ...p.rules, [r.k]: newVal } }));
                } else {
                  setEditRules(p => ({ ...p, [r.k]: newVal }));
                }
              };
              return (
                <div className="rule-card" key={r.k}>
                  <div className="rule-ico">{r.ico}</div>
                  <div className="rule-inf"><div className="rule-name">{r.l}</div><div className="rule-desc">{r.d}</div></div>
                  {r.type === 'bool' ? (
                    <div className={`tog ${val ? 'on-g' : ''}`} onClick={() => onChange(!val)} style={{ cursor: 'pointer' }} />
                  ) : (
                    <input type="number" step="0.1" style={{ width: 66, textAlign: 'center' }} value={val} onChange={e => onChange(+e.target.value)} />
                  )}
                </div>
              );
            })}
            <div className="fl g2 mt4">
              <button className="btn bp w100" onClick={async () => {
                const updates = {
                  rules: editRules.rules,
                  max_daily_profit: editRules.max_daily_profit ?? 5.0,
                  max_daily_dd_rule: editRules.max_daily_dd_rule ?? 3.0,
                  block_weekends: editRules.block_weekends ?? true,
                  auto_breakeven: editRules.auto_breakeven ?? true,
                  be_pips: editRules.be_pips ?? 20.0,
                };
                const { error } = await supabase.from('accounts').update(updates).eq('id', editRules.id);
                if (error) { alert('Error al guardar: ' + error.message); return; }
                setAccounts(p => p.map(a => a.id === editRules.id ? { ...a, ...updates } : a));
                setEditRules(null);
              }}>Guardar Reglas</button>
              <button className="btn bg" onClick={() => setEditRules(null)}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function EAView() {
  const [tab, setTab] = useState('mt5');




  const integrations = {
    mt5: {
      title: 'MetaTrader 5 — EA MQL5',
      badge: 'pb-mt5',
      steps: [],
    },
    mt: {
      title: 'Match Trader',
      badge: 'pb-mt',
      soon: true,
      steps: [],
    },
    ct: {
      title: 'cTrader',
      badge: 'pb-ct',
      soon: true,
      steps: [],
    },
    dx: {
      title: 'DXtrade',
      badge: 'pb-dx',
      soon: true,
      steps: [],
    },
  };

  const current = integrations[tab];

  return (
    <div>
      <div className="alert al-b mb4">
        <span>🤖</span>
        <div><b>TradeLeash EA</b> — Bloqueo automático nativo en <b>MT5</b>. Irreversible hasta medianoche. Match Trader, cTrader y DXtrade <span style={{color:'var(--yellow)'}}>próximamente</span>.</div>
      </div>

      <div className="alert al-y mb4">
        <span>⚠️</span>
        <div><b>Importante:</b> Antes de instalar el EA, verifica que tu empresa de fondeo <b>permite el uso de Expert Advisors</b> en la operativa. Algunas prop firms prohíben o restringen el uso de EAs. Consulta los términos y condiciones de tu empresa antes de activarlo en una cuenta real.</div>
      </div>

      <div className="fl g2 mb4" style={{ flexWrap: 'wrap' }}>
        {Object.entries(integrations).map(([k, v]) => (
          <button key={k} className={`chip ${tab === k ? 'on' : ''}`} onClick={() => setTab(k)}>
            <span className={`platform-badge ${v.badge}`} style={{ padding: '1px 5px' }}>{v.title.split('—')[0].trim()}</span>
          </button>
        ))}
      </div>

      {tab === 'mt5' && (
        <>
        <a href='https://tradeleash.com/tutorial.html' target='_blank' rel='noopener noreferrer' style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'rgba(0,212,255,0.08)', border: '1.5px solid rgba(0,212,255,0.25)', borderRadius: 10, padding: '14px 20px', marginBottom: 12, textDecoration: 'none' }}>
          <div><div style={{ fontWeight: 700, color: 'var(--accent)', fontSize: 13, marginBottom: 3 }}>Ver guia de instalacion completa</div>
          <div style={{ color: 'var(--t2)', fontSize: 12 }}>Paso a paso en tradeleash.com/tutorial.html</div></div>
          <div style={{ color: 'var(--accent)', fontSize: 20, fontWeight: 700 }}>→</div>
        </a>
        </>
      )}














      <div className="card">
        {current.soon ? (
          <div style={{ textAlign: 'center', padding: '32px 16px' }}>
            <div style={{ fontSize: 32, marginBottom: 12 }}>🔜</div>
            <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--t1)', marginBottom: 8 }}>Próximamente</div>
            <div style={{ fontSize: 13, color: 'var(--t3)', maxWidth: 340, margin: '0 auto', lineHeight: 1.6 }}>La integración con <b>{current.title}</b> está en desarrollo y estará disponible en una próxima versión.</div>
          </div>
        ) : (
          <>
        <div className="sh mb3"><span className="st">{current.title}</span></div>
        {current.steps.map(s => (
          <div className="rule-card" key={s.n}>
            <div style={{ width: 34, height: 34, borderRadius: '50%', background: 'rgba(79,142,255,.1)', border: '1px solid rgba(79,142,255,.25)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: 'var(--mono)', fontWeight: 700, color: 'var(--ac)', fontSize: 13, flexShrink: 0 }}>{s.n}</div>
            <div className="rule-inf"><div className="rule-name">{s.t}</div><div className="rule-desc">{s.d}</div></div>
          </div>
        ))}
          </>
        )}
      </div>
    </div>
  );
}

function LaunchView() {
  const phases = [
    { ico: '✅', cls: 'done', title: 'MVP Funcional', desc: 'Journal completo + sistema de bloqueo irreversible + EA MT5 + soporte Match Trader, cTrader, DXtrade', date: 'LISTO' },
    { ico: '🔥', cls: 'now', title: 'Beta Cerrada (50 usuarios)', desc: 'Recluta en comunidades de prop trading en Twitter/X, Discord y Reddit. Ofrece acceso gratuito 30 días a cambio de feedback. Objetivo: testimonios reales y detectar bugs.', date: 'MES 1' },
    { ico: '📣', cls: 'next', title: 'Lanzamiento en Comunidades', desc: 'Publica casos de uso reales en Twitter/X: "Este trader iba a perder su FTMO, el bloqueo lo salvó". Entra en Discord de FTMO, The5ers, TopStep. Crea contenido sobre psicología de trading.', date: 'MES 2' },
    { ico: '🤝', cls: 'next', title: 'Afiliados con Prop Firms', desc: 'Contacta FTMO, The5ers, Funded Next como herramienta complementaria. Ofrece comisión por referidos. Una mención en su newsletter = cientos de usuarios.', date: 'MES 3' },
    { ico: '📈', cls: 'next', title: 'Escala y Monetiza', desc: 'Con testimonios y tracción, sube precio a $19/mes. Añade plan anual con descuento. Lanza funcionalidad de reportes PDF para traders que necesitan demostrar su progreso.', date: 'MES 4-6' },
  ];

  const pricing = [
    { name: 'Starter', price: '$0', sub: 'Free forever', feats: ['1 cuenta', 'Journal básico', '30 trades/mes', 'Bloqueo manual'], featured: false },
    { name: 'Pro', price: '$12', sub: '/month · cancel anytime', feats: ['Cuentas ilimitadas', 'Journal + capturas', 'Bloqueo automático irreversible', 'EA MT5 + Match Trader + cTrader + DX', 'Estadísticas emocionales', 'Webhook en tiempo real', 'Soporte prioritario'], featured: true },
    { name: 'Annual', price: '$99', sub: '/year · save $45', feats: ['Todo de Pro', '2 meses gratis', 'Acceso beta a nuevas funciones', 'Soporte 1:1 onboarding'], featured: false },
  ];

  return (
    <div className="launch-page">
      <div className="hero">
        <div className="hero-badge">🚀 ESTRATEGIA DE LANZAMIENTO</div>
        <div className="hero-title">El journal que <span>bloquea tu cuenta</span><br />cuando más lo necesitas</div>
        <div className="hero-sub">Diseñado para traders de prop firms que sobreopean. El único journal con bloqueo irreversible hasta medianoche compatible con MT5, Match Trader, cTrader y DXtrade.</div>
      </div>

      <div className="launch-section">
        <div className="price-cards">
          {pricing.map(p => (
            <div key={p.name} className={`price-card ${p.featured ? 'featured' : ''}`}>
              <div className="price-name">{p.name}</div>
              <div className="price-amount">{p.price}<span>{p.sub}</span></div>
              <div className="price-desc" style={{ marginBottom: 16 }}></div>
              {p.feats.map(f => <div key={f} className="price-feat">{f}</div>)}
              <button className={`btn w100 ${p.featured ? 'bp' : 'bg'}`} style={{ marginTop: 16 }}>{p.featured ? 'Empezar ahora' : 'Seleccionar'}</button>
            </div>
          ))}
        </div>

        <div className="card mb4">
          <div className="sh mb3"><span className="st">💡 Por qué $12/month es el precio correcto</span></div>
          <div className="g3">
            {[
              { ico: '💰', t: 'ROI inmediato', d: 'Un trader funded con cuenta de $10,000 que pierde una prop firm por sobreoperar pierde $150-500 en fees. $14.99/mes es nada comparado con eso.' },
              { ico: '🎯', t: 'Competencia débil', d: 'Tradezella cobra $49/mes y no tiene bloqueo real. TraderSync $30/mes, igual. Tú tienes la función que más dolor resuelve, al precio más accesible.' },
              { ico: '📈', t: 'Escala fácil', d: '500 usuarios a $12 = $6,000 MRR. Con afiliados de prop firms, alcanzable en 6-8 meses. Sube a $19 cuando tengas tracción y testimonios.' },
            ].map(s => (
              <div key={s.t} style={{ background: 'var(--s2)', border: '1px solid var(--b1)', borderRadius: 10, padding: '14px' }}>
                <div style={{ fontSize: 22, marginBottom: 8 }}>{s.ico}</div>
                <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>{s.t}</div>
                <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.55 }}>{s.d}</div>
              </div>
            ))}
          </div>
        </div>

        <div className="launch-title">🗓️ Plan de Lanzamiento</div>
        <div className="launch-sub">De MVP a primeros ingresos en 90 días</div>
        <div className="timeline">
          {phases.map((p, i) => (
            <div key={i} className="tl-item">
              <div className={`tl-dot ${p.cls}`}>{p.ico}</div>
              <div className="tl-content">
                <div className="tl-title">{p.title}</div>
                <div className="tl-desc">{p.desc}</div>
                <div className="tl-date">{p.date}</div>
              </div>
            </div>
          ))}
        </div>

        <div className="card" style={{ marginTop: 24 }}>
          <div className="sh mb3"><span className="st">📣 Canales de Adquisición Prioritarios</span></div>
          <div className="g2">
            {[
              { ico: '🐦', t: 'Twitter / X', d: 'El 90% de la comunidad prop trading vive ahí. Publica clips de la pantalla de bloqueo. "Mi herramienta me salvó la cuenta hoy." Un viral = 1,000 usuarios.' },
              { ico: '💬', t: 'Discord & Reddit', d: 'r/Forex, r/Daytrading, Discord de FTMO/The5ers. Aporta valor primero, menciona la herramienta cuando sea relevante. No spamees.' },
              { ico: '📹', t: 'YouTube / TikTok', d: 'Tutoriales de "cómo configurar el EA de bloqueo en FTMO". SEO a largo plazo. Un video puede traer leads durante años.' },
              { ico: '🤝', t: 'Prop Firms directo', d: 'Escríbeles como herramienta para sus traders. Una integración oficial = distribución masiva. FTMO tiene 100,000+ usuarios activos.' },
            ].map(s => (
              <div key={s.t} style={{ background: 'var(--s2)', border: '1px solid var(--b1)', borderRadius: 10, padding: '14px' }}>
                <div className="fl aic g2 mb2"><span style={{ fontSize: 20 }}>{s.ico}</span><span style={{ fontWeight: 700, fontSize: 13 }}>{s.t}</span></div>
                <div style={{ fontSize: 11, color: 'var(--t2)', lineHeight: 1.55 }}>{s.d}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}



/* ─── PROFILE VIEW ───────────────────────────────────────────────────────── */
function ProfileView({ session, trades, accounts }) {
  const [changingPwd, setChangingPwd] = useState(false);
  const [pwd, setPwd] = useState({ current: '', new: '', confirm: '' });
  const [pwdMsg, setPwdMsg] = useState('');
  const [pwdErr, setPwdErr] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Trial calculation
  const createdAt = new Date(session?.user?.created_at || Date.now());
  const trialEnd = new Date(createdAt.getTime() + 7 * 24 * 60 * 60 * 1000);
  const now = new Date();
  const trialDaysLeft = Math.max(0, Math.ceil((trialEnd - now) / (1000 * 60 * 60 * 24)));
    const isPro = session?.user?.user_metadata?.is_pro === true; const trialActive = !isPro && trialDaysLeft > 0;
  const trialPct = Math.min(100, Math.round(((7 - trialDaysLeft) / 7) * 100));

  const handleChangePwd = async () => {
    if (pwd.new.length < 6) { setPwdErr('La contraseña debe tener al menos 6 caracteres.'); return; }
    if (pwd.new !== pwd.confirm) { setPwdErr('Las contraseñas no coinciden.'); return; }
    setPwdErr('');
    const { error } = await supabase.auth.updateUser({ password: pwd.new });
    if (error) { setPwdErr(error.message); }
    else { setPwdMsg('¡Contraseña actualizada correctamente!'); setChangingPwd(false); setPwd({ current: '', new: '', confirm: '' }); setTimeout(() => setPwdMsg(''), 3000); }
  };

  const handleDeleteAccount = async () => {
    if (!window.confirm('¿Estás seguro? Esta acción eliminará todos tus datos permanentemente y no se puede deshacer.')) return;
    if (!window.confirm('Última confirmación — ¿eliminar cuenta definitivamente?')) return;
    setDeleting(true);
    await supabase.auth.signOut();
  };

  return (
    <div style={{ maxWidth: 600, margin: '0 auto' }}>

      {/* Plan card */}
      <div className="card mb4" style={{ borderLeft: (isPro || trialActive) ? '3px solid var(--ac)' : '3px solid var(--b2)' }}>
        <div className="fl aic jb mb3">
          <div>
            <div className="card-lbl">PLAN ACTUAL</div>
            <div style={{ fontSize: 18, fontWeight: 700, color: 'var(--t1)' }}>
              {isPro ? 'Plan Pro activo' : trialActive ? 'Trial activo' : 'Free'}
            </div>
          </div>
          {isPro ? (
            <span style={{ background: 'var(--ac-glow)', color: 'var(--ac)', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, border: '1px solid rgba(37,99,235,0.2)' }}>✅ Activo</span>
          ) : trialActive ? (
            <span style={{ background: 'var(--ac-glow)', color: 'var(--ac)', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20, border: '1px solid rgba(37,99,235,0.2)' }}>
              {trialDaysLeft} día{trialDaysLeft !== 1 ? 's' : ''} restante{trialDaysLeft !== 1 ? 's' : ''}
            </span>
          ) : (
            <span style={{ background: 'var(--s3)', color: 'var(--t3)', fontSize: 11, fontWeight: 700, padding: '4px 12px', borderRadius: 20 }}>Free</span>
          )}
        </div>

        {trialActive && (
          <>
            <div style={{ fontSize: 12, color: 'var(--t3)', marginBottom: 10 }}>
              Tu prueba gratuita incluye todas las funciones Pro. Termina el <b style={{ color: 'var(--t2)' }}>{trialEnd.toLocaleDateString('es-ES', { day: 'numeric', month: 'long' })}</b>.
            </div>
            <div className="prog mb3">
              <div className="pfill" style={{ width: trialPct + '%', background: trialPct > 70 ? 'var(--red)' : 'var(--ac)' }} />
            </div>
          </>
        )}

        

        <div style={{ background: 'var(--s2)', borderRadius: 10, padding: '14px', marginBottom: 16 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--t1)', marginBottom: 10 }}>Incluido en Pro:</div>
          {['Cuentas ilimitadas', 'Journal completo + capturas', 'Bloqueo automático irreversible', 'EA para MT5', 'Estadísticas emocionales y por setup', 'Exportar a CSV', 'Soporte prioritario'].map(f => (
            <div key={f} className="fl aic g2 mb2" style={{ fontSize: 12, color: 'var(--t2)' }}>
              <span style={{ color: 'var(--green)', fontSize: 14 }}>✓</span> {f}
            </div>
          ))}
        </div>

        {!isPro && (
        <button className="btn bp w100" style={{ fontSize: 13 }} onClick={() => { fetch('/api/stripe', {method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'create_checkout',email:session?.user?.email})}).then(r=>r.json()).then(d=>{if(d.url)window.location.href=d.url})}}>
          {trialActive ? '🚀 Suscribirse a Pro — $14.99/mes' : '🔓 Activar Pro — $14.99/mes'}
        </button>
        )}
        {isPro && (
          <div style={{ textAlign: 'center', padding: '10px 0', fontSize: 13, fontWeight: 600, color: 'var(--ac)' }}>✅ Plan Pro activo — gracias por tu suscripción</div>
        )}
        <div style={{ fontSize: 11, color: 'var(--t4)', textAlign: 'center', marginTop: 8 }}>Cancela cuando quieras · Sin permanencia</div>
      </div>

      {/* Account info */}
      <div className="card mb4">
        <div className="sh mb3"><span className="st">👤 Información de Cuenta</span></div>
        <div className="fl aic jb" style={{ padding: '10px 0', borderBottom: '1px solid var(--b1)' }}>
          <span style={{ fontSize: 12, color: 'var(--t3)' }}>Email</span>
          <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--t1)', fontFamily: 'var(--mono)' }}>{session?.user?.email}</span>
        </div>
        <div className="fl aic jb" style={{ padding: '10px 0', borderBottom: '1px solid var(--b1)' }}>
          <span style={{ fontSize: 12, color: 'var(--t3)' }}>Miembro desde</span>
          <span style={{ fontSize: 12, color: 'var(--t2)', fontFamily: 'var(--mono)' }}>{createdAt.toLocaleDateString('es-ES', { day: 'numeric', month: 'long', year: 'numeric' })}</span>
        </div>
        <div className="fl aic jb" style={{ padding: '10px 0', borderBottom: '1px solid var(--b1)' }}>
          <span style={{ fontSize: 12, color: 'var(--t3)' }}>Trades registrados</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ac)', fontFamily: 'var(--mono)' }}>{trades.length}</span>
        </div>
        <div className="fl aic jb" style={{ padding: '10px 0' }}>
          <span style={{ fontSize: 12, color: 'var(--t3)' }}>Cuentas conectadas</span>
          <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--ac)', fontFamily: 'var(--mono)' }}>{accounts.length}</span>
        </div>
      </div>

      {/* Change password */}
      <div className="card mb4">
        <div className="sh mb3">
          <span className="st">🔑 Contraseña</span>
          <button className="btn bg btn-sm" onClick={() => setChangingPwd(p => !p)}>{changingPwd ? 'Cancelar' : 'Cambiar contraseña'}</button>
        </div>
        {pwdMsg && <div className="alert al-g mb3" style={{ fontSize: 12 }}><span>✅</span><span>{pwdMsg}</span></div>}
        {changingPwd && (
          <div>
            {pwdErr && <div className="alert al-r mb3" style={{ fontSize: 12 }}><span>❌</span><span>{pwdErr}</span></div>}
            <div className="fg mb3">
              <label>Nueva contraseña</label>
              <input type="password" placeholder="Mínimo 6 caracteres" value={pwd.new} onChange={e => setPwd(p => ({ ...p, new: e.target.value }))} />
            </div>
            <div className="fg mb3">
              <label>Confirmar contraseña</label>
              <input type="password" placeholder="Repite la contraseña" value={pwd.confirm} onChange={e => setPwd(p => ({ ...p, confirm: e.target.value }))} />
            </div>
            <button className="btn bp" onClick={handleChangePwd}>Guardar contraseña</button>
          </div>
        )}
        {!changingPwd && <div style={{ fontSize: 12, color: 'var(--t3)' }}>Tu contraseña está configurada. Cámbiala si crees que tu cuenta está en riesgo.</div>}
      </div>

      {/* Danger zone */}
      <div className="card" style={{ borderColor: 'rgba(220,38,38,0.2)' }}>
        <div className="sh mb3"><span className="st" style={{ color: 'var(--red)' }}>⚠️ Zona de peligro</span></div>
        <div style={{ fontSize: 12, color: 'var(--t3)', marginBottom: 14, lineHeight: 1.6 }}>
          Eliminar tu cuenta borrará permanentemente todos tus trades, cuentas y datos. Esta acción no se puede deshacer.
        </div>
        <button className="btn br btn-sm" onClick={handleDeleteAccount} disabled={deleting}>
          {deleting ? 'Cerrando sesión...' : 'Eliminar mi cuenta'}
        </button>
      </div>

    </div>
  );
}

/* ─── ONBOARDING PANEL ───────────────────────────────────────────────────── */
function OnboardingPanel({ trades, accounts, onDismiss, onNavigate }) {
  const hasAccount = accounts.some(a => a.name && a.broker);
  const hasTrade = trades.length > 0;
  const hasEA = false; // user marks manually
  const [eaDone, setEaDone] = useState(() => {
    try { return localStorage.getItem('tg_onb_ea') === '1'; } catch { return false; }
  });
  const [rulesDone, setRulesDone] = useState(() => {
    try { return localStorage.getItem('tg_onb_rules') === '1'; } catch { return false; }
  });

  const steps = [
    { id: 'account', label: 'Añade tu primera cuenta', desc: 'Bróker, plataforma y reglas de riesgo', done: hasAccount, nav: 'accounts' },
    { id: 'trade', label: 'Registra tu primer trade', desc: 'Par, PnL, emoción y setup', done: hasTrade, nav: 'journal' },
    { id: 'ea', label: 'Instala el EA en MT5', desc: 'Bloqueo automático activado', done: eaDone, nav: 'ea', manual: true },
    { id: 'rules', label: 'Configura tus reglas', desc: 'Max DD, max trades, cooldown', done: rulesDone, nav: 'accounts', manual: true },
  ];

  const completed = steps.filter(s => s.done).length;
  const pct = Math.round((completed / steps.length) * 100);
  const allDone = completed === steps.length;
  const activeStep = steps.find(s => !s.done);

  const markManual = (id) => {
    if (id === 'ea') { setEaDone(true); try { localStorage.setItem('tg_onb_ea','1'); } catch {} }
    if (id === 'rules') { setRulesDone(true); try { localStorage.setItem('tg_onb_rules','1'); } catch {} }
  };

  return (
    <div className="onb-panel">
      <div className="onb-title">🚀 Primeros pasos</div>
      <div className="onb-sub">Completa la configuración inicial para sacarle el máximo partido a TradeLeash.</div>

      {steps.map(s => (
        <div key={s.id} className={`onb-item ${s.done ? 'done' : s.id === activeStep?.id ? 'active' : ''}`}
          onClick={() => { if (!s.done) { onNavigate(s.nav); if (s.manual) markManual(s.id); } }}>
          <div className={`onb-ico ${s.done ? 'done' : s.id === activeStep?.id ? 'active' : ''}`}>
            {s.done ? '✓' : ''}
          </div>
          <div>
            <div className={`onb-lbl ${s.done ? 'done' : ''}`}>{s.label}</div>
            {!s.done && <div className="onb-desc">{s.desc}</div>}
          </div>
        </div>
      ))}

      <div className="onb-prog">
        <div className="onb-prog-lbl">{completed} de {steps.length} completados</div>
        <div className="onb-prog-bar"><div className="onb-prog-fill" style={{ width: pct + '%' }} /></div>
      </div>

      {activeStep && !allDone && (
        <div className="onb-tip">
          💡 Siguiente: <b>{activeStep.label}</b>
        </div>
      )}

      {allDone && (
        <div className="onb-tip" style={{ textAlign: 'center' }}>
          🎉 ¡Todo listo! Ya tienes TradeLeash configurado al 100%.
        </div>
      )}

      <button className="onb-dismiss" onClick={onDismiss}>
        {allDone ? 'Cerrar guía' : 'Ocultar por ahora'}
      </button>
    </div>
  );
}

/* ─── APP SHELL ───────────────────────────────────────────────────────────── */

// ─── PROP FIRMS VIEW ─────────────────────────────────────────────────────────
const PROP_FIRMS_LIST = [
  'FTMO', 'The5ers', 'MyForexFunds', 'Funded Next', 'True Forex Funds',
  'E8 Funding', 'Topstep', 'Apex Trader Funding', 'Funder Trading',
  'Alpha Capital', 'Instant Funding', 'Otra'
];
const ACCOUNT_SIZES = ['$5.000', '$10.000', '$25.000', '$50.000', '$100.000', '$200.000', 'Otro'];
const PHASES = ['Challenge', 'Verificación', 'Funded', 'Completada'];
const STATUS_CFG = {
  activa:   { label: 'Activa',   color: 'var(--green)',  bg: 'var(--green-bg)'  },
  perdida:  { label: 'Perdida',  color: 'var(--red)',    bg: 'var(--red-bg)'    },
  retirada: { label: 'Retirada', color: 'var(--t3)',     bg: 'var(--s3)'        },
  pausada:  { label: 'Pausada',  color: 'var(--yellow)', bg: 'var(--yellow-bg)' },
};
function PropFirmsView({ session }) {
  const [pfAccs, setPfAccs] = useState([]);
  const [pfLoading, setPfLoading] = useState(true);
  const [pfShowAdd, setPfShowAdd] = useState(false);
  const [pfEdit, setPfEdit] = useState(null);
  const [pfSaving, setPfSaving] = useState(false);
  const pfEmpty = { prop_firm:'FTMO', account_name:'', account_size:'$10.000', phase:'Challenge', status:'activa', challenge_cost:'', amount_withdrawn:'', extra_costs:'', notes:'' };
  const [pfForm, setPfForm] = useState(pfEmpty);
  useEffect(() => {
    if (!session?.user?.id) return;
    (async () => {
      setPfLoading(true);
      const { data } = await supabase.from('prop_accounts').select('*').eq('user_id', session.user.id).order('created_at', { ascending: false });
      setPfAccs(data || []);
      setPfLoading(false);
    })();
  }, [session]);
  const fmtM = (n) => { const v = parseFloat(n)||0; return v===0?'—':'$'+v.toLocaleString('es-ES',{minimumFractionDigits:2,maximumFractionDigits:2}); };
  const calcROI = (a) => { const c=(parseFloat(a.challenge_cost)||0)+(parseFloat(a.extra_costs)||0); const w=parseFloat(a.amount_withdrawn)||0; return c===0?null:w-c; };
  const totalInv  = pfAccs.reduce((s,a)=>s+(parseFloat(a.challenge_cost)||0)+(parseFloat(a.extra_costs)||0),0);
  const totalWith = pfAccs.reduce((s,a)=>s+(parseFloat(a.amount_withdrawn)||0),0);
  const totalROI  = totalWith - totalInv;
  const activeC   = pfAccs.filter(a=>a.status==='activa').length;
  const lostC     = pfAccs.filter(a=>a.status==='perdida').length;
  const wr        = pfAccs.length?((pfAccs.length-lostC)/pfAccs.length*100).toFixed(0):0;
  const pfSave = async () => {
    setPfSaving(true);
    const payload = { user_id:session.user.id, prop_firm:pfForm.prop_firm, account_name:pfForm.account_name, account_size:pfForm.account_size, phase:pfForm.phase, status:pfForm.status, challenge_cost:parseFloat(pfForm.challenge_cost)||0, amount_withdrawn:parseFloat(pfForm.amount_withdrawn)||0, extra_costs:parseFloat(pfForm.extra_costs)||0, notes:pfForm.notes };
    if (pfEdit) {
      const { error } = await supabase.from('prop_accounts').update(payload).eq('id', pfEdit.id);
      if (!error) { setPfAccs(p=>p.map(a=>a.id===pfEdit.id?{...a,...payload}:a)); setPfEdit(null); }
    } else {
      const { data, error } = await supabase.from('prop_accounts').insert([payload]).select().single();
      if (!error && data) setPfAccs(p=>[data,...p]);
    }
    setPfForm(pfEmpty); setPfShowAdd(false); setPfSaving(false);
  };
  const pfEditOpen = (a) => { setPfForm({ prop_firm:a.prop_firm, account_name:a.account_name||'', account_size:a.account_size, phase:a.phase, status:a.status, challenge_cost:a.challenge_cost||'', amount_withdrawn:a.amount_withdrawn||'', extra_costs:a.extra_costs||'', notes:a.notes||'' }); setPfEdit(a); setPfShowAdd(true); };
  const pfDel = async (id) => { if (!window.confirm('¿Eliminar esta cuenta?')) return; await supabase.from('prop_accounts').delete().eq('id',id); setPfAccs(p=>p.filter(a=>a.id!==id)); };
  return (
    <div>
      <div className="fr mb4" style={{gap:12,flexWrap:'wrap'}}>
        {[{l:'Total invertido',v:fmtM(totalInv),c:'var(--red)',ico:'💸'},{l:'Total retirado',v:fmtM(totalWith),c:'var(--green)',ico:'💰'},{l:'ROI neto',v:fmtM(totalROI),c:totalROI>=0?'var(--green)':'var(--red)',ico:'📊'},{l:'Activas',v:activeC,c:'var(--ac)',ico:'✅'},{l:'Perdidas',v:lostC,c:'var(--red)',ico:'❌'},{l:'% Éxito',v:wr+'%',c:'var(--purple)',ico:'🏆'}].map(s=>(
          <div key={s.l} className="card" style={{flex:'1 1 130px',padding:'14px 16px',minWidth:120}}>
            <div style={{fontSize:18,marginBottom:6}}>{s.ico}</div>
            <div style={{fontSize:11,color:'var(--t3)',marginBottom:4}}>{s.l}</div>
            <div style={{fontSize:18,fontWeight:700,fontFamily:'var(--mono)',color:s.c}}>{s.v}</div>
          </div>
        ))}
      </div>
      <div className="fl aic jb mb3">
        <div>
          <div style={{fontSize:15,fontWeight:700,color:'var(--t1)'}}>🏆 Historial de Prop Firms</div>
          <div style={{fontSize:11,color:'var(--t3)',marginTop:2}}>{pfAccs.length} cuenta{pfAccs.length!==1?'s':''} registrada{pfAccs.length!==1?'s':''}</div>
        </div>
        <button className="btn bp" onClick={()=>{setPfForm(pfEmpty);setPfEdit(null);setPfShowAdd(true);}}>➕ Añadir cuenta</button>
      </div>
      {pfLoading ? (
        <div className="card" style={{padding:32,textAlign:'center',color:'var(--t3)',fontSize:13}}>Cargando...</div>
      ) : pfAccs.length===0 ? (
        <div className="card" style={{padding:48,textAlign:'center'}}>
          <div style={{fontSize:40,marginBottom:12}}>🏆</div>
          <div style={{fontSize:14,fontWeight:600,color:'var(--t1)',marginBottom:6}}>Sin cuentas registradas</div>
          <div style={{fontSize:12,color:'var(--t3)',marginBottom:20}}>Añade tus cuentas de prop firms para llevar el control de tu ROI</div>
          <button className="btn bp" onClick={()=>setPfShowAdd(true)}>➕ Añadir primera cuenta</button>
        </div>
      ) : (
        <div style={{display:'flex',flexDirection:'column',gap:10}}>
          {pfAccs.map(acc=>{
            const roi=calcROI(acc);
            const cost=(parseFloat(acc.challenge_cost)||0)+(parseFloat(acc.extra_costs)||0);
            const st=STATUS_CFG[acc.status]||STATUS_CFG.activa;
            return (
              <div key={acc.id} className="card" style={{padding:'14px 16px'}}>
                <div className="fl aic jb" style={{flexWrap:'wrap',gap:10}}>
                  <div className="fl aic g2" style={{flex:1,minWidth:200}}>
                    <div style={{width:40,height:40,borderRadius:10,background:'var(--s2)',border:'1px solid var(--b1)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:18,flexShrink:0}}>🏦</div>
                    <div>
                      <div style={{fontSize:13,fontWeight:700,color:'var(--t1)'}}>{acc.prop_firm}</div>
                      {acc.account_name&&<div style={{fontSize:11,color:'var(--t3)'}}>{acc.account_name}</div>}
                      <div className="fl aic g2 mt1">
                        <span style={{fontSize:10,fontWeight:600,color:'var(--t2)',background:'var(--s2)',border:'1px solid var(--b1)',borderRadius:4,padding:'1px 6px'}}>{acc.account_size}</span>
                        <span style={{fontSize:10,color:'var(--t3)'}}>{acc.phase}</span>
                      </div>
                    </div>
                  </div>
                  <div className="fl g3" style={{flexWrap:'wrap',gap:'8px 24px'}}>
                    <div style={{textAlign:'center'}}><div style={{fontSize:10,color:'var(--t3)',marginBottom:2}}>Coste</div><div style={{fontSize:13,fontWeight:700,fontFamily:'var(--mono)',color:'var(--red)'}}>{fmtM(cost)}</div></div>
                    <div style={{textAlign:'center'}}><div style={{fontSize:10,color:'var(--t3)',marginBottom:2}}>Retirado</div><div style={{fontSize:13,fontWeight:700,fontFamily:'var(--mono)',color:'var(--green)'}}>{fmtM(acc.amount_withdrawn)}</div></div>
                    <div style={{textAlign:'center'}}><div style={{fontSize:10,color:'var(--t3)',marginBottom:2}}>ROI neto</div><div style={{fontSize:14,fontWeight:700,fontFamily:'var(--mono)',color:roi===null?'var(--t4)':roi>=0?'var(--green)':'var(--red)'}}>{roi===null?'—':(roi>=0?'+':'')+fmtM(roi)}</div></div>
                  </div>
                  <div className="fl aic g2">
                    <span style={{fontSize:11,fontWeight:600,color:st.color,background:st.bg,border:`1px solid ${st.color}30`,borderRadius:6,padding:'3px 10px'}}>{st.label}</span>
                    <button onClick={()=>pfEditOpen(acc)} style={{background:'var(--s2)',border:'1px solid var(--b1)',borderRadius:6,padding:'5px 10px',cursor:'pointer',fontSize:12,color:'var(--t2)'}}>✏️</button>
                    <button onClick={()=>pfDel(acc.id)} style={{background:'var(--red-bg)',border:'1px solid rgba(220,38,38,0.2)',borderRadius:6,padding:'5px 10px',cursor:'pointer',fontSize:12,color:'var(--red)'}}>🗑️</button>
                  </div>
                </div>
                {acc.notes&&<div style={{marginTop:10,paddingTop:10,borderTop:'1px solid var(--b1)',fontSize:11,color:'var(--t3)',fontStyle:'italic'}}>📝 {acc.notes}</div>}
              </div>
            );
          })}
        </div>
      )}
      {pfShowAdd&&(
        <div className="ov" onClick={()=>{setPfShowAdd(false);setPfEdit(null);}}>
          <div className="modal" onClick={e=>e.stopPropagation()} style={{maxWidth:500}}>
            <button className="mc" onClick={()=>{setPfShowAdd(false);setPfEdit(null);}}>✕</button>
            <div className="mt">{pfEdit?'✏️ Editar cuenta':'➕ Nueva cuenta Prop Firm'}</div>
            <div className="ms">Registra los datos para calcular el ROI automáticamente</div>
            <div className="fr mb3">
              <div className="fg" style={{marginBottom:0}}><label>Prop Firm</label><select value={pfForm.prop_firm} onChange={e=>setPfForm(p=>({...p,prop_firm:e.target.value}))}>{PROP_FIRMS_LIST.map(f=><option key={f}>{f}</option>)}</select></div>
              <div className="fg" style={{marginBottom:0}}><label>Nombre / referencia</label><input placeholder="Challenge #1..." value={pfForm.account_name} onChange={e=>setPfForm(p=>({...p,account_name:e.target.value}))}/></div>
            </div>
            <div className="fr mb3">
              <div className="fg" style={{marginBottom:0}}><label>Tamaño</label><select value={pfForm.account_size} onChange={e=>setPfForm(p=>({...p,account_size:e.target.value}))}>{ACCOUNT_SIZES.map(s=><option key={s}>{s}</option>)}</select></div>
              <div className="fg" style={{marginBottom:0}}><label>Fase</label><select value={pfForm.phase} onChange={e=>setPfForm(p=>({...p,phase:e.target.value}))}>{PHASES.map(f=><option key={f}>{f}</option>)}</select></div>
            </div>
            <div className="fg mb3"><label>Estado</label><div className="fl g2" style={{flexWrap:'wrap'}}>{Object.entries(STATUS_CFG).map(([k,v])=><button key={k} onClick={()=>setPfForm(p=>({...p,status:k}))} style={{padding:'6px 14px',borderRadius:8,cursor:'pointer',fontSize:12,fontWeight:600,border:`1px solid ${pfForm.status===k?v.color:'var(--b1)'}`,background:pfForm.status===k?v.bg:'var(--s2)',color:pfForm.status===k?v.color:'var(--t3)'}}>{v.label}</button>)}</div></div>
            <div className="fr mb3">
              <div className="fg" style={{marginBottom:0}}><label>Coste challenge ($)</label><input type="number" placeholder="149" value={pfForm.challenge_cost} onChange={e=>setPfForm(p=>({...p,challenge_cost:e.target.value}))}/></div>
              <div className="fg" style={{marginBottom:0}}><label>Costes extra ($)</label><input type="number" placeholder="0" value={pfForm.extra_costs} onChange={e=>setPfForm(p=>({...p,extra_costs:e.target.value}))}/></div>
            </div>
            <div className="fg mb3"><label>Dinero retirado ($)</label><input type="number" placeholder="0" value={pfForm.amount_withdrawn} onChange={e=>setPfForm(p=>({...p,amount_withdrawn:e.target.value}))}/></div>
            <div className="fg mb4"><label>Notas (opcional)</label><input placeholder="Perdida por DD..." value={pfForm.notes} onChange={e=>setPfForm(p=>({...p,notes:e.target.value}))}/></div>
            {parseFloat(pfForm.challenge_cost)>0&&<div className="alert al-b mb3" style={{fontSize:12}}>📊 ROI estimado: <b style={{color:(parseFloat(pfForm.amount_withdrawn)||0)-(parseFloat(pfForm.challenge_cost)||0)-(parseFloat(pfForm.extra_costs)||0)>=0?'var(--green)':'var(--red)'}}>{fmtM((parseFloat(pfForm.amount_withdrawn)||0)-(parseFloat(pfForm.challenge_cost)||0)-(parseFloat(pfForm.extra_costs)||0))}</b></div>}
            <div className="fl g2">
              <button className="btn bp w100" onClick={pfSave} disabled={pfSaving}>{pfSaving?'Guardando...':pfEdit?'💾 Guardar cambios':'➕ Añadir cuenta'}</button>
              <button className="btn bg" onClick={()=>{setPfShowAdd(false);setPfEdit(null);}}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


function LandingPage({ onEnter }) {
  const [showAuth, setShowAuth] = useState(false);
  if (showAuth) return <Auth />;
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Manrope:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;600&display=swap');
        .lp-body { font-family: 'Manrope', sans-serif; background: #fff; color: #0f172a; margin: 0; overflow-x: hidden; }
        .lp-nav { position: fixed; top: 0; left: 0; right: 0; z-index: 100; display: flex; align-items: center; justify-content: space-between; padding: 16px 32px; background: rgba(255,255,255,0.9); backdrop-filter: blur(20px); border-bottom: 1px solid #e2e8f0; }
        .lp-logo { display: flex; align-items: center; gap: 10px; text-decoration: none; }
        .lp-logo-mark { width: 34px; height: 34px; background: #059669; border-radius: 9px; display: flex; align-items: center; justify-content: center; font-size: 16px; }
        .lp-logo-name { font-size: 20px; font-weight: 800; color: #0f172a; letter-spacing: -.3px; }
        .lp-logo-name span { color: #059669; }
        .lp-nav-links { display: flex; align-items: center; gap: 4px; }
        .lp-nav-link { color: #475569; text-decoration: none; font-size: 14px; font-weight: 500; padding: 8px 16px; border-radius: 8px; }
        .lp-nav-link:hover { background: #f1f5f9; color: #0f172a; }
        .lp-btn { background: #059669; color: #fff; padding: 10px 20px; border-radius: 10px; font-size: 14px; font-weight: 600; border: none; cursor: pointer; font-family: 'Manrope', sans-serif; transition: background .15s; }
        .lp-btn:hover { background: #047857; }
        .lp-btn-outline { background: transparent; color: #0f172a; padding: 14px 28px; border-radius: 12px; font-size: 16px; font-weight: 600; border: 1.5px solid #cbd5e1; cursor: pointer; font-family: 'Manrope', sans-serif; transition: border-color .15s; }
        .lp-btn-outline:hover { border-color: #059669; }
        .lp-btn-lg { background: #059669; color: #fff; padding: 16px 32px; border-radius: 12px; font-size: 16px; font-weight: 600; border: none; cursor: pointer; font-family: 'Manrope', sans-serif; box-shadow: 0 4px 14px rgba(5,150,105,0.3); transition: background .15s, transform .15s; }
        .lp-btn-lg:hover { background: #047857; transform: translateY(-1px); }
        .lp-hero { padding: 140px 32px 100px; text-align: center; background: linear-gradient(180deg, #f0fdf4 0%, #fff 100%); }
        .lp-badge { display: inline-flex; align-items: center; gap: 8px; background: #ecfdf5; border: 1px solid #a7f3d0; color: #059669; padding: 6px 16px; border-radius: 100px; font-size: 13px; font-weight: 600; margin-bottom: 32px; }
        .lp-badge-dot { width: 7px; height: 7px; border-radius: 50%; background: #059669; animation: lp-pulse 2s infinite; }
        @keyframes lp-pulse { 0%,100%{opacity:1} 50%{opacity:.4} }
        .lp-h1 { font-weight: 800; font-size: clamp(36px,5.5vw,68px); line-height: 1.08; letter-spacing: -2.5px; margin-bottom: 24px; max-width: 880px; margin-left: auto; margin-right: auto; }
        .lp-h1 .blue { color: #2563eb; }
        .lp-h1 .red { color: #dc2626; }
        .lp-sub { font-size: clamp(16px,2vw,19px); color: #475569; max-width: 560px; margin: 0 auto 48px; line-height: 1.7; }
        .lp-actions { display: flex; gap: 12px; justify-content: center; flex-wrap: wrap; margin-bottom: 64px; }
        .lp-stats { display: flex; gap: 0; justify-content: center; border: 1.5px solid #e2e8f0; border-radius: 16px; background: #fff; box-shadow: 0 1px 3px rgba(0,0,0,0.06); overflow: hidden; max-width: 600px; margin: 0 auto; }
        .lp-stat { flex: 1; padding: 20px 24px; text-align: center; border-right: 1px solid #e2e8f0; }
        .lp-stat:last-child { border-right: none; }
        .lp-stat-num { font-size: 28px; font-weight: 800; color: #0f172a; letter-spacing: -1px; }
        .lp-stat-label { font-size: 12px; color: #94a3b8; font-weight: 500; margin-top: 2px; }
        .lp-section { padding: 100px 32px; max-width: 1100px; margin: 0 auto; }
        .lp-tag { font-size: 12px; font-weight: 700; letter-spacing: 3px; color: #059669; text-transform: uppercase; margin-bottom: 16px; }
        .lp-h2 { font-weight: 800; font-size: clamp(28px,4vw,46px); line-height: 1.1; letter-spacing: -1.5px; margin-bottom: 20px; }
        .lp-h2-sub { font-size: 17px; color: #475569; max-width: 580px; line-height: 1.7; margin-bottom: 56px; }
        .lp-grid-3 { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px,1fr)); gap: 20px; }
        .lp-prob-card { background: #fff; border: 1.5px solid #fecaca; border-radius: 16px; padding: 28px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
        .lp-prob-icon { font-size: 28px; margin-bottom: 14px; }
        .lp-prob-title { font-weight: 700; font-size: 16px; margin-bottom: 8px; color: #dc2626; }
        .lp-prob-text { font-size: 14px; color: #475569; line-height: 1.65; }
        .lp-feat-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(320px,1fr)); gap: 20px; }
        .lp-feat-card { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 16px; padding: 32px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); transition: border-color .2s, transform .2s; }
        .lp-feat-card:hover { border-color: #a7f3d0; transform: translateY(-3px); }
        .lp-feat-card.star { border-color: #a7f3d0; background: #f0fdf4; }
        .lp-feat-icon { width: 48px; height: 48px; border-radius: 12px; display: flex; align-items: center; justify-content: center; font-size: 22px; margin-bottom: 20px; }
        .lp-feat-title { font-weight: 700; font-size: 18px; margin-bottom: 10px; }
        .lp-feat-text { font-size: 14px; color: #475569; line-height: 1.7; margin-bottom: 16px; }
        .lp-feat-tag { display: inline-flex; background: #ecfdf5; color: #059669; border: 1px solid #a7f3d0; padding: 4px 12px; border-radius: 100px; font-size: 12px; font-weight: 600; }
        .lp-steps { display: flex; flex-direction: column; }
        .lp-step { display: grid; grid-template-columns: 72px 1fr; gap: 32px; align-items: start; padding: 44px 0; border-bottom: 1px solid #e2e8f0; }
        .lp-step:last-child { border-bottom: none; }
        .lp-step-num { width: 60px; height: 60px; border-radius: 16px; background: #059669; color: #fff; display: flex; align-items: center; justify-content: center; font-weight: 800; font-size: 22px; flex-shrink: 0; box-shadow: 0 4px 12px rgba(5,150,105,0.25); }
        .lp-step-title { font-weight: 700; font-size: 21px; margin-bottom: 10px; }
        .lp-step-text { font-size: 15px; color: #475569; line-height: 1.7; }
        .lp-step-code { margin-top: 16px; background: #0f172a; color: #a5f3fc; border-radius: 10px; padding: 14px 18px; font-family: 'JetBrains Mono', monospace; font-size: 13px; display: inline-block; }
        .lp-compat-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 20px; }
        .lp-compat-card { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 16px; padding: 28px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
        .lp-compat-label { font-size: 11px; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 20px; }
        .lp-compat-label.active { color: #059669; }
        .lp-compat-label.soon { color: #94a3b8; }
        .lp-compat-item { display: flex; align-items: center; gap: 12px; padding: 12px 0; border-bottom: 1px solid #e2e8f0; }
        .lp-compat-item:last-child { border-bottom: none; }
        .lp-compat-name { font-weight: 600; font-size: 14px; }
        .lp-compat-desc { font-size: 12px; color: #94a3b8; }
        .lp-compat-badge { margin-left: auto; font-size: 11px; font-weight: 600; padding: 3px 10px; border-radius: 100px; }
        .lp-compat-badge.active { background: #ecfdf5; color: #059669; border: 1px solid #a7f3d0; }
        .lp-compat-badge.soon { background: #f1f5f9; color: #94a3b8; border: 1px solid #e2e8f0; }
        .lp-price-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px,1fr)); gap: 20px; align-items: start; }
        .lp-price-card { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 20px; padding: 36px 32px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); position: relative; }
        .lp-price-card.star { border-color: #059669; box-shadow: 0 0 0 4px rgba(5,150,105,0.08); }
        .lp-price-badge { position: absolute; top: -13px; left: 50%; transform: translateX(-50%); background: #059669; color: #fff; padding: 4px 18px; border-radius: 100px; font-size: 12px; font-weight: 700; white-space: nowrap; }
        .lp-price-name { font-size: 12px; color: #94a3b8; font-weight: 700; letter-spacing: 2px; text-transform: uppercase; margin-bottom: 16px; }
        .lp-price-amount { font-weight: 800; font-size: 52px; line-height: 1; letter-spacing: -2px; margin-bottom: 4px; }
        .lp-price-amount span { font-size: 16px; color: #475569; font-weight: 500; letter-spacing: 0; }
        .lp-price-period { font-size: 13px; color: #94a3b8; margin-bottom: 24px; }
        .lp-price-trial { background: #ecfdf5; border: 1px solid #a7f3d0; border-radius: 10px; padding: 12px 16px; font-size: 13px; color: #059669; margin-bottom: 24px; font-weight: 600; }
        .lp-price-feats { list-style: none; margin-bottom: 28px; padding: 0; }
        .lp-price-feats li { display: flex; gap: 10px; align-items: flex-start; font-size: 14px; color: #475569; padding: 9px 0; border-bottom: 1px solid #e2e8f0; }
        .lp-price-feats li:last-child { border-bottom: none; }
        .lp-price-feats li .ck { color: #059669; flex-shrink: 0; font-weight: 700; }
        .lp-price-btn { display: block; width: 100%; background: #059669; color: #fff; border: none; cursor: pointer; padding: 14px; border-radius: 10px; font-size: 15px; font-weight: 600; text-align: center; transition: background .15s; font-family: 'Manrope', sans-serif; }
        .lp-price-btn:hover { background: #047857; }
        .lp-price-btn.out { background: transparent; border: 1.5px solid #cbd5e1; color: #475569; }
        .lp-price-btn.out:hover { border-color: #059669; color: #059669; }
        .lp-testi-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px,1fr)); gap: 20px; }
        .lp-testi-card { background: #fff; border: 1.5px solid #e2e8f0; border-radius: 16px; padding: 28px; box-shadow: 0 1px 3px rgba(0,0,0,0.04); }
        .lp-testi-stars { color: #d97706; font-size: 15px; margin-bottom: 14px; letter-spacing: 2px; }
        .lp-testi-text { font-size: 14px; color: #475569; line-height: 1.7; margin-bottom: 20px; }
        .lp-testi-author { display: flex; align-items: center; gap: 12px; }
        .lp-testi-avatar { width: 38px; height: 38px; border-radius: 50%; background: #f1f5f9; border: 1.5px solid #e2e8f0; display: flex; align-items: center; justify-content: center; font-size: 17px; }
        .lp-testi-name { font-size: 13px; font-weight: 700; }
        .lp-testi-role { font-size: 12px; color: #94a3b8; }
        .lp-faq-list { max-width: 780px; }
        .lp-faq-item { border-bottom: 1px solid #e2e8f0; }
        .lp-faq-q { width: 100%; background: none; border: none; cursor: pointer; display: flex; justify-content: space-between; align-items: center; padding: 22px 0; text-align: left; font-size: 15px; font-weight: 600; color: #0f172a; font-family: 'Manrope', sans-serif; }
        .lp-faq-icon { width: 24px; height: 24px; border-radius: 50%; background: #f1f5f9; display: flex; align-items: center; justify-content: center; font-size: 16px; color: #94a3b8; flex-shrink: 0; }
        .lp-faq-a { font-size: 14px; color: #475569; line-height: 1.7; max-height: 0; overflow: hidden; transition: max-height .3s ease, padding .3s; }
        .lp-faq-a.open { max-height: 200px; padding-bottom: 20px; }
        .lp-cta { text-align: center; padding: 120px 32px; background: #0f172a; color: #fff; }
        .lp-footer { border-top: 1px solid #e2e8f0; padding: 32px; display: flex; justify-content: space-between; align-items: center; flex-wrap: wrap; gap: 16px; max-width: 1100px; margin: 0 auto; }
        .lp-divider { height: 1px; background: #e2e8f0; }
        .lp-bg-gray { background: #f8fafc; border-top: 1px solid #e2e8f0; border-bottom: 1px solid #e2e8f0; }
        .lp-bg-red { background: #fef2f2; border-top: 1px solid #fecaca; border-bottom: 1px solid #fecaca; }
        @media (max-width: 768px) {
          .lp-nav { padding: 12px 16px; }
          .lp-nav-links .lp-nav-link { display: none; }
          .lp-hero { padding: 100px 20px 60px; }
          .lp-section { padding: 60px 20px; }
          .lp-stats { flex-direction: column; border-radius: 12px; max-width: 100%; }
          .lp-stat { border-right: none; border-bottom: 1px solid #e2e8f0; }
          .lp-stat:last-child { border-bottom: none; }
          .lp-step { grid-template-columns: 56px 1fr; gap: 18px; }
          .lp-step-num { width: 50px; height: 50px; font-size: 18px; }
          .lp-compat-grid { grid-template-columns: 1fr; }
          .lp-actions { flex-direction: column; align-items: stretch; }
          .lp-btn-lg, .lp-btn-outline { text-align: center; }
          .lp-cta { padding: 80px 20px; }
          .lp-footer { flex-direction: column; text-align: center; }
        }
      `}</style>
      <div className="lp-body">
        <nav className="lp-nav">
          <div className="lp-logo">
            <div className="lp-logo-mark">📈</div>
            <div className="lp-logo-name">Trade<span>Leash</span></div>
          </div>
          <div className="lp-nav-links">
            <a href="#features" className="lp-nav-link">Funciones</a>
            <a href="#how" className="lp-nav-link">Cómo funciona</a>
            <a href="#pricing" className="lp-nav-link">Precio</a>
            <button className="lp-btn" onClick={() => setShowAuth(true)}>Empezar gratis</button>
          </div>
        </nav>

        <div className="lp-hero">
          <div className="lp-badge"><div className="lp-badge-dot"></div>Ahora con análisis de IA</div>
          <h1 className="lp-h1">El journal que <span className="blue">bloquea tu cuenta</span><br/>cuando más lo <span className="red">necesitas</span></h1>
          <p className="lp-sub">Registra cada operación, analiza tus patrones con IA y activa un bloqueo irreversible cuando rompes tus reglas. Creado para traders profesionales.</p>
          <div className="lp-actions">
            <button className="lp-btn-lg" onClick={() => setShowAuth(true)}>Empezar gratis — 7 días Pro</button>
            <a href="#how" className="lp-btn-outline">Ver cómo funciona →</a>
          </div>
          <div className="lp-stats">
            <div className="lp-stat"><div className="lp-stat-num">MT5</div><div className="lp-stat-label">EA Nativo</div></div>
            <div className="lp-stat"><div className="lp-stat-num">4+</div><div className="lp-stat-label">Plataformas</div></div>
            <div className="lp-stat"><div className="lp-stat-num">$0</div><div className="lp-stat-label">Para empezar</div></div>
            <div className="lp-stat"><div className="lp-stat-num">IA</div><div className="lp-stat-label">Análisis Claude</div></div>
          </div>
        </div>

        <div className="lp-bg-red">
          <div className="lp-section">
            <div className="lp-tag">El problema</div>
            <h2 className="lp-h2">¿Cuántas cuentas has<br/>reventado este año?</h2>
            <p className="lp-h2-sub">El problema no es tu estrategia. Es lo que haces después de una racha de pérdidas.</p>
            <div className="lp-grid-3">
              <div className="lp-prob-card"><div className="lp-prob-icon">💸</div><div className="lp-prob-title">Revenge trading</div><div className="lp-prob-text">Pierdes un trade, abres otro más grande para recuperar. Y otro. Hasta que la cuenta explota.</div></div>
              <div className="lp-prob-card"><div className="lp-prob-icon">🧠</div><div className="lp-prob-title">Las emociones mandan</div><div className="lp-prob-text">Sabes las reglas. Pero cuando el mercado aprieta, la disciplina desaparece. El cerebro siempre encuentra una excusa.</div></div>
              <div className="lp-prob-card"><div className="lp-prob-icon">📉</div><div className="lp-prob-title">Sin datos, sin patrón</div><div className="lp-prob-text">No sabes a qué hora pierdes más, qué setup falla, en qué sesión eres rentable. Sin datos, repites los mismos errores.</div></div>
            </div>
          </div>
        </div>

        <div id="features">
          <div className="lp-section">
            <div className="lp-tag">Solución</div>
            <h2 className="lp-h2">Todo lo que necesita<br/>un trader</h2>
            <p className="lp-h2-sub">No es solo un journal. Es un sistema que registra, analiza y te bloquea cuando estás a punto de arruinar tu cuenta.</p>
            <div className="lp-feat-grid">
              <div className="lp-feat-card star"><div className="lp-feat-icon" style={{background:'#ecfdf5'}}>🔒</div><div className="lp-feat-title">Bloqueo automático irreversible</div><div className="lp-feat-text">Cuando alcanzas tu límite de drawdown diario o trades máximos, la cuenta se bloquea hasta medianoche. Sin botón para desactivarlo.</div><span className="lp-feat-tag">⚡ Solo en TradeLeash</span></div>
              <div className="lp-feat-card"><div className="lp-feat-icon" style={{background:'#eff6ff'}}>🤖</div><div className="lp-feat-title">Análisis con IA</div><div className="lp-feat-text">Claude analiza tu historial completo y detecta patrones: tu mejor hora para operar, qué setup falla, cómo afectan tus emociones al resultado.</div><span className="lp-feat-tag" style={{background:'#eff6ff',color:'#2563eb',borderColor:'#bfdbfe'}}>Nuevo</span></div>
              <div className="lp-feat-card"><div className="lp-feat-icon" style={{background:'#fffbeb'}}>📊</div><div className="lp-feat-title">Journal completo</div><div className="lp-feat-text">Registra par, tipo, lots, PnL, R:R, setup, sesión, emoción y notas. Añade capturas. Filtra por cualquier variable. Exporta a CSV.</div><img src="/screenshots/screenshot_journal.png" alt="Formulario de registro de trade" style={{width:'100%',borderRadius:10,marginTop:12,border:'1.5px solid #e2e8f0'}}/></div>
              <div className="lp-feat-card"><div className="lp-feat-icon" style={{background:'#f5f3ff'}}>📡</div><div className="lp-feat-title">EA para MetaTrader 5</div><div className="lp-feat-text">El EA registra tus operaciones automáticamente vía webhook en tiempo real. Sin copiar y pegar. Sin olvidarte de anotar un trade.</div></div>
              <div className="lp-feat-card"><div className="lp-feat-icon" style={{background:'#fef2f2'}}>🏆</div><div className="lp-feat-title">Compatible con prop firms</div><div className="lp-feat-text">Servidor en Europa (EU-West, Irlanda). Sin IPs de EEUU. Compatible con cualquier prop firm que tenga restricciones geográficas.</div></div>
              <div className="lp-feat-card"><div className="lp-feat-icon" style={{background:'#ecfdf5'}}>📈</div><div className="lp-feat-title">Seguimiento de ROI</div><div className="lp-feat-text">Registra cada cuenta que compras, cuánto pagaste y cuánto recuperaste. TradeLeash te muestra el ROI real de tu trading.</div></div>
            </div>
          </div>
        </div>

        <div className="lp-bg-gray" id="how">
          <div className="lp-section">
            <div className="lp-tag">Cómo funciona</div>
            <h2 className="lp-h2">En marcha en menos<br/>de 10 minutos</h2>
            <p className="lp-h2-sub">Sin configuraciones complicadas. Sin servidores que gestionar.</p>
            <div className="lp-steps">
              <div className="lp-step"><div className="lp-step-num">01</div><div><div className="lp-step-title">Crea tu cuenta y configura tus reglas</div><div className="lp-step-text">Añade tus cuentas de trading, define tu drawdown máximo diario y número máximo de trades por sesión. TradeLeash hará el resto.</div></div></div>
              <div className="lp-step"><div className="lp-step-num">02</div><div><div className="lp-step-title">Instala el EA en MetaTrader 5</div><div className="lp-step-text">Descarga el EA desde la app, cópialo en tu carpeta de MT5 y pega tu token único. Cada trade se registra automáticamente en tiempo real.</div><div className="lp-step-code">📡 MT5 → Webhook → Supabase EU-West → TradeLeash</div><img src="/screenshots/screenshot_cuenta.png" alt="Cuenta conectada con EA" style={{width:'100%',borderRadius:10,marginTop:16,border:'1.5px solid #e2e8f0'}}/></div></div>
              <div className="lp-step"><div className="lp-step-num">03</div><div><div className="lp-step-title">Opera con disciplina forzada</div><div className="lp-step-text">Si alcanzas tu límite, la cuenta se bloquea automáticamente. Sin excepciones. Así proteges tu cuenta sin depender de tu fuerza de voluntad.</div><img src="/screenshots/screenshot_bloqueo.png" alt="Pantalla de bloqueo" style={{width:'100%',maxWidth:420,borderRadius:10,marginTop:16,border:'1.5px solid #e2e8f0'}}/></div></div>
              <div className="lp-step"><div className="lp-step-num">04</div><div><div className="lp-step-title">Analiza con IA y mejora cada semana</div><div className="lp-step-text">La IA lee tu historial y genera un informe: tus mejores horas, setups ganadores, correlación entre emociones y resultado. Deja de repetir errores.</div></div></div>
            </div>
          </div>
        </div>

        <div className="lp-section">
          <div className="lp-tag">Compatibilidad</div>
          <h2 className="lp-h2">Funciona con tu plataforma</h2>
          <p className="lp-h2-sub">Importación automática para MT5 y Match Trader. Journal manual para cualquier otra plataforma.</p>
          <div className="lp-compat-grid">
            <div className="lp-compat-card">
              <div className="lp-compat-label active">Importación automática</div>
              <div className="lp-compat-item"><span style={{fontSize:20}}>⚙️</span><div><div className="lp-compat-name">MetaTrader 5</div><div className="lp-compat-desc">EA nativo + bloqueo automático</div></div><span className="lp-compat-badge active">Activo</span></div>
              <div className="lp-compat-item"><span style={{fontSize:20}}>🔄</span><div><div className="lp-compat-name">Match Trader</div><div className="lp-compat-desc">Importación directa de historial</div></div><span className="lp-compat-badge active">Activo</span></div>
            </div>
            <div className="lp-compat-card">
              <div className="lp-compat-label soon">Journal manual · en desarrollo</div>
              <div className="lp-compat-item"><span style={{fontSize:20}}>📊</span><div><div className="lp-compat-name">cTrader</div><div className="lp-compat-desc">Journal manual disponible</div></div><span className="lp-compat-badge soon">Pronto</span></div>
              <div className="lp-compat-item"><span style={{fontSize:20}}>📈</span><div><div className="lp-compat-name">DXtrade</div><div className="lp-compat-desc">Journal manual disponible</div></div><span className="lp-compat-badge soon">Pronto</span></div>
            </div>
          </div>
          <p style={{marginTop:20,fontSize:13,color:'#94a3b8',textAlign:'center'}}>🔒 Servidor en EU-West (Irlanda) · Sin IPs de EEUU · Compatible con cualquier prop firm</p>
        </div>

        <div className="lp-bg-gray" id="pricing">
          <div className="lp-section">
            <div className="lp-tag">Precio</div>
            <h2 className="lp-h2">Sin letra pequeña.<br/>Sin permanencia.</h2>
            <p className="lp-h2-sub">Empieza gratis 7 días con todas las funciones. Cancela cuando quieras.</p>
            <div className="lp-price-grid">
              <div className="lp-price-card">
                <div className="lp-price-name">Empieza gratis</div>
                <div className="lp-price-amount">$0</div>
                <div className="lp-price-period">7 días · sin tarjeta</div>
                <div className="lp-price-trial">🎁 Acceso completo durante 7 días — cuentas ilimitadas, IA, EA y bloqueo automático. Sin restricciones.</div>
                <ul className="lp-price-feats">
                  <li><span className="ck">✓</span>Cuentas ilimitadas</li>
                  <li><span className="ck">✓</span>Journal completo + capturas</li>
                  <li><span className="ck">✓</span>Bloqueo automático irreversible</li>
                  <li><span className="ck">✓</span>EA para MT5</li>
                  <li><span className="ck">✓</span>Análisis con IA (Claude)</li>
                  <li><span className="ck">✓</span>Estadísticas emocionales y por setup</li>
                </ul>
                <button className="lp-price-btn out" onClick={() => setShowAuth(true)}>Empezar gratis</button>
              </div>
              <div className="lp-price-card star">
                <div className="lp-price-badge">Más popular</div>
                <div className="lp-price-name">Pro</div>
                <div className="lp-price-amount">$14.99<span>/mes</span></div>
                <div className="lp-price-period">Cancela cuando quieras</div>
                <ul className="lp-price-feats">
                  <li><span className="ck">✓</span>Cuentas ilimitadas</li>
                  <li><span className="ck">✓</span>Journal completo + capturas</li>
                  <li><span className="ck">✓</span>Bloqueo automático irreversible</li>
                  <li><span className="ck">✓</span>EA para MT5</li>
                  <li><span className="ck">✓</span>Análisis con IA (Claude)</li>
                  <li><span className="ck">✓</span>Estadísticas emocionales y por setup</li>
                  <li><span className="ck">✓</span>Exportar a CSV</li>
                  <li><span className="ck">✓</span>Soporte prioritario</li>
                </ul>
                <button className="lp-price-btn" onClick={() => setShowAuth(true)}>Suscribirse a Pro</button>
              </div>
              <div className="lp-price-card">
                <div className="lp-price-name">Anual</div>
                <div className="lp-price-amount">$99<span>/año</span></div>
                <div className="lp-price-period">Ahorras $80</div>
                <ul className="lp-price-feats">
                  <li><span className="ck">✓</span>Todo lo de Pro</li>
                  <li><span className="ck">✓</span>Acceso beta a nuevas funciones</li>
                </ul>
                <button className="lp-price-btn out" onClick={() => setShowAuth(true)}>Seleccionar anual</button>
              </div>
            </div>
            <p style={{textAlign:'center',marginTop:24,fontSize:13,color:'#94a3b8'}}>💡 Reventar una cuenta cuesta mucho más que $14.99/mes.</p>
          </div>
        </div>

        <div style={{background:'#f8fafc',borderTop:'1px solid #e2e8f0',borderBottom:'1px solid #e2e8f0'}}>
        <div className="lp-section">
          <div className="lp-tag">Inteligencia artificial</div>
          <h2 className="lp-h2">Tu journal analizado<br/>por Claude IA</h2>
          <p className="lp-h2-sub">No son estadísticas genéricas. Es un análisis real de tu operativa, con recomendaciones específicas para ti.</p>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:40,alignItems:'center'}}>
            <div>
              {[{l:'Mejor sesión de trading',v:'Londres AM',ok:true},{l:'Setup con mayor win rate',v:'LQ Sweep 73%',ok:true},{l:'Emoción → resultado',v:'Calma → +2.1R',ok:true},{l:'Hora de pérdida máxima',v:'14:00–16:00',ok:false},{l:'Patrón revenge trading',v:'Detectado ⚠️',ok:false}].map(s=>(
                <div key={s.l} style={{display:'flex',justifyContent:'space-between',alignItems:'center',padding:'12px 0',borderBottom:'1px solid #e2e8f0',fontSize:14}}>
                  <span style={{color:'#475569'}}>{s.l}</span>
                  <span style={{fontFamily:'monospace',fontSize:13,fontWeight:600,background:s.ok?'#ecfdf5':'#fef2f2',color:s.ok?'#059669':'#dc2626',padding:'3px 10px',borderRadius:6}}>{s.v}</span>
                </div>
              ))}
              <p style={{marginTop:20,fontSize:14,color:'#475569'}}>Informe automático basado en tu historial real de trades.</p>
            </div>
            <div style={{background:'#fff',border:'1.5px solid #e2e8f0',borderRadius:16,padding:24,boxShadow:'0 4px 6px rgba(0,0,0,0.05)'}}>
              <div style={{display:'flex',alignItems:'center',gap:8,marginBottom:16,paddingBottom:14,borderBottom:'1px solid #e2e8f0',fontSize:14,fontWeight:700}}>
                <div style={{width:8,height:8,borderRadius:'50%',background:'#059669'}}></div>
                Informe semanal · generado por Claude
              </div>
              <div style={{padding:'12px 0',borderBottom:'1px solid #e2e8f0',fontSize:13,color:'#475569',lineHeight:1.6}}>
                <strong style={{color:'#0f172a',display:'block',marginBottom:6}}>📊 Análisis de tus 12 trades esta semana</strong>
                Pierdes el 80% de los trades que abres después de las 15h. Cuando registras "frustrado", el trade siguiente pierde en el 90% de los casos.
              </div>
              <div style={{padding:'12px 0',borderBottom:'1px solid #e2e8f0',fontSize:13,color:'#475569',lineHeight:1.6}}>
                <strong style={{color:'#0f172a',display:'block',marginBottom:6}}>⚠️ Patrón detectado: revenge trading</strong>
                3 losses se abrieron en menos de 5 minutos tras un trade perdedor. Añade cooldown de 30 min.
              </div>
              <div style={{padding:'12px 0',fontSize:13,color:'#475569',lineHeight:1.6}}>
                <strong style={{color:'#059669',display:'block',marginBottom:6}}>✓ Recomendación</strong>
                Corta la sesión a las 14:30. Tu win rate en Londres AM es del 73% — ahí está tu ventaja real.
              </div>
            </div>
          </div>
        </div>
        </div>

        <div className="lp-section">
          <div className="lp-tag">Traders reales</div>
          <h2 className="lp-h2">Lo que dicen<br/>los que lo usan</h2>
          <div className="lp-testi-grid">
            <div className="lp-testi-card"><div className="lp-testi-stars">★★★★★</div><div className="lp-testi-text">"Llevaba 3 cuentas reventadas por revenge trading. Desde que el bloqueo se activa automáticamente, no he vuelto a perder una cuenta por sobreoperar."</div><div className="lp-testi-author"><div className="lp-testi-avatar">👤</div><div><div className="lp-testi-name">Carlos M.</div><div className="lp-testi-role">Trader FTMO · Fase 2</div></div></div></div>
            <div className="lp-testi-card"><div className="lp-testi-stars">★★★★★</div><div className="lp-testi-text">"La IA me dijo que pierdo el 80% de mis trades después de las 17h. Nunca lo había visto. Ahora cierro la sesión a las 16:30 y el resultado cambió completamente."</div><div className="lp-testi-author"><div className="lp-testi-avatar">👤</div><div><div className="lp-testi-name">Ana R.</div><div className="lp-testi-role">Trader The5ers · Funded</div></div></div></div>
            <div className="lp-testi-card"><div className="lp-testi-stars">★★★★★</div><div className="lp-testi-text">"El EA de MT5 lo configuro en 5 minutos. Cada trade aparece automáticamente en el journal. No tengo que hacer nada y tengo un registro perfecto de todo."</div><div className="lp-testi-author"><div className="lp-testi-avatar">👤</div><div><div className="lp-testi-name">David L.</div><div className="lp-testi-role">Trader FundingPips · MT5</div></div></div></div>
          </div>
        </div>

        <div className="lp-cta">
          <div className="lp-tag" style={{color:'#6ee7b7'}}>Empieza hoy</div>
          <h2 className="lp-h2" style={{color:'#fff',marginBottom:20}}>¿Cuánto te cuesta<br/>la siguiente cuenta?</h2>
          <p style={{color:'#94a3b8',fontSize:17,maxWidth:500,margin:'0 auto 40px',lineHeight:1.7}}>TradeLeash es lo que pones entre tus emociones y tu cuenta. 7 días gratis. Sin tarjeta.</p>
          <div style={{display:'flex',justifyContent:'center',gap:16,flexWrap:'wrap'}}>
            <button className="lp-btn-lg" style={{background:'#fff',color:'#059669'}} onClick={() => setShowAuth(true)}>Empezar gratis ahora</button>
            <a href="#features" className="lp-btn-outline" style={{color:'#fff',borderColor:'rgba(255,255,255,0.2)',background:'rgba(255,255,255,0.05)'}}>Ver todas las funciones</a>
          </div>
        </div>

        <footer className="lp-footer">
          <div style={{fontWeight:800,fontSize:18}}>TradeLeash</div>
          <div style={{fontSize:13,color:'#94a3b8'}}>Servidor en EU-West (Irlanda) · Pagos seguros con Stripe · © 2026 TradeLeash</div>
        </footer>
      </div>
    </>
  );
}

export default function App() {
  const [session, setSession] = useState(null);
  const [loadingAuth, setLoadingAuth] = useState(true);
  const [isPasswordRecovery, setIsPasswordRecovery] = useState(false);
  const [navOpen, setNavOpen] = useState(true);
  const [view, setView] = useState('dashboard');
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('tg_theme') || 'light';
    // Apply immediately before first render
    if (saved === 'dark') {
      document.documentElement.setAttribute('data-theme', 'dark');
    } else {
      document.documentElement.removeAttribute('data-theme');
    }
    return saved;
  });

  const toggleTheme = () => {
    setTheme(t => {
      const next = t === 'dark' ? 'light' : 'dark';
      if (next === 'dark') {
        document.documentElement.setAttribute('data-theme', 'dark');
      } else {
        document.documentElement.removeAttribute('data-theme');
      }
      localStorage.setItem('tg_theme', next);
      return next;
    });
  };
  const [accounts, setAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [trades, setTrades] = useState([]);
  const [onbDismissed, setOnbDismissed] = useState(() => {
    try { return localStorage.getItem('tg_onb_done') === '1'; } catch { return false; }
  });

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('payment') === 'success') {
      window.history.replaceState({}, '', window.location.pathname);
      supabase.auth.refreshSession().then(({ data }) => {
        if (data?.session) setSession(data.session);
      });
    }
  }, []);

  useEffect(() => {
    // Crisp chat widget
    window.$crisp = [];
    window.CRISP_WEBSITE_ID = "e438c670-9f27-47de-9352-9456113dcf64";
    const d = document, s = d.createElement("script");
    s.src = "https://client.crisp.chat/l.js";
    s.async = 1;
    d.getElementsByTagName("head")[0].appendChild(s);
  }, []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const hash = window.location.hash;
    let recoveryMode = params.get('type') === 'recovery' || 
                       hash.includes('type=recovery') ||
                       localStorage.getItem('recovery_pending') === '1';
    if (recoveryMode) {
      setIsPasswordRecovery(true);
      localStorage.setItem('recovery_pending', '1');
    }

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'PASSWORD_RECOVERY') {
        recoveryMode = true;
        localStorage.setItem('recovery_pending', '1');
        setIsPasswordRecovery(true);
        setSession(session);
        setLoadingAuth(false);
        if(event==='SIGNED_IN'&&session&&session.user){const c=new Date(session.user.created_at).getTime();if((Date.now()-c)<300000){fetch('/api/welcome',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({email:session.user.email})}).catch(()=>{});}}
      } else if (event === 'SIGNED_IN' && (recoveryMode || localStorage.getItem('recovery_pending') === '1')) {
        localStorage.removeItem('recovery_pending');
        setIsPasswordRecovery(true);
        setLoadingAuth(false);
        setSession(session);
      } else {
        recoveryMode = false;
        setIsPasswordRecovery(false);
        setSession(session);
        setLoadingAuth(false);
        if(event==='SIGNED_IN'&&session&&session.user){const c=new Date(session.user.created_at).getTime();if((Date.now()-c)<60000){fetch('/api/welcome',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({email:session.user.email})}).catch(()=>{});}}
      }
    });

    supabase.auth.getSession().then(({ data: { session } }) => {
      if (!recoveryMode) {
        setSession(session);
        setLoadingAuth(false);
      }
    });

    return () => subscription.unsubscribe();
  }, []);

  useEffect(() => {
    if (!session) return;
    supabase.auth.getUser().then(({ data }) => {
      if (data?.user) setSession(s => ({ ...s, user: data.user }));
    });
  }, []);

  useEffect(() => {
    if (!session) return;
    supabase.from('trades').select('*').eq('user_id', session.user.id).order('created_at', { ascending: false })
      .then(({ data }) => { if (data && data.length > 0) setTrades(data); else setTrades([]); });
  }, [session]);

  const loadAccounts = (sess) => {
    if (!sess) return;
    supabase.from('accounts').select('*').eq('user_id', sess.user.id).order('created_at', { ascending: true })
      .then(({ data }) => {
        if (data && data.length > 0) {
          setAccounts(data.map(a => ({
            ...a,
            balance: a.balance || 0,
            equity: a.equity || 0,
            dd: a.dd || 0,
            rules: a.rules || { maxTrades: a.max_trades || 5, maxLoss: 2, maxConsec: 3, cooldown: 15 },
            todayTrades: a.today_trades || 0,
            todayLoss: a.today_loss || 0,
            dailyDD: a.daily_dd || 0,
            maxDD: a.max_dd || 10,
            maxDailyDD: a.max_daily_dd || 5,
            lockUntil: a.lock_until || null,
            lockReason: a.lock_reason || '',
            connected: a.connected || false,
            locked: a.locked && a.lock_until && Date.now() < a.lock_until ? true : false,
            ea_token: a.ea_token || '',
            custom_setups: a.custom_setups || null,
          })));
        }
        setLoadingAccounts(false);
      });
  };

  useEffect(() => {
    if (!session) return;
    loadAccounts(session);
    const interval = setInterval(() => loadAccounts(session), 30000);
    return () => clearInterval(interval);
  }, [session]);

  if (loadingAuth) return <div style={{background:'#07090f',height:'100vh',display:'flex',alignItems:'center',justifyContent:'center',color:'#4f8eff',fontFamily:'monospace'}}>Cargando...</div>;
  if (isPasswordRecovery) return <Auth />;
  if (!session) return <LandingPage onEnter={() => {}} />;


  const _isPro = session?.user?.user_metadata?.is_pro === true;
  const _createdAt = session?.user?.created_at ? new Date(session.user.created_at) : null;
  const _trialOk = _createdAt ? (Date.now() - _createdAt.getTime()) < 7 * 24 * 60 * 60 * 1000 : false;
  if (!_isPro && !_trialOk) return (
    <>
      <style>{STYLES}</style>
      <div style={{minHeight:'100vh',background:'var(--bg)',display:'flex',alignItems:'center',justifyContent:'center',padding:24}}>
        <div style={{maxWidth:400,width:'100%',background:'var(--s1)',border:'1px solid var(--b1)',borderRadius:16,padding:'40px 32px',textAlign:'center',boxShadow:'0 8px 32px rgba(0,0,0,0.08)'}}>
          <div style={{fontSize:48,marginBottom:16}}>🔒</div>
          <div style={{fontSize:22,fontWeight:700,marginBottom:8}}>Tu prueba gratuita ha terminado</div>
          <div style={{fontSize:14,color:'var(--t3)',marginBottom:28,lineHeight:1.6}}>Activa tu plan Pro para seguir accediendo a TradeLeash.</div>
          <div style={{background:'var(--s2)',borderRadius:12,padding:20,marginBottom:24,border:'1px solid var(--b1)'}}>
            <div style={{fontSize:32,fontWeight:800,color:'var(--ac)',marginBottom:4}}>$14.99<span style={{fontSize:14,fontWeight:500,color:'var(--t3)'}}>/mes</span></div>
            <div style={{fontSize:12,color:'var(--t3)'}}>Todas las funciones · Sin límites · Cancela cuando quieras</div>
          </div>
          <button className="btn bp w100" style={{fontSize:15,padding:'14px 0',marginBottom:12}} onClick={() => fetch('/api/stripe',{method:'POST',headers:{'Content-Type':'application/json'},body:JSON.stringify({action:'create_checkout',email:session?.user?.email})}).then(r=>r.json()).then(d=>{if(d.url)window.location.href=d.url})}>
            🚀 Activar Plan Pro
          </button>
          <button onClick={() => supabase.auth.signOut()} style={{background:'none',border:'none',color:'var(--t4)',fontSize:12,cursor:'pointer',textDecoration:'underline'}}>Cerrar sesión</button>
        </div>
      </div>
    </>
  );

  const NAVS = [
    { id: 'dashboard', l: 'Dashboard', ico: '⬛' },
    { id: 'journal', l: 'Journal', ico: '📝' },
    { id: 'emotions', l: 'Emociones', ico: '🧠' },
    { id: 'accounts', l: 'Cuentas', ico: '🏦', pip: accounts.filter(a => a.locked).length || null },
    { id: 'ea', l: 'EA & Bloqueo', ico: '🤖' },
    { id: 'propfirms', l: 'Prop Firms', ico: '🏆',  },
    { id: 'ai', l: 'Análisis IA', ico: '🧠' },
    { id: 'import', l: 'Importar', ico: '📥' },
    { id: 'profile', l: 'Mi Cuenta', ico: '👤' },
  ];

  const TITLES = { dashboard: 'Dashboard', journal: 'Journal de Trading', emotions: 'Estadísticas Emocionales', accounts: 'Cuentas & Bloqueo', ea: 'EA & Integraciones', propfirms: 'Prop Firms & ROI', profile: 'Mi Cuenta' , ai: 'Análisis IA', import: 'Importar Historial' };

  return (
    <>
      <style>{STYLES}</style>
      <div className="shell">
        <nav className={`nav ${navOpen ? 'wide' : ''}`}>
          <div className="nav-logo">
            <div className="nav-logo-mark">📈</div>
            <div className="nav-logo-text">
              <div className="nav-logo-name">Trade<span>Leash</span></div>
              <div className="nav-logo-tag">TRADING JOURNAL</div>
            </div>
          </div>
          <div className="nav-links">
            <div className="nav-group">NAVEGACIÓN</div>
            {NAVS.map(n => (
              <div key={n.id} className={`ni ${view === n.id ? 'on' : ''}`} onClick={() => setView(n.id)}>
                <span className="ni-ico">{n.ico}</span>
                <span className="ni-lbl">{n.l}</span>
                {n.pip ? <span className="pip">{n.pip}</span> : null}
                {n.beta ? <span style={{fontSize:9,fontWeight:700,background:'var(--purple-bg)',color:'var(--purple)',border:'1px solid var(--purple)',borderRadius:4,padding:'1px 5px',marginLeft:4}}>BETA</span> : null}
              </div>
            ))}
          </div>
          <div className="nav-bot">
            <button className="nav-toggle" onClick={() => setNavOpen(p => !p)}>{navOpen ? '◀' : '▶'}</button>
          </div>
        </nav>

        <div className="main">
          <div className="topbar">
            <div className="topbar-title">{TITLES[view]}</div>
            <div className="topbar-right">
              <button onClick={toggleTheme} style={{background:'var(--s3)',border:'1px solid var(--b1)',color:'var(--t2)',padding:'5px 11px',borderRadius:8,cursor:'pointer',fontWeight:700,fontSize:13,fontFamily:'var(--sans)',transition:'all .15s'}} title="Cambiar tema">{theme === 'dark' ? '☀️' : '🌙'}</button>
              <button onClick={() => supabase.auth.signOut()} style={{background:'var(--red-bg)',border:'1px solid rgba(220,38,38,0.2)',color:'var(--red)',padding:'5px 11px',borderRadius:7,cursor:'pointer',fontWeight:600,fontSize:11,fontFamily:'var(--sans)'}}>Cerrar sesión</button>
              <div className="fl aic g2 fm" style={{ fontSize: 10, color: 'var(--t3)' }}>
                <span style={{ width: 6, height: 6, borderRadius: '50%', background: 'var(--green)', display: 'inline-block', boxShadow: '0 0 6px var(--green)' }} />
                {accounts.filter(a => a.connected).length} live · {accounts.filter(a => a.locked).length} bloqueadas
              </div>
            </div>
          </div>
          <div style={{ display: 'flex', flex: 1, overflow: 'hidden' }}>
            <div className="content" style={{ flex: 1 }}>
              {view === 'dashboard' && <Dashboard accounts={accounts} trades={trades} />}
              {view === 'journal' && <JournalView trades={trades} setTrades={setTrades} accounts={accounts} setAccounts={setAccounts} session={session} />}
              {view === 'emotions' && <StatsView trades={trades} />}
              {view === 'accounts' && <AccountsView accounts={accounts} setAccounts={setAccounts} session={session} />}
              {view === 'ea' && <EAView />}
              {view === 'propfirms' && <PropFirmsView session={session} />}
              {view === 'profile' && <ProfileView session={session} trades={trades} accounts={accounts} />}
              {view === 'launch' && <LaunchView />}
              {view === 'ai' && <AIAnalysis trades={trades} session={session} accounts={accounts} />}
              {view === 'import' && <ImportHistory session={session} accounts={accounts} setTrades={setTrades} />}
            </div>
            {!onbDismissed && <OnboardingPanel trades={trades} accounts={accounts} onDismiss={() => { setOnbDismissed(true); try { localStorage.setItem('tg_onb_done','1'); } catch {} }} onNavigate={setView} />}
          </div>
        </div>
      </div>
    </>
  );
}







